// client/src/coms/views/canvas_parts/CanvasRoomView.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import CanvasDesktopLayout from './CanvasDesktopLayout';
import CanvasStageSurface from './CanvasStageSurface';
import CanvasModelViewport from './CanvasModelViewport';
import CanvasRightSidebarExtensions from './CanvasRightSidebarExtensions';
import CanvasGamePanel from './CanvasGamePanel';
import useSketchRelayGame from './useSketchRelayGame';
import PartyArcadeView from './party/PartyArcadeView';
import CanvasAnimationPanel from './CanvasAnimationPanel';
import CanvasReplayModal from './CanvasReplayModal';
import CanvasAreaAssignmentModal from './CanvasAreaAssignmentModal';
import CanvasLayersPanel from './canvas_room_view_parts/CanvasLayersPanel';
import { createLiveClientId, postCanvasLiveStrokeEvent } from './canvas_room_view_parts/canvasRoomLiveUtils';
import { DEFAULT_CALLIGRAPHY_SETTINGS } from './canvas_room_view_parts/calligraphyBrushUtils';
import {
  DEFAULT_LIQUIFY_MODE,
  DEFAULT_LIQUIFY_SETTINGS,
  normaliseLiquifyMode,
} from './canvas_room_view_parts/liquifyUtils';
import { getPaintSettings, normalisePaintSettings } from './canvas_room_view_parts/paintBrushUtils';
import ImportedBrushImportModal from './ImportedBrushImportModal';
import {
  getMeta as getBrushLibraryMeta,
  listRecords as listBrushRecords,
  loadRuntimePreset,
  loadRuntimeShell,
  migrateLegacyLibrary,
  setMeta as setBrushLibraryMeta,
  subscribeLibrary,
  updateRecord as updateBrushRecord,
} from './canvas_room_view_parts/brushImport/brushLibrary.js';

import { clampDocumentDimension } from './canvasRoomUtils';
import {
  fallbackTool,
  isToolAllowed,
  resolveGameRestrictions,
  snapColour,
  snapSize,
} from './canvasGameMode';
import {
  getCanvasAreaUsersFromLayers,
  getCurrentUserAreaOwnerKey,
  getCanvasAreaSelectionForOwner,
  canAreaOwnerDrawAtPoint,
  sanitizeCanvasAreaAssignments,
} from './canvas_room_view_parts/canvasAreaAssignments';

// Brush packs dropped on the canvas open the importer; plain images still
// become layers.
const BRUSH_PACK_FILE_PATTERN = /\.(brushset|brush|brushlibrary|bundle|kpp|gbr|gih|myb|abr|voxbrush)$/i;

const DEFAULT_DOCUMENT_SIZE = { width: 1600, height: 1000 };
const DEFAULT_ZOOM = 0.75;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 64;
const ZOOM_BUTTON_FACTOR = 1.25;
const MAX_HISTORY = 30;

const TOOL_SETTINGS_STORAGE_KEY = 'voxemera.canvas.gpuToolSettings.v1';
const LAST_TOOL_STORAGE_KEY = 'voxemera.canvas.gpuLastTool.v1';
const LAST_COLOUR_STORAGE_KEY = 'voxemera.canvas.gpuLastColour.v1';
const PAINT_SETTINGS_STORAGE_KEY = 'voxemera.canvas.gpuPaintSettings.v1';
const PAINT_MATERIAL_3D_STORAGE_KEY = 'voxemera.canvas.paintMaterial3d.v1';
const WORKSPACE_MODE_STORAGE_KEY_PREFIX = 'voxemera.canvas.workspaceMode.v1';

const DEFAULT_3D_PAINT_MATERIAL_SETTINGS = Object.freeze({
  gloss: 35,
  height: 12,
  metalness: 0,
});

const CANVAS_STATE_DB_NAME = 'voxemera-canvas-room-local-state';
const CANVAS_STATE_STORE_NAME = 'canvasStates';
const CANVAS_STATE_KEY_PREFIX = 'voxemera-canvas-room-state-v2';
const CANVAS_STATE_SAVE_DELAY_MS = 150;
const IMPORTED_BRUSH_LIBRARY_STATE_KEY = 'voxemera-imported-brush-library-v1';

const GAME_DRAWING_STATE_KEY_PREFIX = 'voxemera-game-drawing-v1';
const GAME_DRAWING_STATE_MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000;

function getCanvasStateKey(canvasId) {
  // Game drawings (see canvasGameMode.js) keep their own local copy so a reload mid-game restores them.
  if (typeof canvasId === 'string' && canvasId.startsWith('game:')) return `${GAME_DRAWING_STATE_KEY_PREFIX}:${canvasId.slice(5)}`;
  const numericId = Number(canvasId);
  return Number.isInteger(numericId) && numericId > 0
    ? `${CANVAS_STATE_KEY_PREFIX}:${numericId}`
    : '';
}

function getWorkspaceModeStorageKey(canvasId) {
  const numericId = Number(canvasId);
  return Number.isInteger(numericId) && numericId > 0
    ? `${WORKSPACE_MODE_STORAGE_KEY_PREFIX}:${numericId}`
    : WORKSPACE_MODE_STORAGE_KEY_PREFIX;
}

function readStoredWorkspaceMode(canvasId) {
  if (typeof window === 'undefined') return '2d';
  try {
    return window.localStorage.getItem(getWorkspaceModeStorageKey(canvasId)) === '3d' ? '3d' : '2d';
  } catch {
    return '2d';
  }
}

function writeStoredWorkspaceMode(canvasId, mode) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(getWorkspaceModeStorageKey(canvasId), mode === '3d' ? '3d' : '2d');
  } catch {}
}


function sanitize3dPaintMaterialSettings(settings) {
  const source = settings && typeof settings === 'object' ? settings : {};
  const clampPercent = (value, fallback) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? Math.max(0, Math.min(100, numericValue))
      : fallback;
  };

  return {
    gloss: clampPercent(source.gloss, DEFAULT_3D_PAINT_MATERIAL_SETTINGS.gloss),
    height: clampPercent(source.height, DEFAULT_3D_PAINT_MATERIAL_SETTINGS.height),
    metalness: clampPercent(source.metalness, DEFAULT_3D_PAINT_MATERIAL_SETTINGS.metalness),
  };
}

function readStored3dPaintMaterialSettings() {
  if (typeof window === 'undefined') return { ...DEFAULT_3D_PAINT_MATERIAL_SETTINGS };
  try {
    const stored = JSON.parse(window.localStorage.getItem(PAINT_MATERIAL_3D_STORAGE_KEY) || 'null');
    return sanitize3dPaintMaterialSettings(stored);
  } catch {
    return { ...DEFAULT_3D_PAINT_MATERIAL_SETTINGS };
  }
}

function writeStored3dPaintMaterialSettings(settings) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      PAINT_MATERIAL_3D_STORAGE_KEY,
      JSON.stringify(sanitize3dPaintMaterialSettings(settings))
    );
  } catch {}
}

function getCanvasAnimationStateKey(canvasId) {
  const stateKey = getCanvasStateKey(canvasId);
  return stateKey ? `${stateKey}:animation` : '';
}

function getCanvasAnimationFrameStateKey(canvasId, frameId) {
  const animationKey = getCanvasAnimationStateKey(canvasId);
  const safeFrameId = String(frameId || '').trim();
  return animationKey && safeFrameId ? `${animationKey}:frame:${safeFrameId}` : '';
}

function createAnimationFrameId() {
  return `frame-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function sanitizeAnimationFrame(frame, index = 0) {
  const source = frame && typeof frame === 'object' ? frame : {};
  const layers = Array.isArray(source.layers) ? source.layers.filter(Boolean).map((layer) => ({ ...layer })) : [];
  return {
    id: String(source.id || createAnimationFrameId()),
    name: String(source.name || `Frame ${index + 1}`),
    index,
    activeLayerId: String(source.activeLayerId || ''),
    layers,
    layerBitmaps: source.layerBitmaps && typeof source.layerBitmaps === 'object' ? { ...source.layerBitmaps } : {},
    paintLayerStates: source.paintLayerStates && typeof source.paintLayerStates === 'object' ? { ...source.paintLayerStates } : {},
    savedAt: Number(source.savedAt) || Date.now(),
  };
}

function sanitizeAnimationFrames(frames) {
  return (Array.isArray(frames) ? frames : []).filter(Boolean).map((frame, index) => sanitizeAnimationFrame(frame, index));
}

function loadAnimationImage(dataUrl) {
  return new Promise((resolve, reject) => {
    if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
      reject(new Error('Invalid animation image.'));
      return;
    }
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to load animation image.'));
    image.src = dataUrl;
  });
}

async function mergeAnimationRasterPatch(previousDataUrl, patch, fallbackWidth, fallbackHeight) {
  const source = patch && typeof patch === 'object' ? patch : null;
  const patchDataUrl = String(source && source.dataUrl ? source.dataUrl : '');
  if (!source || !patchDataUrl.startsWith('data:image/')) return previousDataUrl || '';
  const replaceLayer = Boolean(source.replaceLayer || source.fullLayer || source.mode === 'replace_layer');
  if (replaceLayer) return patchDataUrl;

  const patchImage = await loadAnimationImage(patchDataUrl);
  let previousImage = null;
  if (typeof previousDataUrl === 'string' && previousDataUrl.startsWith('data:image/')) {
    try { previousImage = await loadAnimationImage(previousDataUrl); } catch {}
  }
  const width = Math.max(1, Number(previousImage && previousImage.width) || Number(fallbackWidth) || (Number(source.x) || 0) + (Number(source.width) || patchImage.width));
  const height = Math.max(1, Number(previousImage && previousImage.height) || Number(fallbackHeight) || (Number(source.y) || 0) + (Number(source.height) || patchImage.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width);
  canvas.height = Math.round(height);
  const context = canvas.getContext('2d');
  if (!context) return previousDataUrl || patchDataUrl;
  if (previousImage) context.drawImage(previousImage, 0, 0);
  context.drawImage(
    patchImage,
    Number(source.x) || 0,
    Number(source.y) || 0,
    Number(source.width) || patchImage.width,
    Number(source.height) || patchImage.height,
  );
  return canvas.toDataURL('image/png');
}


function openCanvasStateDb() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB is unavailable.'));
      return;
    }

    const request = window.indexedDB.open(CANVAS_STATE_DB_NAME, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CANVAS_STATE_STORE_NAME)) {
        db.createObjectStore(CANVAS_STATE_STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Unable to open canvas storage.'));
  });
}

function readCanvasState(key) {
  if (!key) return Promise.resolve(null);
  return openCanvasStateDb().then((db) => new Promise((resolve, reject) => {
    const transaction = db.transaction(CANVAS_STATE_STORE_NAME, 'readonly');
    const request = transaction.objectStore(CANVAS_STATE_STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result ? request.result.payload : null);
    request.onerror = () => reject(request.error || new Error('Unable to read canvas storage.'));
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      try { db.close(); } catch {}
    };
  }));
}

function writeCanvasState(key, payload) {
  if (!key || !payload) return Promise.resolve(false);
  return openCanvasStateDb().then((db) => new Promise((resolve, reject) => {
    const transaction = db.transaction(CANVAS_STATE_STORE_NAME, 'readwrite');
    transaction.objectStore(CANVAS_STATE_STORE_NAME).put({
      key,
      payload,
      savedAt: Date.now(),
    });
    transaction.oncomplete = () => {
      db.close();
      resolve(true);
    };
    transaction.onerror = () => {
      try { db.close(); } catch {}
      reject(transaction.error || new Error('Unable to save canvas storage.'));
    };
  }));
}

// Game drawings are only needed while their game runs; old ones are removed when a new one opens.
function purgeStaleGameDrawingStates(now = Date.now()) {
  return openCanvasStateDb().then((db) => new Promise((resolve) => {
    const transaction = db.transaction(CANVAS_STATE_STORE_NAME, 'readwrite');
    const request = transaction.objectStore(CANVAS_STATE_STORE_NAME).openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      const record = cursor.value || {};
      const savedAt = Number(record.payload && record.payload.savedAt) || 0;
      if (String(record.key || '').startsWith(`${GAME_DRAWING_STATE_KEY_PREFIX}:`) && now - savedAt > GAME_DRAWING_STATE_MAX_AGE_MS) cursor.delete();
      cursor.continue();
    };
    transaction.oncomplete = () => {
      try { db.close(); } catch {}
      resolve(true);
    };
    transaction.onerror = () => {
      try { db.close(); } catch {}
      resolve(false);
    };
  })).catch(() => false);
}

function deleteCanvasState(key) {
  if (!key) return Promise.resolve(false);
  return openCanvasStateDb().then((db) => new Promise((resolve, reject) => {
    const transaction = db.transaction(CANVAS_STATE_STORE_NAME, 'readwrite');
    transaction.objectStore(CANVAS_STATE_STORE_NAME).delete(key);
    transaction.oncomplete = () => {
      db.close();
      resolve(true);
    };
    transaction.onerror = () => {
      try { db.close(); } catch {}
      reject(transaction.error || new Error('Unable to delete canvas storage.'));
    };
  }));
}

function persistAnimationFramesIndividually(canvasId, frames, activeFrameId, enabled) {
  const safeFrames = sanitizeAnimationFrames(frames);
  const safeActiveFrameId = String(activeFrameId || safeFrames[0]?.id || '');
  return writeCanvasState(getCanvasAnimationStateKey(canvasId), {
    version: 1,
    type: 'voxemera-canvas-animation-local-state',
    enabled: Boolean(enabled),
    activeFrameId: safeActiveFrameId,
    frames: safeFrames,
    savedAt: Date.now(),
  });
}

function readAnimationFramesIndividually(canvasId, manifest) {
  const source = manifest && typeof manifest === 'object' ? manifest : {};
  const completeFrames = sanitizeAnimationFrames(source.frames);
  const frameIds = Array.isArray(source.frameIds)
    ? source.frameIds.map((frameId) => String(frameId || '').trim()).filter(Boolean)
    : [];

  if (completeFrames.some((frame) => Object.keys(frame.layerBitmaps || {}).length > 0) || !frameIds.length) {
    return Promise.resolve({
      enabled: Boolean(source.enabled),
      activeFrameId: String(source.activeFrameId || completeFrames[0]?.id || ''),
      frames: completeFrames,
    });
  }

  return Promise.all(frameIds.map((frameId) => readCanvasState(getCanvasAnimationFrameStateKey(canvasId, frameId))))
    .then((records) => {
      const frames = records.map((record, index) => {
        const storedFrame = record && record.frame && typeof record.frame === 'object' ? record.frame : record;
        return storedFrame && typeof storedFrame === 'object' ? sanitizeAnimationFrame(storedFrame, index) : null;
      }).filter(Boolean);
      return {
        enabled: Boolean(source.enabled),
        activeFrameId: String(source.activeFrameId || frames[0]?.id || ''),
        frames,
      };
    });
}

const PEN_TOOL_DEFAULTS = {
  strokeColor: '#111111',
  brushSize: 24,
  brushSoftness: 0,
  jitterAmount: 0,
  toolOpacity: 1,
};

const CALLIGRAPHY_TOOL_DEFAULTS = {
  ...PEN_TOOL_DEFAULTS,
  brushSize: DEFAULT_CALLIGRAPHY_SETTINGS.width,
  calligraphySettings: { ...DEFAULT_CALLIGRAPHY_SETTINGS },
};

const PAINT_TOOL_DEFAULTS = {
  ...PEN_TOOL_DEFAULTS,
  strokeColor: '#d52b24',
  brushSize: 70,
};

const AIRBRUSH_TOOL_DEFAULTS = {
  ...PEN_TOOL_DEFAULTS,
  brushSize: 100,
  airbrushDistance: 100,
  airbrushPaintFlow: 20,
  airbrushPressurePaintFlowEnabled: false,
  airbrushAirPressure: 5,
  airbrushNeedleLimit: 100,
};


function readStoredLastColour() {
  if (typeof window === 'undefined') return PEN_TOOL_DEFAULTS.strokeColor;
  try {
    const stored = window.localStorage.getItem(LAST_COLOUR_STORAGE_KEY);
    return typeof stored === 'string' && stored.trim() ? stored : PEN_TOOL_DEFAULTS.strokeColor;
  } catch {
    return PEN_TOOL_DEFAULTS.strokeColor;
  }
}

function writeStoredLastColour(colour) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAST_COLOUR_STORAGE_KEY, String(colour || PEN_TOOL_DEFAULTS.strokeColor));
  } catch {
  }
}

function readStoredPaintSettings() {
  if (typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PAINT_SETTINGS_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredPaintSettings(settings) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PAINT_SETTINGS_STORAGE_KEY, JSON.stringify(settings || {}));
  } catch {
  }
}

function readStoredToolSettings() {
  if (typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(TOOL_SETTINGS_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredToolSettings(settings) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TOOL_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
  }
}

function readStoredLastTool() {
  if (typeof window === 'undefined') {
    return {
      activeTool: 'pen',
      activeBrushPreset: 'pen',
      activeImportedBrushId: '',
    };
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(LAST_TOOL_STORAGE_KEY) || '{}');
    return {
      activeTool: typeof parsed.activeTool === 'string' && parsed.activeTool ? parsed.activeTool : 'pen',
      activeBrushPreset: typeof parsed.activeBrushPreset === 'string' && parsed.activeBrushPreset ? parsed.activeBrushPreset : 'pen',
      activeImportedBrushId: typeof parsed.activeImportedBrushId === 'string' ? parsed.activeImportedBrushId : '',
    };
  } catch {
    return {
      activeTool: 'pen',
      activeBrushPreset: 'pen',
      activeImportedBrushId: '',
    };
  }
}

function writeStoredLastTool(lastTool) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAST_TOOL_STORAGE_KEY, JSON.stringify(lastTool));
  } catch {
  }
}

function getDefaultSettingsForKey(key) {
  if (key === 'paint') return PAINT_TOOL_DEFAULTS;
  if (key === 'airbrush') return AIRBRUSH_TOOL_DEFAULTS;
  if (key === 'calligraphy') return CALLIGRAPHY_TOOL_DEFAULTS;
  return PEN_TOOL_DEFAULTS;
}

function getStoredSettingsForKey(settingsByKey, key, fallbackColour = PEN_TOOL_DEFAULTS.strokeColor) {
  const defaults = {
    ...getDefaultSettingsForKey(key),
    strokeColor: fallbackColour,
  };
  const stored = settingsByKey && settingsByKey[key];
  return {
    ...defaults,
    ...(stored && typeof stored === 'object' ? stored : {}),
  };
}


function getDisplayNameFromValue(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value !== 'object') return '';
  return String(
    value.displayName
    || value.display_name
    || value.username
    || value.userName
    || value.name
    || value.nickname
    || ''
  ).trim();
}

function readStoredCurrentUserName() {
  if (typeof window === 'undefined') return '';
  const keys = ['currentUser', 'user', 'authUser', 'voxemeraUser', 'profile'];
  for (const key of keys) {
    try {
      const raw = window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const name = getDisplayNameFromValue(parsed);
      if (name) return name;
    } catch {
    }
  }
  return '';
}

function resolveCurrentUserName(currentUserName, currentUser, user) {
  return getDisplayNameFromValue(currentUserName)
    || getDisplayNameFromValue(currentUser)
    || getDisplayNameFromValue(user)
    || readStoredCurrentUserName()
    || 'User';
}


function normaliseOwnerValue(value) {
  return String(value || '').trim().toLowerCase();
}

function isLayerOwnedByCurrentUser(layer, clientId, ownerName, userId = 0) {
  if (!layer) return false;

  const safeUserId = Number(userId);
  const layerUserId = Number(layer.ownerUserId || layer.owner_user_id || 0);
  if (Number.isInteger(safeUserId) && safeUserId > 0 && Number.isInteger(layerUserId) && layerUserId > 0) {
    return safeUserId === layerUserId;
  }

  const safeClientId = String(clientId || '').trim();
  const layerClientId = String(layer.ownerClientId || '').trim();
  if (safeClientId && layerClientId) return safeClientId === layerClientId;

  const safeOwnerName = normaliseOwnerValue(ownerName);
  const layerOwnerName = normaliseOwnerValue(layer.ownerName);
  return Boolean(safeOwnerName && layerOwnerName && safeOwnerName === layerOwnerName);
}

function getEventOwnerName(event) {
  return String(
    event?.ownerName
    || event?.senderName
    || event?.userName
    || event?.displayName
    || ''
  ).trim();
}

function getUserIdFromValue(value) {
  if (!value || typeof value !== 'object') return 0;
  const numeric = Number(value.id || value.userId || value.user_id);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : 0;
}

function getCanvasOwnerId(canvas) {
  if (!canvas || typeof canvas !== 'object') return 0;
  const numeric = Number(canvas.owner_user_id || canvas.ownerUserId || canvas.ownerId);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : 0;
}

function getLayerOwnerKey(layer) {
  if (!layer) return '';
  const ownerUserId = Number(layer.ownerUserId || layer.owner_user_id);
  if (Number.isInteger(ownerUserId) && ownerUserId > 0) return `user:${ownerUserId}`;
  const ownerClientId = String(layer.ownerClientId || '').trim();
  if (ownerClientId) return `client:${ownerClientId}`;
  const ownerName = normaliseOwnerValue(layer.ownerName);
  return ownerName ? `name:${ownerName}` : '';
}


function sanitiseLayerIdPart(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'unknown';
}

function createOwnedLayerId(ownerProfile = {}, suffix = '') {
  const ownerUserId = Number(ownerProfile.ownerUserId || ownerProfile.owner_user_id || 0);
  const ownerClientId = String(ownerProfile.ownerClientId || '').trim();
  const ownerName = String(ownerProfile.ownerName || '').trim();
  const ownerToken = Number.isInteger(ownerUserId) && ownerUserId > 0
    ? `user-${ownerUserId}`
    : ownerClientId
      ? `client-${sanitiseLayerIdPart(ownerClientId)}`
      : `name-${sanitiseLayerIdPart(ownerName)}`;
  const safeSuffix = suffix ? `-${sanitiseLayerIdPart(suffix)}` : '';
  return `layer-${ownerToken}-${Date.now()}${safeSuffix}-${Math.random().toString(16).slice(2)}`;
}

function getOwnerIdentityKey(ownerUserId, ownerClientId, ownerName) {
  const numericUserId = Number(ownerUserId || 0);
  if (Number.isInteger(numericUserId) && numericUserId > 0) return `user:${numericUserId}`;
  const safeClientId = String(ownerClientId || '').trim();
  if (safeClientId) return `client:${safeClientId}`;
  const safeOwnerName = normaliseOwnerValue(ownerName);
  return safeOwnerName ? `name:${safeOwnerName}` : '';
}

function getLayerDeletionKey(layerId, ownerUserId, ownerClientId, ownerName) {
  return `${String(layerId || '').trim()}|${getOwnerIdentityKey(ownerUserId, ownerClientId, ownerName)}`;
}

function layerMatchesOwnerIdentity(layer, ownerUserId, ownerClientId, ownerName) {
  if (!layer) return false;
  const targetKey = getOwnerIdentityKey(ownerUserId, ownerClientId, ownerName);
  if (!targetKey) return true;
  return getLayerOwnerKey(layer) === targetKey;
}

function createReplacementLayer(ownerProfile, index = 0) {
  const ownerName = String(ownerProfile?.ownerName || 'User').trim() || 'User';
  const ownerClientId = String(ownerProfile?.ownerClientId || '').trim();
  const ownerUserId = Number(ownerProfile?.ownerUserId || 0);
  return {
    id: createOwnedLayerId(ownerProfile, `replacement-${index}`),
    name: 'Layer 1',
    ownerName,
    ownerClientId,
    ownerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : undefined,
    visible: true,
    opacity: 1,
    revision: 1,
    layerRevision: 1,
    updatedAt: Date.now(),
    layerUpdatedAt: Date.now(),
  };
}


function getLayerRevision(layer) {
  return Math.max(0, Number(layer && (layer.revision ?? layer.layerRevision)) || 0);
}

function getLayerUpdatedAt(layer) {
  const value = layer && (layer.updatedAt ?? layer.layerUpdatedAt ?? layer.updated_at);
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) return numeric;
  const parsed = Date.parse(String(value || ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function compareLayerVersions(left, right) {
  const leftRevision = getLayerRevision(left);
  const rightRevision = getLayerRevision(right);
  if (leftRevision !== rightRevision) return leftRevision > rightRevision ? 1 : -1;
  const leftUpdatedAt = getLayerUpdatedAt(left);
  const rightUpdatedAt = getLayerUpdatedAt(right);
  if (leftUpdatedAt !== rightUpdatedAt) return leftUpdatedAt > rightUpdatedAt ? 1 : -1;
  return 0;
}

function withLayerVersion(layer, revision, updatedAt) {
  const safeUpdatedAt = Math.max(1, Number(updatedAt) || Date.now());
  const safeRevision = Math.max(1, Number(revision) || safeUpdatedAt);
  return {
    ...layer,
    revision: safeRevision,
    layerRevision: safeRevision,
    updatedAt: safeUpdatedAt,
    layerUpdatedAt: safeUpdatedAt,
  };
}

function downloadDataUrl(dataUrl, fileName) {
  if (!dataUrl) return;
  const anchor = document.createElement('a');
  anchor.href = dataUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to read image file.'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(file);
  });
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to read image file.'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('Unable to decode image file.'));
      image.onload = () => resolve(image);
      image.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  });
}

function readTextFromFile(file) {
  if (file && typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to read Voxemera file.'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
}

function downloadBlob(blob, fileName) {
  if (!blob || typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function getReplayEventLayerId(event) {
  return String(
    event?.layerId
    || event?.state?.layerId
    || event?.targetLayerId
    || ''
  ).trim();
}

function isReplayDrawingEvent(event) {
  if (!event || typeof event !== 'object') return false;
  const type = String(event.type || '').trim();
  return type === 'stroke_start' || type === 'stroke_point' || type === 'stroke_end';
}

function cloneReplayEvent(event) {
  if (!event || typeof event !== 'object') return null;
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(event);
    } catch {
    }
  }
  return {
    ...event,
    point: event.point && typeof event.point === 'object' ? { ...event.point } : event.point,
    patch: event.patch && typeof event.patch === 'object' ? { ...event.patch } : event.patch,
    state: event.state && typeof event.state === 'object'
      ? {
          ...event.state,
          patch: event.state.patch && typeof event.state.patch === 'object'
            ? { ...event.state.patch }
            : event.state.patch,
        }
      : event.state,
  };
}

function filterReplayEventsForLayers(events, layers) {
  const layerIds = new Set(
    (Array.isArray(layers) ? layers : [])
      .map((layer) => String(layer && layer.id ? layer.id : '').trim())
      .filter(Boolean)
  );
  return (Array.isArray(events) ? events : []).filter((event) => {
    const layerId = getReplayEventLayerId(event);
    return layerId && layerIds.has(layerId);
  });
}

function serialiseReplayEvents(events) {
  return (Array.isArray(events) ? events : []).map((event) => {
    try {
      return JSON.parse(JSON.stringify(event));
    } catch {
      const safeEvent = {
        ...event,
        importedBrush: undefined,
        state: event && event.state && typeof event.state === 'object'
          ? { ...event.state, importedBrush: undefined }
          : event && event.state,
      };
      try {
        return JSON.parse(JSON.stringify(safeEvent));
      } catch {
        return null;
      }
    }
  }).filter(Boolean);
}

export default function CanvasRoomView({
  canvasId,
  canvas,
  canvasTabs = [],
  canvasTabBusy = false,
  onOpenCanvasTab,
  onCreateCanvasTab,
  onRenameCanvasTab,
  onCopyCanvasTab,
  onDeleteCanvasTab,
  onReorderCanvasTabs,
  onBack,
  currentUserName,
  currentUser,
  user,
  // Drawing games embed this editor with gameMode = { key, rules, forced, underlays, overlays, mirror,
  // rotation, blindLabel, apiRef, onEngineEvent, onContentChange, isInputBlocked }. The document is local
  // (a negative canvasId, so nothing is sent to the Canvas server) and the rules restrict the real tools.
  gameMode = null,
}) {
  const stageRef = useRef(null);
  const modelViewportRef = useRef(null);
  const canvasViewportRef = useRef(null);
  const canvasFrameRef = useRef(null);
  const zoomRef = useRef(DEFAULT_ZOOM);
  const viewRotationRef = useRef(0);
  const viewPanRef = useRef({ x: 0, y: 0 });
  const mobilePinchRef = useRef(null);
  const mobileTouchPointerIdsRef = useRef(new Set());
  const mobileGestureInputBlockedRef = useRef(false);
  const mobilePendingTouchStartRef = useRef(null);
  const initialFillCanvasIdRef = useRef('');
  const layerOwnerName = useMemo(
    () => resolveCurrentUserName(currentUserName, currentUser, user),
    [currentUser, currentUserName, user]
  );
  const currentUserId = useMemo(
    () => getUserIdFromValue(user) || getUserIdFromValue(currentUser),
    [currentUser, user]
  );
  const canvasOwnerId = useMemo(() => getCanvasOwnerId(canvas), [canvas]);
  const isCanvasOwner = currentUserId > 0 && canvasOwnerId === currentUserId;
  const gameModeRef = useRef(gameMode);
  gameModeRef.current = gameMode;
  const gameRulesKey = gameMode ? JSON.stringify([gameMode.rules || {}, gameMode.forced || null]) : '';
  const gameRestrictions = useMemo(
    () => (gameRulesKey ? resolveGameRestrictions(...JSON.parse(gameRulesKey)) : null),
    [gameRulesKey]
  );
  const gameRestrictionsRef = useRef(gameRestrictions);
  gameRestrictionsRef.current = gameRestrictions;
  const persistenceId = gameMode && gameMode.key ? `game:${gameMode.key}` : canvasId;
  const resolvedCanvasDocumentSize = useMemo(() => ({
    width: clampDocumentDimension(canvas && canvas.canvas_width, DEFAULT_DOCUMENT_SIZE.width),
    height: clampDocumentDimension(canvas && canvas.canvas_height, DEFAULT_DOCUMENT_SIZE.height),
  }), [canvas]);
  const [documentSize, setDocumentSize] = useState(() => resolvedCanvasDocumentSize);
  const canvasMetadataReady = Boolean(
    canvas
    && Number(canvas.id) === Number(canvasId)
    && Number(canvas.canvas_width) > 0
    && Number(canvas.canvas_height) > 0
  );
  const canvasStageReady = Boolean(
    canvasMetadataReady
    && documentSize.width === resolvedCanvasDocumentSize.width
    && documentSize.height === resolvedCanvasDocumentSize.height
  );
  const [savingDocumentSize, setSavingDocumentSize] = useState(false);
  const liveClientIdRef = useRef(createLiveClientId());
  const deletedLayerIdsRef = useRef(new Set());
  const inventoryRepliesRef = useRef(new Map()); // sender live client id -> when we last answered its inventory
  const privateRemoteLayerIdsRef = useRef(new Set());
  const latestLayerPrivacyRevisionRef = useRef(new Map());
  const latestLayerRevisionByOwnerRef = useRef(new Map());
  const localLayerRevisionRef = useRef(Date.now());
  const layersRef = useRef([]);
  const fileInputRef = useRef(null);
  const modelFileInputRef = useRef(null);
  const [modelViewOpen, setModelViewOpen] = useState(() => !gameMode && readStoredWorkspaceMode(canvasId) === '3d');
  const [paintMaterialSettings, setPaintMaterialSettings] = useState(readStored3dPaintMaterialSettings);
  const [pbrBrushActive, setPbrBrushActive] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handlePbrActiveChange = (event) => {
      setPbrBrushActive(Boolean(event?.detail?.active));
    };
    window.addEventListener('voxemera:3d-pbr-active-change', handlePbrActiveChange);
    return () => window.removeEventListener('voxemera:3d-pbr-active-change', handlePbrActiveChange);
  }, []);
  const [lock3dPaintToStartingSurface, setLock3dPaintToStartingSurface] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('voxemera-3d-lock-starting-surface') === '1';
  });
  const handleLock3dPaintToStartingSurfaceChange = useCallback((enabled) => {
    const nextValue = Boolean(enabled);
    setLock3dPaintToStartingSurface(nextValue);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('voxemera-3d-lock-starting-surface', nextValue ? '1' : '0');
    }
  }, []);
  const handlePaintMaterialSettingsChange = useCallback((key, value) => {
    const safeKey = ['gloss', 'height', 'metalness'].includes(key) ? key : null;
    if (!safeKey) return;

    const numericValue = Number(value);
    const safeValue = Number.isFinite(numericValue)
      ? Math.max(0, Math.min(100, numericValue))
      : DEFAULT_3D_PAINT_MATERIAL_SETTINGS[safeKey];

    setPaintMaterialSettings((current) => {
      const nextSettings = sanitize3dPaintMaterialSettings({
        ...(current || DEFAULT_3D_PAINT_MATERIAL_SETTINGS),
        [safeKey]: safeValue,
      });
      writeStored3dPaintMaterialSettings(nextSettings);
      return nextSettings;
    });
  }, []);
  const [modelFile, setModelFile] = useState(null);
  const fileInputModeRef = useRef('image');
  const replayEventsRef = useRef([]);
  const replaySequenceRef = useRef(0);
  const deletedReplayLayerEventsRef = useRef(new Map());
  const historyRef = useRef([]);
  const captureSnapshotRef = useRef(null);
  const redoHistoryRef = useRef([]);
  const clearSnapshotRef = useRef(null);
  const clearHistoryResetPendingRef = useRef(false);
  const localStateHydratedRef = useRef(false);
  const localStateRestoreStartedRef = useRef(false);
  const localStateSaveTimerRef = useRef(0);
  const localStateSaveInFlightRef = useRef(Promise.resolve());
  const [localStateRevision, setLocalStateRevision] = useState(0);
  const [replayRevision, setReplayRevision] = useState(0);
  const [replayOpen, setReplayOpen] = useState(false);
  const initialToolSettingsRef = useRef(null);
  const initialLastToolRef = useRef(null);
  if (initialToolSettingsRef.current === null) initialToolSettingsRef.current = readStoredToolSettings();
  if (initialLastToolRef.current === null) initialLastToolRef.current = readStoredLastTool();
  const initialLastColourRef = useRef(null);
  if (initialLastColourRef.current === null) initialLastColourRef.current = readStoredLastColour();
  const initialPenSettings = getStoredSettingsForKey(initialToolSettingsRef.current, 'pen', initialLastColourRef.current);
  const [toolSettingsByKey, setToolSettingsByKey] = useState(initialToolSettingsRef.current);
  const [activeTool, setActiveToolState] = useState(() => {
    const initialTool = initialLastToolRef.current.activeTool;
    return gameRestrictions && !isToolAllowed(gameRestrictions, initialTool) ? fallbackTool(gameRestrictions) : initialTool;
  });
  // In game mode a tool the rules do not allow cannot be picked (from the rail, a shortcut or a preset).
  const setActiveTool = useCallback((next) => {
    setActiveToolState((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      const restrictions = gameRestrictionsRef.current;
      return restrictions && !isToolAllowed(restrictions, value) ? current : value;
    });
  }, []);
  const activeToolRef = useRef(activeTool);
  activeToolRef.current = activeTool;
  const [transformMode, setTransformMode] = useState('scale');
  const [perspectiveAxis, setPerspectiveAxis] = useState('vertical');
  const [activeBrushPreset, setActiveBrushPreset] = useState(initialLastToolRef.current.activeBrushPreset);
  const [strokeColor, setStrokeColor] = useState(initialPenSettings.strokeColor);
  const [brushSize, setBrushSize] = useState(initialPenSettings.brushSize);
  const strokeColorRef = useRef(strokeColor);
  strokeColorRef.current = strokeColor;
  const brushSizeRef = useRef(brushSize);
  brushSizeRef.current = brushSize;
  const [brushSoftness, setBrushSoftness] = useState(initialPenSettings.brushSoftness);
  const [jitterAmount, setJitterAmount] = useState(initialPenSettings.jitterAmount);
  const [toolOpacity, setToolOpacity] = useState(initialPenSettings.toolOpacity);
  const [textSettings, setTextSettings] = useState(() => {
    if (typeof window === 'undefined') return { text: '', fontFamily: 'Arial', fontSize: 48, lineHeight: 1.2, letterSpacing: 0, align: 'left', bold: false, italic: false, underline: false };
    try {
      const parsed = JSON.parse(window.localStorage.getItem('voxemera.canvas.textSettings') || 'null');
      return {
        text: typeof parsed?.text === 'string' ? parsed.text : '',
        fontFamily: typeof parsed?.fontFamily === 'string' ? parsed.fontFamily : 'Arial',
        fontSize: Math.max(6, Math.min(400, Number(parsed?.fontSize) || 48)),
        lineHeight: Math.max(0.8, Math.min(3, Number(parsed?.lineHeight) || 1.2)),
        bold: parsed?.bold === true,
        italic: parsed?.italic === true,
        underline: parsed?.underline === true,
        letterSpacing: Math.max(-20, Math.min(100, Number(parsed?.letterSpacing) || 0)),
        align: ['left', 'center', 'right'].includes(parsed?.align) ? parsed.align : 'left',
      };
    } catch {
      return { text: '', fontFamily: 'Arial', fontSize: 48, lineHeight: 1.2, letterSpacing: 0, align: 'left', bold: false, italic: false, underline: false };
    }
  });
  const [bucketExpansion, setBucketExpansion] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const stored = Number(window.localStorage.getItem('voxemera.canvas.bucketExpansion'));
    return Number.isFinite(stored) ? Math.max(-20, Math.min(20, Math.round(stored))) : 0;
  });
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [viewRotation, setViewRotation] = useState(0);
  const [viewPan, setViewPan] = useState({ x: 0, y: 0 });


  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('voxemera.canvas.textSettings', JSON.stringify(textSettings));
    } catch {}
  }, [textSettings]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    viewRotationRef.current = viewRotation;
  }, [viewRotation]);

  useEffect(() => {
    viewPanRef.current = viewPan;
  }, [viewPan]);
  const [historyCount, setHistoryCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);
  const [hasPixels, setHasPixels] = useState(false);
  const [pencilOrientation, setPencilOrientation] = useState(0);
  const [pencilGrade, setPencilGrade] = useState(0);
  const [calligraphyAutoEnabled, setCalligraphyAutoEnabled] = useState(true);
  const [calligraphySettings, setCalligraphySettings] = useState({ ...DEFAULT_CALLIGRAPHY_SETTINGS });
  const [airbrushDistance, setAirbrushDistance] = useState(AIRBRUSH_TOOL_DEFAULTS.airbrushDistance);
  const [airbrushPaintFlow, setAirbrushPaintFlow] = useState(AIRBRUSH_TOOL_DEFAULTS.airbrushPaintFlow);
  const [airbrushPressurePaintFlowEnabled, setAirbrushPressurePaintFlowEnabled] = useState(AIRBRUSH_TOOL_DEFAULTS.airbrushPressurePaintFlowEnabled);
  const [airbrushAirPressure, setAirbrushAirPressure] = useState(AIRBRUSH_TOOL_DEFAULTS.airbrushAirPressure);
  const [airbrushNeedleLimit, setAirbrushNeedleLimit] = useState(AIRBRUSH_TOOL_DEFAULTS.airbrushNeedleLimit);
  const [liquifyMode, setLiquifyMode] = useState(DEFAULT_LIQUIFY_MODE);
  const [liquifySettings, setLiquifySettings] = useState({
    ...DEFAULT_LIQUIFY_SETTINGS,
    size: 50,
    strength: 50,
  });
  const [paintSettings, setPaintSettingsState] = useState(() => normalisePaintSettings({
    ...getPaintSettings(),
    ...readStoredPaintSettings(),
  }));
  const [activeImportedBrushId, setActiveImportedBrushId] = useState(initialLastToolRef.current.activeImportedBrushId);
  const [importedBrushPresets, setImportedBrushPresets] = useState([]);
  const [droppedBrushFiles, setDroppedBrushFiles] = useState(null);
  const importedBrushLibraryRestoredRef = useRef(false);
  const importedBrushLoadingRef = useRef(new Set());

  // Brushes live in the IndexedDB brush library (brushImport/brushLibrary.js).
  // The version-1 session record is migrated once and left untouched.
  const reloadBrushLibrary = useCallback(async () => {
    const records = (await listBrushRecords()).filter((record) => !record.library.deletedAt);
    const shells = await Promise.all(records.map(loadRuntimeShell));
    setImportedBrushPresets((current) => {
      const loaded = new Map((Array.isArray(current) ? current : []).filter((brush) => brush && brush.loaded).map((brush) => [brush.id, brush]));
      return shells.map((shell) => {
        const previous = loaded.get(shell.id);
        if (previous && previous.brushRecord && previous.brushRecord.updatedAt === shell.brushRecord.updatedAt) return previous;
        return shell;
      });
    });
    return records;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const legacy = await readCanvasState(IMPORTED_BRUSH_LIBRARY_STATE_KEY).catch(() => null);
        if (Array.isArray(legacy?.brushes) && legacy.brushes.length) await migrateLegacyLibrary(legacy.brushes);
        await reloadBrushLibrary();
        if (cancelled) return;
        const savedActiveId = String((await getBrushLibraryMeta('activeBrushId')) || legacy?.activeImportedBrushId || '');
        if (savedActiveId && !cancelled) setActiveImportedBrushId((current) => current || savedActiveId);
      } catch {}
      if (!cancelled) importedBrushLibraryRestoredRef.current = true;
    })();
    const unsubscribe = subscribeLibrary(() => {
      reloadBrushLibrary().catch(() => {});
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [reloadBrushLibrary]);

  // Shells carry only a preview; decode tips when a brush is selected.
  useEffect(() => {
    if (!activeImportedBrushId) return;
    if (importedBrushLibraryRestoredRef.current) setBrushLibraryMeta('activeBrushId', activeImportedBrushId).catch(() => {});
    const brush = importedBrushPresets.find((item) => item && item.id === activeImportedBrushId);
    if (!brush || brush.loaded || !brush.brushRecord || importedBrushLoadingRef.current.has(brush.id)) return;
    importedBrushLoadingRef.current.add(brush.id);
    loadRuntimePreset(brush.brushRecord)
      .then((runtime) => {
        setImportedBrushPresets((current) => current.map((item) => (item && item.id === runtime.id ? runtime : item)));
        updateBrushRecord(runtime.id, { library: { lastUsedAt: Date.now() } }).catch(() => {});
      })
      .catch(() => {})
      .finally(() => importedBrushLoadingRef.current.delete(brush.id));
  }, [activeImportedBrushId, importedBrushPresets]);

  const [animationEnabled, setAnimationEnabled] = useState(() => Boolean(canvas && (canvas.animation_enabled != null ? canvas.animation_enabled : canvas.animationEnabled)));
  const animationEnabledRef = useRef(Boolean(canvas && (canvas.animation_enabled != null ? canvas.animation_enabled : canvas.animationEnabled)));
  const [animationFrames, setAnimationFrames] = useState([]);
  const [animationActiveFrameId, setAnimationActiveFrameId] = useState('');
  const animationFramesRef = useRef([]);
  const animationActiveFrameIdRef = useRef('');
  const animationActiveFrameIndexRef = useRef(0);
  const animationMountedFrameIdRef = useRef('');
  const animationMountedFrameIndexRef = useRef(-1);
  const animationRestoringFrameRef = useRef(false);
  const animationStageSnapshotsRef = useRef(new Map());
  const animationCaptureTimerRef = useRef(0);
  const animationBroadcastTimerRef = useRef(0);
  const animationScheduleSaveRef = useRef(null);
  const canvasLiveMessageHandlerRef = useRef(null);
  const initialLayerIdRef = useRef('');
  if (!initialLayerIdRef.current) {
    initialLayerIdRef.current = createOwnedLayerId({
      ownerName: layerOwnerName,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId || 0,
    }, 'initial');
  }
  const [layers, setLayers] = useState([withLayerVersion({ id: initialLayerIdRef.current, name: 'Layer 1', ownerName: layerOwnerName, ownerClientId: liveClientIdRef.current, ownerUserId: currentUserId || undefined, visible: true, opacity: 1 }, 1, Date.now())]);
  const [textObjectsByLayer, setTextObjectsByLayer] = useState({});
  const textObjectsByLayerRef = useRef({});
  const [canvasAreasOpen, setCanvasAreasOpen] = useState(false);
  const [areaAssignments, setAreaAssignments] = useState(() => sanitizeCanvasAreaAssignments(null));
  const areaAssignmentsRef = useRef(sanitizeCanvasAreaAssignments(null));
  const [activeLayerId, setActiveLayerId] = useState(initialLayerIdRef.current);
  const canvasAreaUsers = useMemo(() => getCanvasAreaUsersFromLayers(
    layers,
    currentUserId,
    layerOwnerName,
    liveClientIdRef,
    canvas,
    []
  ), [canvas, currentUserId, layerOwnerName, layers]);
  const currentCanvasAreaOwnerKey = useMemo(() => {
    const fallbackOwnerKey = getCurrentUserAreaOwnerKey(currentUserId, liveClientIdRef);
    const safeUserId = Number(currentUserId || 0);
    const safeClientId = String(liveClientIdRef.current || '').trim();
    const slots = areaAssignments && areaAssignments.slots && typeof areaAssignments.slots === 'object'
      ? Object.values(areaAssignments.slots)
      : [];

    const matchingSlot = slots.find((slot) => {
      if (!slot || typeof slot !== 'object') return false;
      const slotUserId = Number(slot.ownerUserId || 0);
      if (safeUserId > 0 && slotUserId > 0 && safeUserId === slotUserId) return true;
      const slotClientId = String(slot.ownerClientId || '').trim();
      return Boolean(safeClientId && slotClientId && safeClientId === slotClientId);
    });

    return String(matchingSlot && matchingSlot.ownerKey ? matchingSlot.ownerKey : fallbackOwnerKey).trim();
  }, [areaAssignments, currentUserId]);
  const activeCanvasAreaSelection = useMemo(() => getCanvasAreaSelectionForOwner(
    areaAssignments,
    documentSize,
    currentCanvasAreaOwnerKey,
    activeLayerId
  ), [activeLayerId, areaAssignments, currentCanvasAreaOwnerKey, documentSize]);
  const canCurrentUserDrawAtCanvasPoint = useCallback((point) => canAreaOwnerDrawAtPoint(
    point,
    areaAssignments,
    documentSize,
    currentCanvasAreaOwnerKey
  ), [areaAssignments, currentCanvasAreaOwnerKey, documentSize]);


  useEffect(() => {
    areaAssignmentsRef.current = sanitizeCanvasAreaAssignments(areaAssignments);
  }, [areaAssignments]);

  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    animationEnabledRef.current = animationEnabled;
  }, [animationEnabled]);

  useEffect(() => {
    animationFramesRef.current = animationFrames;
    const activeIndex = animationFrames.findIndex((frame) => String(frame && frame.id ? frame.id : '') === String(animationActiveFrameIdRef.current || ''));
    if (activeIndex >= 0) animationActiveFrameIndexRef.current = activeIndex;
  }, [animationFrames]);

  useEffect(() => {
    animationActiveFrameIdRef.current = animationActiveFrameId;
    const activeIndex = animationFramesRef.current.findIndex((frame) => String(frame && frame.id ? frame.id : '') === String(animationActiveFrameId || ''));
    if (activeIndex >= 0) animationActiveFrameIndexRef.current = activeIndex;
  }, [animationActiveFrameId]);

  useEffect(() => {
    const nextEnabled = Boolean(canvas && (canvas.animation_enabled != null ? canvas.animation_enabled : canvas.animationEnabled));
    animationEnabledRef.current = nextEnabled;
    setAnimationEnabled(nextEnabled);
  }, [canvas]);

  useEffect(() => {
    const key = getCanvasAnimationStateKey(canvasId);
    if (!key) return undefined;
    let cancelled = false;
    readCanvasState(key).then((payload) => {
      if (cancelled || !payload || typeof payload !== 'object') return null;
      return readAnimationFramesIndividually(canvasId, payload);
    }).then((storedAnimation) => {
      if (cancelled || !storedAnimation) return;
      const frames = sanitizeAnimationFrames(storedAnimation.frames);
      if (!frames.length) return;
      const activeFrameId = String(storedAnimation.activeFrameId || frames[0].id);
      animationFramesRef.current = frames;
      animationActiveFrameIdRef.current = activeFrameId;
      animationActiveFrameIndexRef.current = Math.max(0, frames.findIndex((frame) => frame.id === activeFrameId));
      setAnimationFrames(frames);
      setAnimationActiveFrameId(activeFrameId);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canvasId]);

  const sketchRelay = useSketchRelayGame({ canvasId });
  const sketchRelayLiveHandlerRef = useRef(sketchRelay.handleLiveEvent);
  sketchRelayLiveHandlerRef.current = sketchRelay.handleLiveEvent;
  // The Games tab opens a new canvas with ?game=sketch-relay so the game panel is ready.
  const [canvasGamePanelOpen, setCanvasGamePanelOpen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('game') === 'sketch-relay';
    } catch {
      return false;
    }
  });

  const canvasName = `Canvas ${Number(canvasId) || ''}`.trim();

  const appendReplayEvent = useCallback((event) => {
    if (!isReplayDrawingEvent(event)) return null;
    const layerId = getReplayEventLayerId(event);
    if (!layerId) return null;
    const clonedEvent = cloneReplayEvent(event);
    if (!clonedEvent) return null;
    const replaySequence = replaySequenceRef.current + 1;
    replaySequenceRef.current = replaySequence;
    const recordedEvent = {
      ...clonedEvent,
      layerId,
      replaySequence,
      loggedAt: Date.now(),
    };
    replayEventsRef.current.push(recordedEvent);
    if (String(recordedEvent.type || '') === 'stroke_end') {
      setReplayRevision((value) => value + 1);
    }
    return recordedEvent;
  }, []);

  const purgeReplayLayerEvents = useCallback((layerId) => {
    const safeLayerId = String(layerId || '').trim();
    if (!safeLayerId) return false;
    const removedEvents = replayEventsRef.current.filter(
      (event) => getReplayEventLayerId(event) === safeLayerId
    );
    if (!removedEvents.length) return false;
    replayEventsRef.current = replayEventsRef.current.filter(
      (event) => getReplayEventLayerId(event) !== safeLayerId
    );
    const archivedEvents = deletedReplayLayerEventsRef.current.get(safeLayerId) || [];
    deletedReplayLayerEventsRef.current.set(
      safeLayerId,
      [...archivedEvents, ...removedEvents].sort(
        (left, right) => Number(left.replaySequence || 0) - Number(right.replaySequence || 0)
      )
    );
    setReplayRevision((value) => value + 1);
    return true;
  }, []);

  const restoreReplayLayerEvents = useCallback((layerId) => {
    const safeLayerId = String(layerId || '').trim();
    if (!safeLayerId) return false;
    const archivedEvents = deletedReplayLayerEventsRef.current.get(safeLayerId);
    if (!Array.isArray(archivedEvents) || !archivedEvents.length) return false;
    replayEventsRef.current = [...replayEventsRef.current, ...archivedEvents].sort(
      (left, right) => Number(left.replaySequence || 0) - Number(right.replaySequence || 0)
    );
    deletedReplayLayerEventsRef.current.delete(safeLayerId);
    setReplayRevision((value) => value + 1);
    return true;
  }, []);

  const handleLocalLiveEvent = useCallback((event) => {
    const game = gameModeRef.current;
    if (game && event && typeof game.onEngineEvent === 'function') {
      game.onEngineEvent(event, { tool: activeToolRef.current, color: strokeColorRef.current, size: brushSizeRef.current });
    }
    const safeCanvasId = Number(canvasId);
    if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0 || !event) return;

    const eventLayerId = String(event.layerId || activeLayerId || '').trim();
    let eventLayer = layersRef.current.find((layer) => String(layer && layer.id ? layer.id : '') === eventLayerId) || null;
    let versionFields = {};

    if ((String(event.type || '') === 'stroke_end' || String(event.type || '') === 'paint_action') && eventLayerId) {
      const updatedAt = Date.now();
      const revision = Math.max(getLayerRevision(eventLayer) + 1, updatedAt);
      eventLayer = withLayerVersion(eventLayer || { id: eventLayerId }, revision, updatedAt);
      const nextLayers = layersRef.current.map((layer) => String(layer && layer.id ? layer.id : '') === eventLayerId
        ? { ...layer, ...eventLayer }
        : layer);
      layersRef.current = nextLayers;
      setLayers(nextLayers);
      versionFields = {
        revision,
        layerRevision: revision,
        updatedAt,
        layerUpdatedAt: updatedAt,
      };
    }

    const eventTimestamp = Date.now();
    const outgoingFrames = animationEnabledRef.current
      ? sanitizeAnimationFrames(animationFramesRef.current)
      : [];
    const mountedFrameId = String(animationMountedFrameIdRef.current || animationActiveFrameIdRef.current || '');
    const mountedFrameIndexFromId = outgoingFrames.findIndex(
      (frame) => String(frame && frame.id ? frame.id : '') === mountedFrameId
    );
    const rememberedMountedFrameIndex = Number(animationMountedFrameIndexRef.current);
    const outgoingFrameIndex = animationEnabledRef.current
      ? mountedFrameIndexFromId >= 0
        ? mountedFrameIndexFromId
        : Number.isInteger(rememberedMountedFrameIndex)
          && rememberedMountedFrameIndex >= 0
          && rememberedMountedFrameIndex < outgoingFrames.length
          ? rememberedMountedFrameIndex
          : -1
      : -1;
    const outgoingFrameId = animationEnabledRef.current && outgoingFrameIndex >= 0
      ? String(outgoingFrames[outgoingFrameIndex]?.id || mountedFrameId || '')
      : '';
    if (animationEnabledRef.current && (outgoingFrameIndex < 0 || !outgoingFrameId)) return;
    const outgoingEvent = {
      ...event,
      ...versionFields,
      clientId: liveClientIdRef.current,
      ownerClientId: liveClientIdRef.current,
      ownerName: layerOwnerName,
      ownerUserId: currentUserId || undefined,
      layerName: eventLayer?.name || layerOwnerName,
      layerId: eventLayerId,
      frameId: outgoingFrameId,
      animationFrameId: outgoingFrameId,
      frameIndex: outgoingFrameIndex,
      animationFrameIndex: outgoingFrameIndex,
      seq: eventTimestamp,
      timestamp: Number(event.timestamp) || eventTimestamp,
    };
    outgoingEvent.state = {
      ...(outgoingEvent.state && typeof outgoingEvent.state === 'object' ? outgoingEvent.state : {}),
      frameId: outgoingFrameId,
      animationFrameId: outgoingFrameId,
      frameIndex: outgoingFrameIndex,
      animationFrameIndex: outgoingFrameIndex,
    };

    const completedAnimationEvent = animationEnabledRef.current
      && outgoingFrameId
      && eventLayerId
      && ['stroke_end', 'paint_action'].includes(String(outgoingEvent.type || ''));
    let animationConfirmationEvent = null;
    if (completedAnimationEvent && stageRef.current && typeof stageRef.current.exportLayerPatch === 'function') {
      const fullLayerPatch = stageRef.current.exportLayerPatch(eventLayerId);
      if (fullLayerPatch && typeof fullLayerPatch.dataUrl === 'string' && fullLayerPatch.dataUrl.startsWith('data:image/')) {
        const confirmationTimestamp = Date.now();
        const confirmationPatch = {
          ...fullLayerPatch,
          layerId: eventLayerId,
          replaceLayer: true,
          fullLayer: true,
          mode: 'replace_layer',
          frameId: outgoingFrameId,
          animationFrameId: outgoingFrameId,
          frameIndex: outgoingFrameIndex,
          animationFrameIndex: outgoingFrameIndex,
        };
        animationConfirmationEvent = {
          ...outgoingEvent,
          type: 'raster_patch',
          strokeId: `${String(outgoingEvent.strokeId || 'stroke')}-frame-confirm-${confirmationTimestamp}`,
          patch: confirmationPatch,
          seq: confirmationTimestamp,
          timestamp: confirmationTimestamp,
          state: {
            ...(outgoingEvent.state && typeof outgoingEvent.state === 'object' ? outgoingEvent.state : {}),
            frameId: outgoingFrameId,
            animationFrameId: outgoingFrameId,
            frameIndex: outgoingFrameIndex,
            animationFrameIndex: outgoingFrameIndex,
            patch: confirmationPatch,
          },
        };
      }
    }
    appendReplayEvent(outgoingEvent);
    const outgoingType = String(outgoingEvent.type || '');
    if (animationEnabledRef.current && (outgoingType === 'stroke_end' || outgoingType === 'paint_action' || outgoingType === 'raster_patch')) {
      if (typeof animationScheduleSaveRef.current === 'function') animationScheduleSaveRef.current('frame_update', false);
    }
    if (eventLayer && eventLayer.hiddenFromOthers === true) return;
    postCanvasLiveStrokeEvent(safeCanvasId, outgoingEvent);
    if (animationConfirmationEvent) {
      postCanvasLiveStrokeEvent(safeCanvasId, animationConfirmationEvent);
    }
  }, [activeLayerId, appendReplayEvent, canvasId, currentUserId, layerOwnerName]);

  const sendCanvasAnimationEvent = useCallback((action = 'state', override = {}) => {
    const safeCanvasId = Number(canvasId);
    if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0) return false;
    const frames = sanitizeAnimationFrames(
      Array.isArray(override.frames) ? override.frames : animationFramesRef.current
    );
    const enabled = override.enabled !== undefined
      ? Boolean(override.enabled)
      : Boolean(animationEnabledRef.current);
    const activeFrameId = String(
      override.activeFrameId !== undefined
        ? override.activeFrameId
        : animationActiveFrameIdRef.current
    );
    const payload = {
      enabled,
      animationEnabled: enabled,
      activeFrameId,
      frames,
      savedAt: Date.now(),
    };
    postCanvasLiveStrokeEvent(safeCanvasId, {
      type: 'animation_info',
      clientId: liveClientIdRef.current,
      userId: currentUserId,
      senderUserId: currentUserId,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId,
      ownerName: layerOwnerName,
      animationAction: String(action || 'state'),
      animationPayload: payload,
      strokeId: `animation-${currentUserId || 0}-${Date.now()}`,
      layerId: String(activeLayerId || '').trim(),
      tool: 'animation',
      brushPreset: 'animation',
      reason: `canvas_animation:${String(action || 'state')}`,
      state: {
        animationAction: String(action || 'state'),
        animationPayload: payload,
        enabled,
        animationEnabled: enabled,
        activeFrameId,
        frames,
      },
      timestamp: Date.now(),
    });
    return true;
  }, [activeLayerId, canvasId, currentUserId, layerOwnerName]);

  const sendCanvasAnimationModeEvent = useCallback((enabled, action = 'mode') => {
    return sendCanvasAnimationEvent(action, { enabled });
  }, [sendCanvasAnimationEvent]);

  const sendAuthoritativeLayer = useCallback((layer, targetClientId = '') => {
    if (modelViewOpen) return false;
    const safeCanvasId = Number(canvasId);
    const layerId = String(layer && layer.id ? layer.id : '').trim();
    if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0 || !layerId || !stageRef.current) return false;
    if (layer && layer.hiddenFromOthers === true) return false;
    if (typeof stageRef.current.exportLayerPatch !== 'function') return false;
    const patch = stageRef.current.exportLayerPatch(layerId);
    if (!patch) return false;
    const revision = getLayerRevision(layer);
    const updatedAt = getLayerUpdatedAt(layer);
    postCanvasLiveStrokeEvent(safeCanvasId, {
      type: 'layer_sync_snapshot',
      targetClientId: String(targetClientId || ''),
      layerId,
      layer: { ...layer, revision, layerRevision: revision, updatedAt, layerUpdatedAt: updatedAt },
      patch,
      revision,
      layerRevision: revision,
      updatedAt,
      layerUpdatedAt: updatedAt,
      clientId: liveClientIdRef.current,
      ownerClientId: String(layer.ownerClientId || liveClientIdRef.current),
      ownerUserId: layer.ownerUserId || undefined,
      ownerName: String(layer.ownerName || layerOwnerName),
      layerName: String(layer.name || 'Layer 1'),
      strokeId: `layer-sync-${layerId}-${revision}-${Date.now()}`,
      reason: 'layer_sync_authoritative',
      seq: Date.now(),
    });
    return true;
  }, [canvasId, layerOwnerName, modelViewOpen]);

  const sendLayerPrivacyEvent = useCallback((layer) => {
    const safeCanvasId = Number(canvasId);
    const layerId = String(layer && layer.id ? layer.id : '').trim();
    if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0 || !layerId) return Promise.resolve(false);

    const hiddenFromOthers = layer.hiddenFromOthers === true;
    const revision = getLayerRevision(layer);
    const updatedAt = getLayerUpdatedAt(layer);
    const ownerName = String(layer.ownerName || layerOwnerName || 'User').trim() || 'User';
    const ownerClientId = String(layer.ownerClientId || liveClientIdRef.current || '').trim();
    const ownerUserId = Number(layer.ownerUserId || layer.owner_user_id || currentUserId || 0);

    return postCanvasLiveStrokeEvent(safeCanvasId, {
      type: 'mask_info',
      clientId: liveClientIdRef.current,
      userId: currentUserId || undefined,
      senderUserId: currentUserId || undefined,
      ownerClientId,
      ownerName,
      ownerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : undefined,
      layerId,
      targetLayerId: layerId,
      layerName: String(layer.name || 'Layer 1'),
      hiddenFromOthers,
      revision,
      layerRevision: revision,
      updatedAt,
      layerUpdatedAt: updatedAt,
      seq: Date.now(),
      strokeId: `layer-privacy-${layerId}-${revision}-${Date.now()}`,
      tool: 'mask',
      brushPreset: 'mask',
      maskAction: 'layer_privacy',
      reason: 'layer_event:privacy',
      maskInfo: {
        type: 'canvas_layer_event',
        version: 2,
        layerId,
        maskId: String(layer.name || layerId).slice(0, 128),
        shape: 'rect',
        action: 'layer_privacy',
        hiddenFromOthers,
        inverted: false,
        bounds: { x: 0, y: 0, width: 0, height: 0 },
        transform: { translateX: 0, translateY: 0, rotation: 0, scaleX: 1, scaleY: 1, originX: 0, originY: 0 },
        vertices: [],
        maskVertices: [],
        maskVertexCount: 0,
      },
      state: {
        maskAction: 'layer_privacy',
        reason: 'layer_event:privacy',
        hiddenFromOthers,
        revision,
        layerRevision: revision,
        updatedAt,
        layerUpdatedAt: updatedAt,
      },
      timestamp: Date.now(),
    });
  }, [canvasId, currentUserId, layerOwnerName]);

  const sendLayerState = useCallback((nextLayers, { reply = false } = {}) => {
    if (modelViewOpen) return;
    const safeCanvasId = Number(canvasId);
    if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0) return;
    const ownedSourceLayers = (Array.isArray(nextLayers) ? nextLayers : []).filter((layer) => isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    const ownedLayers = ownedSourceLayers.filter((layer) => layer.hiddenFromOthers !== true);
    const hiddenLayerIds = ownedSourceLayers
      .filter((layer) => layer.hiddenFromOthers === true)
      .map((layer) => String(layer.id || '').trim())
      .filter(Boolean);
    const ownerKey = getOwnerIdentityKey(currentUserId, liveClientIdRef.current, layerOwnerName);
    const knownRevision = Number(latestLayerRevisionByOwnerRef.current.get(ownerKey) || 0);
    const nextRevision = Math.max(Date.now(), localLayerRevisionRef.current + 1, knownRevision + 1);
    localLayerRevisionRef.current = nextRevision;
    if (ownerKey) latestLayerRevisionByOwnerRef.current.set(ownerKey, nextRevision);
    postCanvasLiveStrokeEvent(safeCanvasId, {
      type: 'layer_sync_inventory',
      layers: ownedLayers,
      hiddenLayerIds,
      clientId: liveClientIdRef.current,
      ownerClientId: liveClientIdRef.current,
      ownerName: layerOwnerName,
      ownerUserId: currentUserId || undefined,
      inventoryComplete: localStateHydratedRef.current === true,
      fullyLoaded: localStateHydratedRef.current === true,
      inventoryReply: reply === true,
      layerRevision: nextRevision,
      ownerLayerRevision: nextRevision,
      revision: nextRevision,
      seq: nextRevision,
    });
  }, [canvasId, currentUserId, layerOwnerName, modelViewOpen]);

  const sendCanvasAreaAssignmentsEvent = useCallback((nextAssignments) => {
    const safeCanvasId = Number(canvasId);
    if (!isCanvasOwner || !Number.isInteger(safeCanvasId) || safeCanvasId <= 0) return false;

    const safeAssignments = sanitizeCanvasAreaAssignments(nextAssignments);
    const timestamp = Date.now();

    postCanvasLiveStrokeEvent(safeCanvasId, {
      type: 'mask_info',
      clientId: liveClientIdRef.current,
      userId: currentUserId || undefined,
      senderUserId: currentUserId || undefined,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId || undefined,
      ownerName: layerOwnerName,
      areaAssignments: safeAssignments,
      canvasAreaAssignments: safeAssignments,
      strokeId: `area-assignments-${currentUserId || 0}-${timestamp}`,
      layerId: String(activeLayerId || '__area_assignments__').trim() || '__area_assignments__',
      tool: 'mask',
      brushPreset: 'mask',
      maskAction: 'area_assignments',
      reason: 'area_assignments:update',
      state: {
        maskAction: 'area_assignments',
        reason: 'area_assignments:update',
        areaAssignments: safeAssignments,
        canvasAreaAssignments: safeAssignments,
      },
      seq: timestamp,
      timestamp,
    });

    return true;
  }, [activeLayerId, canvasId, currentUserId, isCanvasOwner, layerOwnerName]);

  useEffect(() => {
    const safeCanvasId = Number(canvasId);
    if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0 || typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      return undefined;
    }

    const source = new window.EventSource(`/api/canvas-live/${encodeURIComponent(String(safeCanvasId))}/events`, {
      withCredentials: true,
    });

    source.onopen = () => {
      window.setTimeout(() => {
        const inventory = layersRef.current
          .filter((layer) => (
            isLayerOwnedByCurrentUser(
              layer,
              liveClientIdRef.current,
              layerOwnerName,
              currentUserId,
            ) && layer.hiddenFromOthers !== true
          ))
          .map((layer) => ({
            id: layer.id,
            name: layer.name,
            ownerName: layer.ownerName,
            ownerClientId: layer.ownerClientId,
            ownerUserId: layer.ownerUserId,
            visible: layer.visible !== false,
            opacity: layer.opacity == null ? 1 : layer.opacity,
            revision: getLayerRevision(layer),
            layerRevision: getLayerRevision(layer),
            updatedAt: getLayerUpdatedAt(layer),
            layerUpdatedAt: getLayerUpdatedAt(layer),
          }));
        sendLayerState(layersRef.current);
        if (isCanvasOwner) {
          sendCanvasAnimationModeEvent(animationEnabledRef.current, 'sync_state');
        }
      }, 150);
    };

    const handleSourceMessage = (message) => {
      try {
        const event = JSON.parse(String(message.data || '{}'));
        if (sketchRelayLiveHandlerRef.current(event)) return;
        const senderClientId = String(event.clientId || event.ownerClientId || '');
        if (!event || senderClientId === liveClientIdRef.current) return;
        const senderUserId = Number(event.ownerUserId || event.senderUserId || event.userId || 0);
        const senderName = getEventOwnerName(event) || 'User';
        const rawEventType = String(event.type || '').trim();
        const eventState = event.state && typeof event.state === 'object' ? event.state : {};
        const eventMaskInfo = event.maskInfo && typeof event.maskInfo === 'object' ? event.maskInfo : null;
        const incomingAreaAssignments = event.areaAssignments
          || event.canvasAreaAssignments
          || eventState.areaAssignments
          || eventState.canvasAreaAssignments
          || null;
        if (incomingAreaAssignments && typeof incomingAreaAssignments === 'object') {
          const safeIncomingAreaAssignments = sanitizeCanvasAreaAssignments(incomingAreaAssignments);
          areaAssignmentsRef.current = safeIncomingAreaAssignments;
          setAreaAssignments(safeIncomingAreaAssignments);
        }
        const maskAction = String(
          event.maskAction
          || eventState.maskAction
          || (eventMaskInfo && (eventMaskInfo.action || eventMaskInfo.maskAction))
          || ''
        ).trim();
        const originalEventType = String(
          event.originalEventType
          || eventState.originalEventType
          || ''
        ).trim();
        const eventType = originalEventType
          || (rawEventType === 'mask_info' && (maskAction.startsWith('layer_sync_') || maskAction.startsWith('canvas_sync_'))
            ? maskAction
            : rawEventType);
        if (rawEventType === 'workspace_mode') {
          const incomingWorkspaceMode = String(
            event.workspaceMode
            || event.mode
            || eventState.workspaceMode
            || eventState.mode
            || ''
          ).trim().toLowerCase() === '3d' ? '3d' : '2d';
          writeStoredWorkspaceMode(canvasId, incomingWorkspaceMode);
          setModelViewOpen(incomingWorkspaceMode === '3d');
          return;
        }

        const isAnimationEvent = rawEventType === 'animation_info'
          || String(event.animationAction || eventState.animationAction || '').trim()
          || String(event.reason || eventState.reason || '').startsWith('canvas_animation:');
        if (isAnimationEvent) {
          const animationPayload = event.animationPayload && typeof event.animationPayload === 'object'
            ? event.animationPayload
            : eventState.animationPayload && typeof eventState.animationPayload === 'object'
              ? eventState.animationPayload
              : eventState;
          const nextEnabled = animationPayload.enabled != null
            ? Boolean(animationPayload.enabled)
            : animationPayload.animationEnabled != null
              ? Boolean(animationPayload.animationEnabled)
              : event.enabled != null
                ? Boolean(event.enabled)
                : Boolean(event.animationEnabled);
          const animationAction = String(event.animationAction || eventState.animationAction || 'state').trim() || 'state';
          const incomingFrames = sanitizeAnimationFrames(animationPayload.frames || event.frames || eventState.frames);
          const incomingActiveFrameId = String(
            animationPayload.activeFrameId
            || event.activeFrameId
            || eventState.activeFrameId
            || incomingFrames[0]?.id
            || ''
          );
          animationEnabledRef.current = nextEnabled;
          setAnimationEnabled(nextEnabled);
          if (incomingFrames.length) {
            for (const incomingFrame of incomingFrames) {
              const incomingFrameId = String(incomingFrame && incomingFrame.id || '');
              const staleSnapshot = animationStageSnapshotsRef.current.get(incomingFrameId);
              if (staleSnapshot && stageRef.current && typeof stageRef.current.destroySnapshot === 'function') {
                stageRef.current.destroySnapshot(staleSnapshot);
              }
              animationStageSnapshotsRef.current.delete(incomingFrameId);
            }
            const previousLocalActiveFrameId = String(animationActiveFrameIdRef.current || '');
            const localFrameStillExists = incomingFrames.some((frame) => frame.id === previousLocalActiveFrameId);
            const nextLocalActiveFrameId = localFrameStillExists
              ? previousLocalActiveFrameId
              : String(incomingActiveFrameId || incomingFrames[0]?.id || '');
            const localActiveFrameWasRemoved = Boolean(previousLocalActiveFrameId) && !localFrameStillExists;
            const isServerAnimationSnapshot = Boolean(
              event.animationServerSnapshot === true
              || eventState.animationServerSnapshot === true
            );
            const isInitialAnimationSnapshot = Boolean(
              !isServerAnimationSnapshot
              && !previousLocalActiveFrameId
              && nextEnabled
            );

            animationFramesRef.current = incomingFrames;
            animationActiveFrameIdRef.current = nextLocalActiveFrameId;
            animationActiveFrameIndexRef.current = Math.max(0, incomingFrames.findIndex((frame) => frame.id === nextLocalActiveFrameId));
            setAnimationFrames(incomingFrames);
            setAnimationActiveFrameId(nextLocalActiveFrameId);
            persistAnimationFramesIndividually(
              canvasId,
              incomingFrames,
              nextLocalActiveFrameId,
              nextEnabled,
            ).catch(() => {});

            const localActiveFrame = incomingFrames.find((frame) => frame.id === nextLocalActiveFrameId) || incomingFrames[0];
            const stageHasVisiblePixels = Boolean(
              stageRef.current
              && typeof stageRef.current.hasPixels === 'function'
              && stageRef.current.hasPixels()
            );
            const shouldRestoreVisibleFrame = Boolean(
              localActiveFrame
              && (
                (animationAction === 'frame_delete' && localActiveFrameWasRemoved)
                || (isInitialAnimationSnapshot && !stageHasVisiblePixels)
                || (isServerAnimationSnapshot && !stageHasVisiblePixels)
              )
            );
            if (shouldRestoreVisibleFrame && stageRef.current && !animationRestoringFrameRef.current) {
              window.setTimeout(() => {
                const stage = stageRef.current;
                if (!stage || animationRestoringFrameRef.current) return;
                animationRestoringFrameRef.current = true;
                const nextLayers = Array.isArray(localActiveFrame.layers) ? localActiveFrame.layers.map((layer) => ({ ...layer })) : [];
                const nextActiveLayerId = nextLayers.some((layer) => String(layer.id) === String(localActiveFrame.activeLayerId))
                  ? String(localActiveFrame.activeLayerId)
                  : String(nextLayers[nextLayers.length - 1]?.id || '');
                Promise.resolve().then(async () => {
                  if (nextLayers.length) {
                    stage.setLayers(nextLayers, nextActiveLayerId);
                    for (const layer of nextLayers) {
                      const layerId = String(layer.id || '').trim();
                      const bitmap = localActiveFrame.layerBitmaps && localActiveFrame.layerBitmaps[layerId];
                      if (layerId && typeof stage.clearLayer === 'function') stage.clearLayer(layerId);
                      if (layerId && typeof bitmap === 'string' && bitmap.startsWith('data:image/')) {
                        await stage.loadLayerDataUrl(layerId, bitmap);
                      }
                      const paintState = localActiveFrame.paintLayerStates && localActiveFrame.paintLayerStates[layerId];
                      if (layerId && paintState && typeof stage.loadPaintLayerState === 'function') {
                        await stage.loadPaintLayerState(layerId, paintState);
                      }
                    }
                    layersRef.current = nextLayers;
                    setLayers(nextLayers);
                    if (nextActiveLayerId) setActiveLayerId(nextActiveLayerId);
                    setHasPixels(stage.hasPixels());
                    if (typeof stage.snapshot === 'function') {
                      const restoredSnapshot = stage.snapshot();
                      if (restoredSnapshot) animationStageSnapshotsRef.current.set(String(localActiveFrame.id || ''), restoredSnapshot);
                    }
                  }
                }).finally(() => {
                  const mountedFrameId = String(localActiveFrame && localActiveFrame.id ? localActiveFrame.id : '');
                  const mountedFrameIndex = incomingFrames.findIndex(
                    (frame) => String(frame && frame.id ? frame.id : '') === mountedFrameId
                  );
                  animationMountedFrameIdRef.current = mountedFrameId;
                  animationMountedFrameIndexRef.current = mountedFrameIndex;
                  animationRestoringFrameRef.current = false;
                });
              }, 0);
            }
          }
          return;
        }

        // Sketch Relay used to travel as mask events from the owner's browser; the server runs it now.
        if (maskAction === 'canvas_game' || String(event.reason || eventState.reason || '').startsWith('canvas_game:')) return;

        if (eventType === 'layer_sync_inventory') {
          if (modelViewOpen) return;
          const targetClientId = String(event.targetClientId || '').trim();
          if (targetClientId && targetClientId !== liveClientIdRef.current) return;
          const incomingLayers = Array.isArray(event.layers)
            ? event.layers.filter((layer) => layer && layer.hiddenFromOthers !== true)
            : [];
          const incomingHiddenLayerIds = Array.isArray(event.hiddenLayerIds)
            ? event.hiddenLayerIds.map((layerId) => String(layerId || '').trim()).filter(Boolean)
            : Array.isArray(eventState.hiddenLayerIds)
              ? eventState.hiddenLayerIds.map((layerId) => String(layerId || '').trim()).filter(Boolean)
              : [];
          const incomingById = new Map(incomingLayers.map((layer) => [String(layer.id || ''), layer]));
          const declaredOwnerLayerIds = new Set([
            ...Array.from(incomingById.keys()).filter(Boolean),
            ...incomingHiddenLayerIds,
          ]);
          const inventoryComplete = Boolean(
            event.inventoryComplete === true
            || event.fullyLoaded === true
            || eventState.inventoryComplete === true
            || eventState.fullyLoaded === true
          );

          if (inventoryComplete && senderClientId !== liveClientIdRef.current) {
            const senderOwnerName = String(event.ownerName || eventState.ownerName || senderName || '').trim();
            const senderOwnerUserId = Number(event.ownerUserId || event.senderUserId || event.userId || eventState.ownerUserId || senderUserId || 0);
            const senderOwnerClientId = String(event.ownerClientId || eventState.ownerClientId || senderClientId || '').trim();
            const currentLayers = Array.isArray(layersRef.current) ? layersRef.current : [];
            const removedLayers = currentLayers.filter((layer) => {
              const layerId = String(layer && layer.id ? layer.id : '').trim();
              if (!layerId || declaredOwnerLayerIds.has(layerId)) return false;

              // A second tab/session can use the same account but has a different
              // live client ID. Its inventory must never delete layers created by
              // this client just because both sessions share ownerUserId/name.
              if (isLayerOwnedByCurrentUser(
                layer,
                liveClientIdRef.current,
                layerOwnerName,
                currentUserId,
              )) return false;

              const layerOwnerClientId = String(layer && layer.ownerClientId ? layer.ownerClientId : '').trim();
              if (senderOwnerClientId && layerOwnerClientId) {
                return layerOwnerClientId === senderOwnerClientId;
              }

              return layerMatchesOwnerIdentity(
                layer,
                senderOwnerUserId,
                senderOwnerClientId,
                senderOwnerName,
              );
            });

            if (removedLayers.length) {
              const removedIds = new Set(removedLayers.map((layer) => String(layer.id || '').trim()).filter(Boolean));
              const nextLayers = currentLayers.filter((layer) => !removedIds.has(String(layer && layer.id ? layer.id : '').trim()));
              const currentActiveLayerId = String(activeLayerId || '').trim();
              const nextActiveLayerId = nextLayers.some((layer) => String(layer.id || '') === currentActiveLayerId)
                ? currentActiveLayerId
                : String(nextLayers[nextLayers.length - 1]?.id || '');

              for (const removedLayer of removedLayers) {
                const removedLayerId = String(removedLayer.id || '').trim();
                if (!removedLayerId) continue;
                deletedLayerIdsRef.current.add(getLayerDeletionKey(
                  removedLayerId,
                  removedLayer.ownerUserId,
                  removedLayer.ownerClientId,
                  removedLayer.ownerName,
                ));
                purgeReplayLayerEvents(removedLayerId);
                if (stageRef.current && typeof stageRef.current.clearLayer === 'function') {
                  try { stageRef.current.clearLayer(removedLayerId); } catch {}
                }
              }

              if (stageRef.current && typeof stageRef.current.setLayers === 'function') {
                stageRef.current.setLayers(nextLayers, nextActiveLayerId);
              }
              layersRef.current = nextLayers;
              setLayers(nextLayers);
              setActiveLayerId(nextActiveLayerId);
              setHasPixels(Boolean(stageRef.current && typeof stageRef.current.hasPixels === 'function' && stageRef.current.hasPixels()));
            }
          }

          for (const localLayer of layersRef.current) {
            if (!isLayerOwnedByCurrentUser(
              localLayer,
              liveClientIdRef.current,
              layerOwnerName,
              currentUserId,
            )) continue;
            if (localLayer.hiddenFromOthers === true) continue;
            const layerId = String(localLayer && localLayer.id ? localLayer.id : '').trim();
            if (!layerId) continue;
            const incomingLayer = incomingById.get(layerId);
            if (!incomingLayer || compareLayerVersions(localLayer, incomingLayer) > 0) {
              sendAuthoritativeLayer(localLayer, senderClientId);
            }
          }
          for (const incomingLayer of incomingLayers) {
            const layerId = String(incomingLayer && incomingLayer.id ? incomingLayer.id : '').trim();
            if (!layerId) continue;
            const localLayer = layersRef.current.find((layer) => String(layer && layer.id ? layer.id : '') === layerId);
            if (!localLayer || compareLayerVersions(incomingLayer, localLayer) > 0) {
              postCanvasLiveStrokeEvent(safeCanvasId, {
                type: 'layer_sync_request',
                targetClientId: senderClientId,
                requesterClientId: liveClientIdRef.current,
                layerId,
                knownRevision: getLayerRevision(localLayer),
                knownUpdatedAt: getLayerUpdatedAt(localLayer),
                clientId: liveClientIdRef.current,
                ownerClientId: liveClientIdRef.current,
                ownerUserId: currentUserId || undefined,
                ownerName: layerOwnerName,
                seq: Date.now(),
              });
            }
          }
          // Answer an inventory with ours, but never answer an answer, and each sender at most every 2 s:
          // replying to every inventory made each client echo the others forever (and grow with 3+).
          const isInventoryReply = event.inventoryReply === true || eventState.inventoryReply === true;
          const lastReplyAt = inventoryRepliesRef.current.get(senderClientId) || 0;
          if (!isInventoryReply && Date.now() - lastReplyAt >= 2000) {
            inventoryRepliesRef.current.set(senderClientId, Date.now());
            sendLayerState(layersRef.current, { reply: true });
          }
          return;
        }

        if (eventType === 'layer_sync_request') {
          if (modelViewOpen) return;
          if (String(event.targetClientId || '') !== liveClientIdRef.current) return;
          const requestedLayerId = String(event.layerId || '').trim();
          const localLayer = layersRef.current.find((layer) => String(layer && layer.id ? layer.id : '') === requestedLayerId);
          if (!localLayer || localLayer.hiddenFromOthers === true) return;
          if (!isLayerOwnedByCurrentUser(
            localLayer,
            liveClientIdRef.current,
            layerOwnerName,
            currentUserId,
          )) return;
          const requesterVersion = { revision: event.knownRevision, updatedAt: event.knownUpdatedAt };
          if (compareLayerVersions(localLayer, requesterVersion) > 0) {
            sendAuthoritativeLayer(localLayer, String(event.requesterClientId || senderClientId));
          }
          return;
        }

        if (eventType === 'layer_sync_snapshot') {
          if (modelViewOpen) return;
          const targetClientId = String(event.targetClientId || '').trim();
          if (targetClientId && targetClientId !== liveClientIdRef.current) return;
          const incomingLayer = event.layer && typeof event.layer === 'object'
            ? event.layer
            : {
                id: event.layerId,
                name: event.layerName,
                ownerName: getEventOwnerName(event),
                ownerClientId: event.ownerClientId || senderClientId,
                ownerUserId: event.ownerUserId,
                revision: event.layerRevision || event.revision,
                updatedAt: event.layerUpdatedAt || event.updatedAt,
              };
          const layerId = String(incomingLayer.id || event.layerId || '').trim();
          if (!layerId || !event.patch || incomingLayer.hiddenFromOthers === true) return;
          const incomingPrivacyRevision = getLayerRevision(incomingLayer);
          const knownPrivacyRevision = Number(latestLayerPrivacyRevisionRef.current.get(layerId) || 0);
          if (privateRemoteLayerIdsRef.current.has(layerId) && incomingPrivacyRevision <= knownPrivacyRevision) return;
          if (incomingPrivacyRevision > knownPrivacyRevision) {
            latestLayerPrivacyRevisionRef.current.set(layerId, incomingPrivacyRevision);
          }
          privateRemoteLayerIdsRef.current.delete(layerId);
          const localLayer = layersRef.current.find((layer) => String(layer && layer.id ? layer.id : '') === layerId);
          if (localLayer && compareLayerVersions(incomingLayer, localLayer) <= 0) return;
          const authoritativeLayer = withLayerVersion({
            ...localLayer,
            ...incomingLayer,
            id: layerId,
            ownerName: String(incomingLayer.ownerName || getEventOwnerName(event) || 'User'),
            ownerClientId: String(incomingLayer.ownerClientId || event.ownerClientId || senderClientId || ''),
            ownerUserId: Number(incomingLayer.ownerUserId || event.ownerUserId || 0) || undefined,
            visible: incomingLayer.visible !== false,
            opacity: Number.isFinite(Number(incomingLayer.opacity)) ? Number(incomingLayer.opacity) : 1,
          }, getLayerRevision(incomingLayer), getLayerUpdatedAt(incomingLayer));
          const byId = new Map(layersRef.current.map((layer) => [String(layer.id || ''), layer]));
          byId.set(layerId, authoritativeLayer);
          const nextLayers = Array.from(byId.values());
          layersRef.current = nextLayers;
          setLayers(nextLayers);
          if (stageRef.current && typeof stageRef.current.setLayers === 'function') {
            stageRef.current.setLayers(nextLayers, activeLayerId);
          }
          if (stageRef.current && typeof stageRef.current.applyRemoteLiveEvent === 'function') {
            stageRef.current.applyRemoteLiveEvent({
              ...event,
              type: 'stroke_end',
              strokeId: String(event.strokeId || `layer-sync-${layerId}-${Date.now()}`),
              layerId,
              layerName: authoritativeLayer.name,
              ownerName: authoritativeLayer.ownerName,
              ownerClientId: authoritativeLayer.ownerClientId,
              ownerUserId: authoritativeLayer.ownerUserId,
              patch: event.patch,
              state: { patch: event.patch, layerId },
            });
          }
          return;
        }

        if (eventType === 'mask_info' && (maskAction === 'layer_delete' || maskAction === 'layer_create' || maskAction === 'layer_privacy')) {
          const targetLayerId = String(
            (eventMaskInfo && eventMaskInfo.layerId)
            || event.deletedLayerId
            || event.targetLayerId
            || event.layerId
            || ''
          ).trim();
          if (!targetLayerId) return;

          if (maskAction === 'layer_privacy') {
            const privacyRevision = Math.max(
              0,
              Number(
                event.layerRevision
                || event.revision
                || eventState.layerRevision
                || eventState.revision
                || event.seq
                || 0
              ) || 0
            );
            const knownPrivacyRevision = Number(latestLayerPrivacyRevisionRef.current.get(targetLayerId) || 0);
            if (privacyRevision > 0 && privacyRevision < knownPrivacyRevision) return;
            if (privacyRevision > 0) {
              latestLayerPrivacyRevisionRef.current.set(
                targetLayerId,
                Math.max(knownPrivacyRevision, privacyRevision),
              );
            }

            const hiddenFromOthers = Boolean(
              event.hiddenFromOthers
              || eventState.hiddenFromOthers
              || (eventMaskInfo && eventMaskInfo.hiddenFromOthers)
            );

            if (hiddenFromOthers) {
              privateRemoteLayerIdsRef.current.add(targetLayerId);
              purgeReplayLayerEvents(targetLayerId);
              setLayers((current) => {
                const nextLayers = current.filter((layer) => String(layer && layer.id ? layer.id : '').trim() !== targetLayerId);
                const localLayer = nextLayers.find((layer) => isLayerOwnedByCurrentUser(
                  layer,
                  liveClientIdRef.current,
                  layerOwnerName,
                  currentUserId,
                ));
                const nextActiveLayerId = localLayer?.id || nextLayers[0]?.id || '';
                layersRef.current = nextLayers;
                if (stageRef.current && typeof stageRef.current.setLayers === 'function') {
                  stageRef.current.setLayers(nextLayers, nextActiveLayerId);
                }
                if (nextActiveLayerId) setActiveLayerId(nextActiveLayerId);
                return nextLayers;
              });
              return;
            }

            privateRemoteLayerIdsRef.current.delete(targetLayerId);
            return;
          }

          const structuralOwnerName = String(event.targetOwnerName || event.layerOwnerName || getEventOwnerName(event) || '').trim();
          const structuralOwnerClientId = String(event.targetOwnerClientId || event.layerOwnerClientId || event.ownerClientId || senderClientId || '').trim();
          const structuralOwnerUserId = Number(event.targetOwnerUserId || event.layerOwnerUserId || event.ownerUserId || event.senderUserId || event.userId || 0);
          const structuralOwnerKey = getOwnerIdentityKey(
            structuralOwnerUserId,
            structuralOwnerClientId,
            structuralOwnerName,
          );
          const structuralRevision = Number(event.layerRevision || event.ownerLayerRevision || event.revision || event.seq || 0);
          const knownStructuralRevision = Number(latestLayerRevisionByOwnerRef.current.get(structuralOwnerKey) || 0);
          if (structuralOwnerKey && structuralRevision > 0 && structuralRevision < knownStructuralRevision) return;
          if (structuralOwnerKey && structuralRevision > 0) {
            latestLayerRevisionByOwnerRef.current.set(
              structuralOwnerKey,
              Math.max(knownStructuralRevision, structuralRevision),
            );
          }

          if (maskAction === 'layer_delete') {
            const targetOwnerName = String(event.targetOwnerName || event.layerOwnerName || getEventOwnerName(event) || '').trim();
            const targetOwnerClientId = String(event.targetOwnerClientId || event.layerOwnerClientId || event.ownerClientId || senderClientId || '').trim();
            const targetOwnerUserId = Number(event.targetOwnerUserId || event.layerOwnerUserId || event.ownerUserId || event.senderUserId || event.userId || 0);
            deletedLayerIdsRef.current.add(getLayerDeletionKey(
              targetLayerId,
              targetOwnerUserId,
              targetOwnerClientId,
              targetOwnerName,
            ));
            purgeReplayLayerEvents(targetLayerId);
            setLayers((current) => {
              const nextLayers = current.filter((layer) => {
                const sameLayerId = String(layer && layer.id ? layer.id : '').trim() === targetLayerId;
                if (!sameLayerId) return true;
                return !layerMatchesOwnerIdentity(
                  layer,
                  targetOwnerUserId,
                  targetOwnerClientId,
                  targetOwnerName,
                );
              });
              const localLayer = nextLayers.find((layer) => isLayerOwnedByCurrentUser(
                layer,
                liveClientIdRef.current,
                layerOwnerName,
                currentUserId,
              ));
              const nextActiveLayerId = localLayer?.id || nextLayers[0]?.id || '';
              if (stageRef.current && typeof stageRef.current.setLayers === 'function') {
                stageRef.current.setLayers(nextLayers, nextActiveLayerId);
              }
              if (nextActiveLayerId) setActiveLayerId(nextActiveLayerId);
              return nextLayers;
            });
            return;
          }

          const remoteOwnerName = getEventOwnerName(event) || String(event.layerOwnerName || 'User').trim() || 'User';
          const remoteOwnerClientId = String(event.layerOwnerClientId || event.ownerClientId || senderClientId || '').trim();
          const remoteOwnerUserId = Number(event.layerOwnerUserId || event.ownerUserId || event.senderUserId || event.userId || 0);
          deletedLayerIdsRef.current.delete(getLayerDeletionKey(
            targetLayerId,
            remoteOwnerUserId,
            remoteOwnerClientId,
            remoteOwnerName,
          ));
          privateRemoteLayerIdsRef.current.delete(targetLayerId);
          restoreReplayLayerEvents(targetLayerId);
          const createdLayer = {
            id: targetLayerId,
            name: String(event.layerName || event.name || (eventMaskInfo && eventMaskInfo.maskId) || 'Layer 1').trim() || 'Layer 1',
            ownerName: remoteOwnerName,
            ownerClientId: remoteOwnerClientId,
            ownerUserId: Number.isInteger(remoteOwnerUserId) && remoteOwnerUserId > 0 ? remoteOwnerUserId : undefined,
            visible: event.visible !== false,
            opacity: Number.isFinite(Number(event.opacity)) ? Number(event.opacity) : 1,
          };
          setLayers((current) => {
            const byId = new Map(current.map((layer) => [String(layer && layer.id ? layer.id : ''), layer]));
            byId.set(targetLayerId, createdLayer);
            const nextLayers = Array.from(byId.values());
            const localLayer = nextLayers.find((layer) => isLayerOwnedByCurrentUser(
              layer,
              liveClientIdRef.current,
              layerOwnerName,
              currentUserId,
            ));
            const nextActiveLayerId = localLayer?.id || nextLayers[0]?.id || '';
            if (stageRef.current && typeof stageRef.current.setLayers === 'function') {
              stageRef.current.setLayers(nextLayers, nextActiveLayerId);
            }
            if (nextActiveLayerId) setActiveLayerId(nextActiveLayerId);
            return nextLayers;
          });
          return;
        }

        if (eventType === 'layer_state' && Array.isArray(event.layers)) {
          const remoteOwnerName = getEventOwnerName(event) || 'User';
          const senderUserId = Number(event.ownerUserId || event.senderUserId || event.userId || 0);
          const senderOwnerKey = getOwnerIdentityKey(senderUserId, senderClientId, remoteOwnerName);
          const incomingLayerRevision = Number(event.layerRevision || event.ownerLayerRevision || event.revision || event.seq || 0);
          const knownLayerRevision = Number(latestLayerRevisionByOwnerRef.current.get(senderOwnerKey) || 0);
          if (senderOwnerKey && incomingLayerRevision > 0 && incomingLayerRevision < knownLayerRevision) return;
          if (senderOwnerKey && incomingLayerRevision > 0) {
            latestLayerRevisionByOwnerRef.current.set(
              senderOwnerKey,
              Math.max(knownLayerRevision, incomingLayerRevision),
            );
          }
          const incomingHiddenLayerIds = new Set(
            (Array.isArray(event.hiddenLayerIds) ? event.hiddenLayerIds : [])
              .map((layerId) => String(layerId || '').trim())
              .filter(Boolean)
          );
          incomingHiddenLayerIds.forEach((layerId) => {
            privateRemoteLayerIdsRef.current.add(layerId);
          });
          const remoteLayers = event.layers
            .filter((layer) => layer && layer.hiddenFromOthers !== true)
            .filter((layer) => !deletedLayerIdsRef.current.has(getLayerDeletionKey(
              layer.id,
              layer.ownerUserId || layer.owner_user_id || senderUserId,
              layer.ownerClientId || senderClientId,
              layer.ownerName || remoteOwnerName,
            )))
            .filter((layer) => {
              const layerUserId = Number(layer.ownerUserId || layer.owner_user_id || 0);
              const ownerClientId = String(layer.ownerClientId || '').trim();
              const ownerName = normaliseOwnerValue(layer.ownerName);
              if (Number.isInteger(senderUserId) && senderUserId > 0 && Number.isInteger(layerUserId) && layerUserId > 0) {
                return senderUserId === layerUserId;
              }
              return ownerClientId
                ? ownerClientId === senderClientId
                : !ownerName || ownerName === normaliseOwnerValue(remoteOwnerName);
            })
            .map((layer) => ({
              ...layer,
              ownerName: String(layer.ownerName || remoteOwnerName).trim() || remoteOwnerName,
              ownerClientId: senderClientId,
              ownerUserId: Number.isInteger(senderUserId) && senderUserId > 0
                ? senderUserId
                : layer.ownerUserId,
            }));

          const remoteIds = new Set(remoteLayers.map((layer) => String(layer.id || '')));
          remoteIds.forEach((layerId) => {
            if (!layerId) return;
            privateRemoteLayerIdsRef.current.delete(layerId);
          });
          for (const currentLayer of layersRef.current) {
            if (isLayerOwnedByCurrentUser(currentLayer, liveClientIdRef.current, layerOwnerName, currentUserId)) continue;
            const currentLayerUserId = Number(currentLayer.ownerUserId || currentLayer.owner_user_id || 0);
            const belongsToSender = Number.isInteger(senderUserId) && senderUserId > 0
              ? currentLayerUserId === senderUserId
              : String(currentLayer.ownerClientId || '') === senderClientId;
            const currentLayerId = String(currentLayer.id || '').trim();
            if (belongsToSender && currentLayerId && !remoteIds.has(currentLayerId)) {
              privateRemoteLayerIdsRef.current.add(currentLayerId);
              purgeReplayLayerEvents(currentLayerId);
            }
          }

          setLayers((current) => {
            const preserved = current.filter((layer) => {
              if (isLayerOwnedByCurrentUser(layer, liveClientIdRef.current, layerOwnerName, currentUserId)) return true;
              const layerUserId = Number(layer.ownerUserId || layer.owner_user_id || 0);
              const belongsToSender = Number.isInteger(senderUserId) && senderUserId > 0
                ? layerUserId === senderUserId
                : String(layer.ownerClientId || '') === senderClientId;
              return !belongsToSender || remoteIds.has(String(layer.id || ''));
            });
            const byId = new Map(preserved.map((layer) => [String(layer.id || ''), layer]));
            for (const layer of remoteLayers) byId.set(String(layer.id || ''), layer);
            const nextLayers = Array.from(byId.values());
            const localLayer = nextLayers.find((layer) => isLayerOwnedByCurrentUser(
              layer,
              liveClientIdRef.current,
              layerOwnerName,
              currentUserId,
            ));
            const nextActiveLayerId = localLayer?.id || nextLayers[0]?.id || '';
            layersRef.current = nextLayers;
            if (stageRef.current && typeof stageRef.current.setLayers === 'function') {
              stageRef.current.setLayers(nextLayers, nextActiveLayerId);
            }
            if (nextActiveLayerId) setActiveLayerId(nextActiveLayerId);
            return nextLayers;
          });
          return;
        }
        if (eventType === 'layer_clear_commit') {
          return;
        }

        const remoteLayerId = String(event.layerId || '');
        const remoteOwnerName = getEventOwnerName(event);
        const remoteOwnerClientId = String(event.ownerClientId || senderClientId || '').trim();
        const remoteOwnerUserId = Number(event.ownerUserId || event.senderUserId || event.userId || 0);
        const eventReason = String(event.reason || eventState.reason || '').trim();
        const isHistoryRestoreConfirmation = eventReason === 'undo_restore:confirm' || eventReason === 'redo_restore:confirm';
        const incomingLayerVersion = {
          revision: event.layerRevision || event.revision || eventState.layerRevision || eventState.revision,
          updatedAt: event.layerUpdatedAt || event.updatedAt || eventState.layerUpdatedAt || eventState.updatedAt,
        };
        if (remoteLayerId && privateRemoteLayerIdsRef.current.has(remoteLayerId)) return;
        if (eventType === 'stroke_end' && remoteLayerId) {
          const localLayerVersion = layersRef.current.find((layer) => String(layer && layer.id ? layer.id : '') === remoteLayerId);
          if (localLayerVersion && compareLayerVersions(incomingLayerVersion, localLayerVersion) < 0) return;
        }
        const remoteDeletionKey = getLayerDeletionKey(
          remoteLayerId,
          remoteOwnerUserId,
          remoteOwnerClientId,
          remoteOwnerName,
        );
        if (remoteLayerId && isHistoryRestoreConfirmation) {
          deletedLayerIdsRef.current.delete(remoteDeletionKey);
        } else if (remoteLayerId && deletedLayerIdsRef.current.has(remoteDeletionKey)) {
          return;
        }
        if (remoteLayerId) {
          const ownerName = remoteOwnerName;
          const remoteLayerName = ownerName || String(event.layerName || '').trim() || 'User';
          setLayers((current) => current.some((layer) => layer.id === remoteLayerId)
            ? current.map((layer) => layer.id === remoteLayerId
              ? {
                  ...layer,
                  ownerName: layer.ownerName || ownerName || remoteLayerName,
                  ownerClientId: layer.ownerClientId || remoteOwnerClientId || senderClientId,
                  ownerUserId: layer.ownerUserId || (Number.isInteger(remoteOwnerUserId) && remoteOwnerUserId > 0 ? remoteOwnerUserId : undefined),
                  name: layer.name === 'Remote layer' ? remoteLayerName : layer.name,
                  ...(eventType === 'stroke_end' ? withLayerVersion({}, getLayerRevision(incomingLayerVersion), getLayerUpdatedAt(incomingLayerVersion)) : {}),
                }
              : layer)
            : [...current, {
                id: remoteLayerId,
                name: remoteLayerName,
                ownerName: ownerName || remoteLayerName,
                ownerClientId: remoteOwnerClientId || senderClientId,
                ownerUserId: Number.isInteger(remoteOwnerUserId) && remoteOwnerUserId > 0 ? remoteOwnerUserId : undefined,
                visible: true,
                opacity: 1,
                ...(eventType === 'stroke_end' ? withLayerVersion({}, getLayerRevision(incomingLayerVersion), getLayerUpdatedAt(incomingLayerVersion)) : {}),
              }]);
        }
        const rawIncomingFrameId = String(
          event.frameId
          || event.animationFrameId
          || eventState.frameId
          || eventState.animationFrameId
          || ''
        ).trim();
        const rawIncomingFrameIndex = Number(
          event.frameIndex
          ?? event.animationFrameIndex
          ?? eventState.frameIndex
          ?? eventState.animationFrameIndex
          ?? -1
        );
        const localAnimationFrames = sanitizeAnimationFrames(animationFramesRef.current);
        let incomingFrameIndex = -1;

        if (animationEnabledRef.current && localAnimationFrames.length) {
          if (
            Number.isInteger(rawIncomingFrameIndex)
            && rawIncomingFrameIndex >= 0
            && rawIncomingFrameIndex < localAnimationFrames.length
          ) {
            incomingFrameIndex = rawIncomingFrameIndex;
          } else if (rawIncomingFrameId) {
            incomingFrameIndex = localAnimationFrames.findIndex(
              (frame) => String(frame && frame.id ? frame.id : '') === rawIncomingFrameId
            );
          } else if (localAnimationFrames.length === 1) {
            incomingFrameIndex = 0;
          }
        }

        const incomingFrameId = incomingFrameIndex >= 0
          ? String(localAnimationFrames[incomingFrameIndex]?.id || '')
          : '';
        let localActiveAnimationFrameIndex = Number(animationMountedFrameIndexRef.current);
        const mountedFrameId = String(animationMountedFrameIdRef.current || '');
        if (mountedFrameId) {
          const mountedIndexFromId = localAnimationFrames.findIndex(
            (frame) => String(frame && frame.id ? frame.id : '') === mountedFrameId
          );
          if (mountedIndexFromId >= 0) localActiveAnimationFrameIndex = mountedIndexFromId;
        }
        if (
          !Number.isInteger(localActiveAnimationFrameIndex)
          || localActiveAnimationFrameIndex < 0
          || localActiveAnimationFrameIndex >= localAnimationFrames.length
        ) {
          localActiveAnimationFrameIndex = localAnimationFrames.findIndex(
            (frame) => String(frame && frame.id ? frame.id : '') === String(animationActiveFrameIdRef.current || '')
          );
        }
        if (localActiveAnimationFrameIndex < 0 && localAnimationFrames.length === 1) {
          localActiveAnimationFrameIndex = 0;
        }
        const localActiveAnimationFrameId = localActiveAnimationFrameIndex >= 0
          ? String(localAnimationFrames[localActiveAnimationFrameIndex]?.id || '')
          : String(animationMountedFrameIdRef.current || animationActiveFrameIdRef.current || '').trim();
        const isAnimationDrawingEvent = ['stroke_start', 'stroke_point', 'stroke_end', 'paint_action', 'raster_patch'].includes(eventType);
        if (animationEnabledRef.current && isAnimationDrawingEvent && incomingFrameIndex < 0) {
          return;
        }
        const routedEvent = incomingFrameId
          ? {
              ...event,
              frameId: incomingFrameId,
              animationFrameId: incomingFrameId,
              frameIndex: incomingFrameIndex,
              animationFrameIndex: incomingFrameIndex,
              state: {
                ...(eventState && typeof eventState === 'object' ? eventState : {}),
                frameId: incomingFrameId,
                animationFrameId: incomingFrameId,
                frameIndex: incomingFrameIndex,
                animationFrameIndex: incomingFrameIndex,
              },
            }
          : event;
        const isInactiveRemoteAnimationFrame = Boolean(
          animationEnabledRef.current
          && incomingFrameIndex >= 0
          && localActiveAnimationFrameIndex >= 0
          && incomingFrameIndex !== localActiveAnimationFrameIndex
        );


        const routedEventState = routedEvent.state && typeof routedEvent.state === 'object' ? routedEvent.state : {};
        const eventPatch = routedEvent.patch && typeof routedEvent.patch === 'object'
          ? routedEvent.patch
          : routedEventState.patch && typeof routedEventState.patch === 'object'
            ? routedEventState.patch
            : null;
        const targetLayerId = String(routedEvent.layerId || eventPatch?.layerId || '').trim();

        if (
          animationEnabledRef.current
          && incomingFrameId
          && targetLayerId
          && eventPatch
          && typeof eventPatch.dataUrl === 'string'
        ) {
          const framesAtReceipt = sanitizeAnimationFrames(animationFramesRef.current);
          const targetIndexAtReceipt = framesAtReceipt.findIndex((frame) => frame.id === incomingFrameId);
          if (targetIndexAtReceipt >= 0) {
            const targetFrameAtReceipt = framesAtReceipt[targetIndexAtReceipt];
            const previousBitmap = targetFrameAtReceipt.layerBitmaps && targetFrameAtReceipt.layerBitmaps[targetLayerId];
            mergeAnimationRasterPatch(
              previousBitmap,
              eventPatch,
              documentSize.width,
              documentSize.height,
            ).then((nextBitmap) => {
              if (!nextBitmap) return;
              const latestFrames = sanitizeAnimationFrames(animationFramesRef.current);
              const latestIndex = latestFrames.findIndex((frame) => frame.id === incomingFrameId);
              if (latestIndex < 0) return;
              const latestFrame = latestFrames[latestIndex];
              const nextLayers = latestFrame.layers.some((layer) => String(layer && layer.id || '') === targetLayerId)
                ? latestFrame.layers
                : [...latestFrame.layers, {
                    id: targetLayerId,
                    name: String(routedEvent.layerName || getEventOwnerName(routedEvent) || 'User'),
                    visible: true,
                    opacity: 1,
                    ownerClientId: String(routedEvent.ownerClientId || senderClientId || ''),
                    ownerUserId: Number(routedEvent.ownerUserId || 0) || undefined,
                    ownerName: String(routedEvent.ownerName || getEventOwnerName(routedEvent) || 'User'),
                  }];
              const updatedFrame = sanitizeAnimationFrame({
                ...latestFrame,
                layers: nextLayers,
                layerBitmaps: {
                  ...(latestFrame.layerBitmaps || {}),
                  [targetLayerId]: nextBitmap,
                },
                savedAt: Date.now(),
              }, latestIndex);
              const nextFrames = latestFrames.map((frame, index) => index === latestIndex ? updatedFrame : frame);
              const staleSnapshot = animationStageSnapshotsRef.current.get(incomingFrameId);
              if (staleSnapshot && stageRef.current && typeof stageRef.current.destroySnapshot === 'function') {
                stageRef.current.destroySnapshot(staleSnapshot);
              }
              animationStageSnapshotsRef.current.delete(incomingFrameId);
              animationFramesRef.current = nextFrames;
              setAnimationFrames(nextFrames);
              persistAnimationFramesIndividually(
                canvasId,
                nextFrames,
                String(animationActiveFrameIdRef.current || ''),
                true,
              ).catch(() => {});
              const mountedTargetFrameId = String(animationMountedFrameIdRef.current || '');
              if (mountedTargetFrameId === incomingFrameId) {
                const currentStage = stageRef.current;
                if (currentStage && typeof currentStage.loadLayerDataUrl === 'function') {
                  Promise.resolve(currentStage.loadLayerDataUrl(targetLayerId, nextBitmap)).then(() => {
                    if (stageRef.current && typeof stageRef.current.hasPixels === 'function') {
                      setHasPixels(stageRef.current.hasPixels());
                    }
                  }).catch(() => {});
                }
              }
            }).catch(() => {});
          }
        }

        if (isInactiveRemoteAnimationFrame) {
          appendReplayEvent(routedEvent);
          return;
        }

        const remoteAreaOwnerKey = Number.isInteger(remoteOwnerUserId) && remoteOwnerUserId > 0
          ? 'user-' + remoteOwnerUserId
          : remoteOwnerClientId
            ? 'client-' + remoteOwnerClientId.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 48)
            : senderClientId
              ? 'client-' + senderClientId.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 48)
              : '';
        const remoteCanvasAreaSelection = getCanvasAreaSelectionForOwner(
          areaAssignmentsRef.current,
          documentSize,
          remoteAreaOwnerKey,
          targetLayerId || remoteLayerId || null,
        );
        const clippedRoutedEvent = remoteCanvasAreaSelection
          ? {
              ...routedEvent,
              canvasAreaSelection: remoteCanvasAreaSelection,
              state: {
                ...(routedEventState && typeof routedEventState === 'object' ? routedEventState : {}),
                canvasAreaSelection: remoteCanvasAreaSelection,
              },
            }
          : routedEvent;

        appendReplayEvent(clippedRoutedEvent);
        if (stageRef.current && typeof stageRef.current.applyRemoteLiveEvent === 'function') {
          stageRef.current.applyRemoteLiveEvent(clippedRoutedEvent);
        }
      } catch {
      }
    };
    source.onmessage = handleSourceMessage;
    canvasLiveMessageHandlerRef.current = handleSourceMessage;

    return () => {
      if (canvasLiveMessageHandlerRef.current === handleSourceMessage) {
        canvasLiveMessageHandlerRef.current = null;
      }
      try {
        source.close();
      } catch {
      }
    };
  }, [activeLayerId, appendReplayEvent, canvasId, currentUserId, documentSize.height, documentSize.width, isCanvasOwner, layerOwnerName, modelViewOpen, purgeReplayLayerEvents, restoreReplayLayerEvents, sendAuthoritativeLayer, sendCanvasAnimationModeEvent, sendLayerState]);

  const destroySnapshots = useCallback((snapshots) => {
    if (!stageRef.current) return;
    for (const snapshot of snapshots) {
      stageRef.current.destroySnapshot(snapshot);
    }
  }, []);

  const updateHistoryCounts = useCallback(() => {
    setHistoryCount(Math.max(0, historyRef.current.length - 1));
    setRedoCount(redoHistoryRef.current.length);
  }, []);

  const getProjectionStrokeCount = useCallback(() => {
    const viewport = modelViewportRef.current;
    return viewport && typeof viewport.getProjectionStrokeCount === 'function'
      ? viewport.getProjectionStrokeCount()
      : 0;
  }, []);

  // Physical Paint Tool strokes on the 3D model (kept by the viewport on the GPU).
  const getPhysical3dStrokeCount = useCallback(() => {
    const viewport = modelViewportRef.current;
    return viewport && typeof viewport.getPhysical3dStrokeCount === 'function'
      ? viewport.getPhysical3dStrokeCount()
      : 0;
  }, []);

  const stampProjectionHistory = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') return snapshot;
    snapshot.projectionStrokeCount = getProjectionStrokeCount();
    snapshot.physical3dStrokeCount = getPhysical3dStrokeCount();
    return snapshot;
  }, [getPhysical3dStrokeCount, getProjectionStrokeCount]);

  const restoreProjectionHistory = useCallback((snapshot) => {
    const viewport = modelViewportRef.current;
    if (!viewport || typeof viewport.setProjectionStrokeCount !== 'function') return;
    viewport.setProjectionStrokeCount(Math.max(0, Number(snapshot?.projectionStrokeCount) || 0));
    if (Number.isFinite(Number(snapshot?.physical3dStrokeCount)) && typeof viewport.setPhysical3dStrokeCount === 'function') {
      viewport.setPhysical3dStrokeCount(Math.max(0, Number(snapshot.physical3dStrokeCount)));
    }
  }, []);

  // One Canvas undo step per physical 3D stroke, like a 3D projection stroke
  // (no 2D stage snapshot: only the viewport's physical paint changed).
  const handlePhysicalPaintStrokeCommitted = useCallback((count) => {
    const safeCount = Math.max(0, Number(count) || 0);
    const latest = historyRef.current[historyRef.current.length - 1];
    if (latest && typeof latest === 'object') latest.physical3dStrokeCount = Math.max(0, safeCount - 1);
    historyRef.current.push({
      kind: 'projection-history-only',
      projectionStrokeCount: getProjectionStrokeCount(),
      physical3dStrokeCount: safeCount,
      activeLayerId: latest?.activeLayerId || activeLayerId,
      layerState: Array.isArray(latest?.layerState)
        ? latest.layerState.map((layer) => ({ ...layer }))
        : (Array.isArray(layers) ? layers.map((layer) => ({ ...layer })) : []),
    });
    destroySnapshots(redoHistoryRef.current);
    redoHistoryRef.current = [];
    while (historyRef.current.length > MAX_HISTORY + 1) {
      const removed = historyRef.current.shift();
      if (removed) stageRef.current?.destroySnapshot(removed);
    }
    updateHistoryCounts();
  }, [activeLayerId, destroySnapshots, getProjectionStrokeCount, layers, updateHistoryCounts]);

  const handleProjectionStrokeCommitted = useCallback((count) => {
    const safeCount = Math.max(0, Number(count) || 0);
    const latest = historyRef.current[historyRef.current.length - 1];
    if (latest && typeof latest === 'object') {
      latest.projectionStrokeCount = Math.max(0, safeCount - 1);
    }

    // A 3D projection stroke changes only CanvasModelViewport's projection list.
    // Do not call stage.snapshot() here: that clones every 2D layer texture plus all
    // oil-paint simulation framebuffers and can exhaust GPU memory on pointerup.
    const projectionSnapshot = {
      kind: 'projection-history-only',
      projectionStrokeCount: safeCount,
      physical3dStrokeCount: getPhysical3dStrokeCount(),
      activeLayerId: latest?.activeLayerId || activeLayerId,
      layerState: Array.isArray(latest?.layerState)
        ? latest.layerState.map((layer) => ({ ...layer }))
        : (Array.isArray(layers) ? layers.map((layer) => ({ ...layer })) : []),
    };

    destroySnapshots(redoHistoryRef.current);
    redoHistoryRef.current = [];
    historyRef.current.push(projectionSnapshot);

    while (historyRef.current.length > MAX_HISTORY + 1) {
      const removed = historyRef.current.shift();
      if (removed) stageRef.current?.destroySnapshot(removed);
    }

    updateHistoryCounts();
    setLocalStateRevision((value) => value + 1);
  }, [activeLayerId, destroySnapshots, getPhysical3dStrokeCount, layers, updateHistoryCounts]);

  const resetHistory = useCallback(() => {
    destroySnapshots(historyRef.current);
    destroySnapshots(redoHistoryRef.current);
    historyRef.current = [];
    redoHistoryRef.current = [];
    clearSnapshotRef.current = null;
    clearHistoryResetPendingRef.current = false;
    const initialSnapshot = stageRef.current && stageRef.current.snapshot();
    if (initialSnapshot) historyRef.current.push(stampProjectionHistory(initialSnapshot));
    updateHistoryCounts();
  }, [destroySnapshots, stampProjectionHistory, updateHistoryCounts]);

  const persistCanvasStateNow = useCallback(async () => {
    const stateKey = getCanvasStateKey(persistenceId);
    const stage = stageRef.current;
    if (!stateKey || !stage || !localStateHydratedRef.current) return false;
    if (typeof stage.exportLayerPatch !== 'function') return false;

    const currentLayers = Array.isArray(layersRef.current)
      ? layersRef.current.filter(Boolean).map((layer) => {
          const layerId = String(layer && layer.id ? layer.id : '').trim();
          const authoritativeTextObject = layerId ? textObjectsByLayerRef.current[layerId] : null;
          if (!authoritativeTextObject) return layer;
          return {
            ...layer,
            name: String(authoritativeTextObject.text || '').replace(/\s+/g, ' ').trim() || layer.name || 'Text',
            textObject: authoritativeTextObject,
          };
        })
      : [];
    const layerBitmaps = {};
    const paintLayerStates = {};

    for (const layer of currentLayers) {
      const layerId = String(layer && layer.id ? layer.id : '').trim();
      if (!layerId) continue;
      const patch = stage.exportLayerPatch(layerId);
      if (patch && typeof patch.dataUrl === 'string' && patch.dataUrl.startsWith('data:image/')) {
        layerBitmaps[layerId] = patch.dataUrl;
      }
      if (typeof stage.exportPaintLayerState === 'function') {
        const paintState = stage.exportPaintLayerState(layerId);
        if (paintState && typeof paintState === 'object') paintLayerStates[layerId] = paintState;
      }
    }

    const replayEvents = serialiseReplayEvents(
      filterReplayEventsForLayers(replayEventsRef.current, currentLayers)
    );
    const payload = {
      version: 4,
      savedAt: Date.now(),
      width: documentSize.width,
      height: documentSize.height,
      activeLayerId: String(activeLayerId || ''),
      layers: currentLayers.map((layer) => ({ ...layer })),
      layerBitmaps,
      paintLayerStates,
      paperSurface: typeof stage.getPaperSurface === 'function' ? stage.getPaperSurface() : null,
      playback: {
        type: 'eventLog',
        eventCount: replayEvents.length,
      },
      replayEvents,
      areaAssignments: sanitizeCanvasAreaAssignments(areaAssignments),
    };

    localStateSaveInFlightRef.current = localStateSaveInFlightRef.current
      .catch(() => false)
      .then(() => writeCanvasState(stateKey, payload));

    return localStateSaveInFlightRef.current;
  }, [activeLayerId, areaAssignments, documentSize.height, documentSize.width, persistenceId]);

  const scheduleCanvasStateSave = useCallback(() => {
    if (typeof window === 'undefined' || !localStateHydratedRef.current) return;
    if (localStateSaveTimerRef.current) window.clearTimeout(localStateSaveTimerRef.current);
    localStateSaveTimerRef.current = window.setTimeout(() => {
      localStateSaveTimerRef.current = 0;
      persistCanvasStateNow().catch(() => {});
    }, CANVAS_STATE_SAVE_DELAY_MS);
  }, [persistCanvasStateNow]);

  const markCanvasStateDirty = useCallback(() => {
    setLocalStateRevision((value) => value + 1);
  }, []);

  const restoreCanvasState = useCallback(async () => {
    if (localStateRestoreStartedRef.current) return;
    localStateRestoreStartedRef.current = true;

    const stateKey = getCanvasStateKey(persistenceId);
    const stage = stageRef.current;
    if (!stateKey || !stage) {
      localStateHydratedRef.current = true;
      resetHistory();
      return;
    }

    try {
      const persisted = await readCanvasState(stateKey);
      const persistedLayers = persisted && Array.isArray(persisted.layers)
        ? persisted.layers.filter((layer) => layer && String(layer.id || '').trim())
        : [];

      setAreaAssignments(sanitizeCanvasAreaAssignments(
        persisted && (persisted.areaAssignments || persisted.canvasAreaAssignments)
      ));
      // The paper is restored first: layer paint states recorded their own
      // surface, and a paper chosen on an empty document has no layer state.
      if (persisted && persisted.paperSurface && typeof stage.setPaperSurface === 'function') {
        stage.setPaperSurface(persisted.paperSurface);
      }

      if (!persistedLayers.length) {
        localStateHydratedRef.current = true;
        resetHistory();
        return;
      }

      const persistedActiveLayerId = String(persisted.activeLayerId || '').trim();
      const nextActiveLayerId = persistedLayers.some((layer) => String(layer.id) === persistedActiveLayerId)
        ? persistedActiveLayerId
        : String(persistedLayers[persistedLayers.length - 1].id);

      stage.setLayers(persistedLayers, nextActiveLayerId);

      const layerBitmaps = persisted.layerBitmaps && typeof persisted.layerBitmaps === 'object'
        ? persisted.layerBitmaps
        : {};

      for (const layer of persistedLayers) {
        const layerId = String(layer.id || '').trim();
        const dataUrl = layerBitmaps[layerId];
        if (!layerId || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) continue;
        await stage.loadLayerDataUrl(layerId, dataUrl);
      }

      const paintLayerStates = persisted.paintLayerStates && typeof persisted.paintLayerStates === 'object'
        ? persisted.paintLayerStates
        : {};
      if (typeof stage.loadPaintLayerState === 'function') {
        for (const layer of persistedLayers) {
          const layerId = String(layer.id || '').trim();
          const paintState = paintLayerStates[layerId];
          if (!layerId || !paintState || typeof paintState !== 'object') continue;
          await stage.loadPaintLayerState(layerId, paintState);
        }
      }

      const persistedReplayEvents = Array.isArray(persisted.replayEvents)
        ? persisted.replayEvents
        : Array.isArray(persisted.strokeLog)
          ? persisted.strokeLog
          : Array.isArray(persisted.events)
            ? persisted.events
            : [];
      replayEventsRef.current = filterReplayEventsForLayers(persistedReplayEvents, persistedLayers)
        .map((event, index) => ({
          ...event,
          replaySequence: Number(event.replaySequence) || index + 1,
          loggedAt: Number(event.loggedAt || event.timestamp || event.seq) || Date.now(),
        }))
        .sort((left, right) => Number(left.replaySequence || 0) - Number(right.replaySequence || 0));
      replaySequenceRef.current = replayEventsRef.current.reduce(
        (maximum, event) => Math.max(maximum, Number(event.replaySequence) || 0),
        0
      );
      deletedReplayLayerEventsRef.current.clear();
      setReplayRevision((value) => value + 1);
      const restoredTextObjects = {};
      persistedLayers.forEach((layer) => {
        const layerId = String(layer && layer.id ? layer.id : '').trim();
        if (layerId && layer && layer.textObject) restoredTextObjects[layerId] = layer.textObject;
      });
      textObjectsByLayerRef.current = restoredTextObjects;
      setTextObjectsByLayer(restoredTextObjects);
      layersRef.current = persistedLayers;
      setLayers(persistedLayers);
      setActiveLayerId(nextActiveLayerId);
      setHasPixels(stage.hasPixels());
    } catch {
    } finally {
      localStateHydratedRef.current = true;
      resetHistory();
    }
  }, [persistenceId, resetHistory]);

  const syncLayerStateFromSnapshot = useCallback((snapshot) => {
    if (!stageRef.current || !snapshot) return;
    const snapshotLayers = Array.isArray(snapshot.layerState) && snapshot.layerState.length
      ? snapshot.layerState.map((layer) => ({ ...layer }))
      : stageRef.current.getLayers();
    const snapshotActiveLayerId = String(snapshot.activeLayerId || '').trim();
    const nextActiveLayerId = snapshotActiveLayerId && snapshotLayers.some((layer) => layer.id === snapshotActiveLayerId)
      ? snapshotActiveLayerId
      : snapshotLayers.length
        ? snapshotLayers[snapshotLayers.length - 1].id
        : '';
    setLayers(snapshotLayers);
    if (nextActiveLayerId) setActiveLayerId(nextActiveLayerId);
  }, []);

  const captureSnapshot = useCallback(() => {
    if (!stageRef.current) return;

    if (clearHistoryResetPendingRef.current && clearSnapshotRef.current) {
      const clearSnapshot = clearSnapshotRef.current;
      const snapshotsToDestroy = historyRef.current.filter((snapshot) => snapshot !== clearSnapshot);
      destroySnapshots(snapshotsToDestroy);
      historyRef.current = [clearSnapshot];
      destroySnapshots(redoHistoryRef.current);
      redoHistoryRef.current = [];
      clearHistoryResetPendingRef.current = false;
      clearSnapshotRef.current = null;
    }

    const snapshot = stageRef.current.snapshot();
    if (!snapshot) return;
    stampProjectionHistory(snapshot);

    if (!clearHistoryResetPendingRef.current && clearSnapshotRef.current && redoHistoryRef.current.includes(clearSnapshotRef.current)) {
      clearSnapshotRef.current = null;
    }
    destroySnapshots(redoHistoryRef.current);
    redoHistoryRef.current = [];
    historyRef.current.push(snapshot);

    while (historyRef.current.length > MAX_HISTORY + 1) {
      const removed = historyRef.current.shift();
      if (removed) stageRef.current.destroySnapshot(removed);
    }

    setHasPixels(stageRef.current.hasPixels());
    updateHistoryCounts();
    markCanvasStateDirty();

  }, [destroySnapshots, markCanvasStateDirty, stampProjectionHistory, updateHistoryCounts]);

  captureSnapshotRef.current = captureSnapshot;

  const handleUndo = useCallback(async (options) => {
    // Games may switch undo off; `force` is the game itself taking back a stroke its rules refuse.
    if (gameRestrictionsRef.current && gameRestrictionsRef.current.noUndo && !(options && options.force === true)) return;
    if (!stageRef.current || historyRef.current.length <= 1) return;
    if (gameModeRef.current && typeof gameModeRef.current.onEdit === 'function' && !(options && options.force === true)) gameModeRef.current.onEdit('undo');

    const current = historyRef.current.pop();
    if (current) redoHistoryRef.current.push(current);
    if (current && current === clearSnapshotRef.current) {
      clearHistoryResetPendingRef.current = false;
    }

    const previousEntry = historyRef.current[historyRef.current.length - 1];
    if (!previousEntry) {
      updateHistoryCounts();
      return;
    }

    // Projection-only history entries (3D projection and physical 3D strokes)
    // contain no 2D GPU textures by design. Undoing one only changes the 3D counts.
    // Undoing a 2D step whose predecessor is 3D-only restores the 2D state from
    // the nearest full snapshot below it (the 2D state did not change in between).
    let previous = previousEntry;
    if (current?.kind !== 'projection-history-only' && previousEntry.kind === 'projection-history-only') {
      previous = [...historyRef.current].reverse().find((entry) => entry && entry.kind !== 'projection-history-only') || previousEntry;
    }
    if (current?.kind === 'projection-history-only' || previous.kind === 'projection-history-only') {
      restoreProjectionHistory(previousEntry);
      setHasPixels(stageRef.current.hasPixels());
      updateHistoryCounts();
      markCanvasStateDirty();
      return;
    }

    const previousLayers = Array.isArray(previous.layerState) ? previous.layerState : [];
    const currentLayers = current && Array.isArray(current.layerState) ? current.layerState : [];
    const previousLocalLayers = previousLayers.filter((layer) => isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    const currentLocalLayers = currentLayers.filter((layer) => isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    const previousLocalIds = new Set(
      previousLocalLayers
        .map((layer) => String(layer && layer.id ? layer.id : '').trim())
        .filter(Boolean)
    );
    const removedLocalLayers = currentLocalLayers.filter((layer) => {
      const layerId = String(layer && layer.id ? layer.id : '').trim();
      return layerId && !previousLocalIds.has(layerId);
    });

    const gpuLayers = typeof stageRef.current.getLayers === 'function'
      ? stageRef.current.getLayers()
      : [];
    const reactById = new Map(
      (Array.isArray(layers) ? layers : [])
        .map((layer) => [String(layer && layer.id ? layer.id : '').trim(), layer])
        .filter(([id]) => id)
    );
    const liveLayers = (Array.isArray(gpuLayers) ? gpuLayers : []).map((layer) => {
      const id = String(layer && layer.id ? layer.id : '').trim();
      return { ...layer, ...(reactById.get(id) || {}), id };
    });
    for (const layer of Array.isArray(layers) ? layers : []) {
      const id = String(layer && layer.id ? layer.id : '').trim();
      if (!id || liveLayers.some((entry) => entry.id === id)) continue;
      liveLayers.push({ ...layer, id });
    }

    const preservedRemoteLayers = liveLayers.filter((layer) => !isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    const mergedLayers = [
      ...preservedRemoteLayers,
      ...previousLocalLayers.map((layer) => ({ ...layer })),
    ];
    const restoredActiveLayerId = previous.activeLayerId && previousLocalIds.has(String(previous.activeLayerId))
      ? String(previous.activeLayerId)
      : previousLocalLayers.length
        ? String(previousLocalLayers[previousLocalLayers.length - 1].id)
        : preservedRemoteLayers.length
          ? String(preservedRemoteLayers[preservedRemoteLayers.length - 1].id)
          : '';

    if (typeof stageRef.current.restoreSnapshotLayers === 'function') {
      stageRef.current.restoreSnapshotLayers(
        previous,
        Array.from(previousLocalIds),
        mergedLayers,
        restoredActiveLayerId,
      );
      setLayers(mergedLayers);
      if (restoredActiveLayerId) setActiveLayerId(restoredActiveLayerId);
    } else {
      stageRef.current.restoreSnapshot(previous);
      syncLayerStateFromSnapshot(previous);
    }
    restoreProjectionHistory(previousEntry);

    for (const layer of removedLocalLayers) {
      purgeReplayLayerEvents(layer.id);
    }

    for (const layer of previousLocalLayers) {
      restoreReplayLayerEvents(layer.id);
      deletedLayerIdsRef.current.delete(getLayerDeletionKey(
        layer.id,
        layer.ownerUserId || layer.owner_user_id || currentUserId || 0,
        layer.ownerClientId || liveClientIdRef.current || '',
        layer.ownerName || layerOwnerName || '',
      ));
    }

    const safeCanvasId = Number(canvasId);
    if (Number.isInteger(safeCanvasId) && safeCanvasId > 0) {
      const undoOperationId = `undo-confirm-${currentUserId || 0}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      let sequence = Date.now();

      for (let index = 0; index < removedLocalLayers.length; index += 1) {
        const layer = removedLocalLayers[index];
        const layerId = String(layer && layer.id ? layer.id : '').trim();
        if (!layerId || layer.hiddenFromOthers === true) continue;
        const ownerName = String(layer.ownerName || layerOwnerName || 'User').trim() || 'User';
        const ownerClientId = String(layer.ownerClientId || liveClientIdRef.current || '').trim();
        const ownerUserId = Number(layer.ownerUserId || layer.owner_user_id || currentUserId || 0);

        await postCanvasLiveStrokeEvent(safeCanvasId, {
          type: 'mask_info',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: sequence += 1,
          strokeId: `${undoOperationId}-delete-${index}`,
          layerId,
          targetLayerId: layerId,
          targetOwnerName: ownerName,
          targetOwnerClientId: ownerClientId,
          targetOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          tool: 'mask',
          brushPreset: 'mask',
          maskAction: 'layer_delete',
          reason: 'undo_restore:delete',
          allowEmpty: true,
          maskInfo: {
            type: 'canvas_layer_event',
            version: 2,
            layerId,
            maskId: String(layer.name || layerId).slice(0, 128),
            shape: 'rect',
            action: 'layer_delete',
            hiddenFromOthers: false,
            inverted: false,
            bounds: { x: 0, y: 0, width: 0, height: 0 },
            transform: { translateX: 0, translateY: 0, rotation: 0, scaleX: 1, scaleY: 1, originX: 0, originY: 0 },
            vertices: [],
            maskVertices: [],
            maskVertexCount: 0,
          },
          state: {
            maskAction: 'layer_delete',
            reason: 'undo_restore:delete',
            allowEmpty: true,
            undoOperationId,
            targetLayerId: layerId,
            targetOwnerName: ownerName,
            targetOwnerClientId: ownerClientId,
            targetOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          },
          timestamp: Date.now(),
        });
      }

      for (let index = 0; index < previousLocalLayers.length; index += 1) {
        const layer = previousLocalLayers[index];
        const layerId = String(layer && layer.id ? layer.id : '').trim();
        if (!layerId || layer.hiddenFromOthers === true) continue;
        const ownerName = String(layer.ownerName || layerOwnerName || 'User').trim() || 'User';
        const ownerClientId = String(layer.ownerClientId || liveClientIdRef.current || '').trim();
        const ownerUserId = Number(layer.ownerUserId || layer.owner_user_id || currentUserId || 0);

        await postCanvasLiveStrokeEvent(safeCanvasId, {
          type: 'mask_info',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: sequence += 1,
          strokeId: `${undoOperationId}-create-${index}`,
          layerId,
          targetLayerId: layerId,
          layerName: String(layer.name || 'Layer 1'),
          targetOwnerName: ownerName,
          targetOwnerClientId: ownerClientId,
          targetOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          layerOwnerName: ownerName,
          layerOwnerClientId: ownerClientId,
          layerOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          visible: layer.visible !== false,
          opacity: Number.isFinite(Number(layer.opacity)) ? Number(layer.opacity) : 1,
          tool: 'mask',
          brushPreset: 'mask',
          maskAction: 'layer_create',
          reason: 'undo_restore:create',
          allowEmpty: true,
          maskInfo: {
            type: 'canvas_layer_event',
            version: 2,
            layerId,
            maskId: String(layer.name || layerId).slice(0, 128),
            shape: 'rect',
            action: 'layer_create',
            hiddenFromOthers: layer.hiddenFromOthers === true,
            inverted: false,
            bounds: { x: 0, y: 0, width: 0, height: 0 },
            transform: { translateX: 0, translateY: 0, rotation: 0, scaleX: 1, scaleY: 1, originX: 0, originY: 0 },
            vertices: [],
            maskVertices: [],
            maskVertexCount: 0,
          },
          state: {
            maskAction: 'layer_create',
            reason: 'undo_restore:create',
            allowEmpty: true,
            undoOperationId,
            targetLayerId: layerId,
            targetOwnerName: ownerName,
            targetOwnerClientId: ownerClientId,
            targetOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          },
          timestamp: Date.now(),
        });
      }

      if (previousLocalLayers.length) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 80);
        });
      }

      for (let index = 0; index < previousLocalLayers.length; index += 1) {
        const layer = previousLocalLayers[index];
        const layerId = String(layer && layer.id ? layer.id : '').trim();
        if (!layerId || typeof stageRef.current.exportLayerPatch !== 'function') continue;
        const patch = stageRef.current.exportLayerPatch(layerId);
        if (!patch) continue;

        appendReplayEvent({
          type: 'stroke_end',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: sequence + 1,
          strokeId: `${undoOperationId}-confirm-${index}`,
          layerId,
          layerName: String(layer.name || layerOwnerName || 'Layer 1'),
          patch,
          rasterPatchEvent: true,
          rasterPatchType: 'raster_patch',
          reason: 'undo_restore:confirm',
          state: {
            rasterPatchEvent: true,
            rasterPatchType: 'raster_patch',
            patch,
            reason: 'undo_restore:confirm',
            undoOperationId,
            layerId,
          },
          timestamp: Date.now(),
        });

        if (layer.hiddenFromOthers === true) continue;

        await postCanvasLiveStrokeEvent(safeCanvasId, {
          type: 'stroke_end',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: sequence += 1,
          strokeId: `${undoOperationId}-confirm-${index}`,
          layerId,
          layerName: String(layer.name || layerOwnerName || 'Layer 1'),
          patch,
          rasterPatchEvent: true,
          rasterPatchType: 'raster_patch',
          reason: 'undo_restore:confirm',
          state: {
            rasterPatchEvent: true,
            rasterPatchType: 'raster_patch',
            patch,
            reason: 'undo_restore:confirm',
            undoOperationId,
            layerId,
          },
          timestamp: Date.now(),
        });
      }
    }

    setHasPixels(stageRef.current.hasPixels());
    updateHistoryCounts();
    markCanvasStateDirty();
  }, [appendReplayEvent, canvasId, currentUserId, layerOwnerName, layers, markCanvasStateDirty, purgeReplayLayerEvents, restoreProjectionHistory, restoreReplayLayerEvents, syncLayerStateFromSnapshot, updateHistoryCounts]);

  const handleRedo = useCallback(async () => {
    if (gameRestrictionsRef.current && gameRestrictionsRef.current.noUndo) return;
    if (!stageRef.current || !redoHistoryRef.current.length) return;
    if (gameModeRef.current && typeof gameModeRef.current.onEdit === 'function') gameModeRef.current.onEdit('redo');

    const current = historyRef.current[historyRef.current.length - 1] || null;
    const snapshot = redoHistoryRef.current.pop();
    if (!snapshot) return;
    historyRef.current.push(snapshot);

    // Redoing a 3D projection marker must not restore or clone the unchanged 2D canvas.
    if (snapshot.kind === 'projection-history-only') {
      restoreProjectionHistory(snapshot);
      setHasPixels(stageRef.current.hasPixels());
      updateHistoryCounts();
      markCanvasStateDirty();
      return;
    }

    const targetLayers = Array.isArray(snapshot.layerState) ? snapshot.layerState : [];
    const currentLayers = current && Array.isArray(current.layerState) ? current.layerState : [];
    const targetLocalLayers = targetLayers.filter((layer) => isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    const currentLocalLayers = currentLayers.filter((layer) => isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    const targetLocalIds = new Set(
      targetLocalLayers
        .map((layer) => String(layer && layer.id ? layer.id : '').trim())
        .filter(Boolean)
    );
    const removedLocalLayers = currentLocalLayers.filter((layer) => {
      const layerId = String(layer && layer.id ? layer.id : '').trim();
      return layerId && !targetLocalIds.has(layerId);
    });

    const gpuLayers = typeof stageRef.current.getLayers === 'function'
      ? stageRef.current.getLayers()
      : [];
    const reactById = new Map(
      (Array.isArray(layers) ? layers : [])
        .map((layer) => [String(layer && layer.id ? layer.id : '').trim(), layer])
        .filter(([id]) => id)
    );
    const liveLayers = (Array.isArray(gpuLayers) ? gpuLayers : []).map((layer) => {
      const id = String(layer && layer.id ? layer.id : '').trim();
      return { ...layer, ...(reactById.get(id) || {}), id };
    });
    for (const layer of Array.isArray(layers) ? layers : []) {
      const id = String(layer && layer.id ? layer.id : '').trim();
      if (!id || liveLayers.some((entry) => entry.id === id)) continue;
      liveLayers.push({ ...layer, id });
    }

    const preservedRemoteLayers = liveLayers.filter((layer) => !isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    const mergedLayers = [
      ...preservedRemoteLayers,
      ...targetLocalLayers.map((layer) => ({ ...layer })),
    ];
    const restoredActiveLayerId = snapshot.activeLayerId && targetLocalIds.has(String(snapshot.activeLayerId))
      ? String(snapshot.activeLayerId)
      : targetLocalLayers.length
        ? String(targetLocalLayers[targetLocalLayers.length - 1].id)
        : preservedRemoteLayers.length
          ? String(preservedRemoteLayers[preservedRemoteLayers.length - 1].id)
          : '';

    if (typeof stageRef.current.restoreSnapshotLayers === 'function') {
      stageRef.current.restoreSnapshotLayers(
        snapshot,
        Array.from(targetLocalIds),
        mergedLayers,
        restoredActiveLayerId,
      );
      setLayers(mergedLayers);
      if (restoredActiveLayerId) setActiveLayerId(restoredActiveLayerId);
    } else {
      stageRef.current.restoreSnapshot(snapshot);
      syncLayerStateFromSnapshot(snapshot);
    }
    restoreProjectionHistory(snapshot);

    for (const layer of removedLocalLayers) {
      purgeReplayLayerEvents(layer.id);
    }

    for (const layer of targetLocalLayers) {
      restoreReplayLayerEvents(layer.id);
      deletedLayerIdsRef.current.delete(getLayerDeletionKey(
        layer.id,
        layer.ownerUserId || layer.owner_user_id || currentUserId || 0,
        layer.ownerClientId || liveClientIdRef.current || '',
        layer.ownerName || layerOwnerName || '',
      ));
    }

    const safeCanvasId = Number(canvasId);
    if (Number.isInteger(safeCanvasId) && safeCanvasId > 0) {
      const redoOperationId = `redo-confirm-${currentUserId || 0}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      let sequence = Date.now();

      for (let index = 0; index < removedLocalLayers.length; index += 1) {
        const layer = removedLocalLayers[index];
        const layerId = String(layer && layer.id ? layer.id : '').trim();
        if (!layerId || layer.hiddenFromOthers === true) continue;
        const ownerName = String(layer.ownerName || layerOwnerName || 'User').trim() || 'User';
        const ownerClientId = String(layer.ownerClientId || liveClientIdRef.current || '').trim();
        const ownerUserId = Number(layer.ownerUserId || layer.owner_user_id || currentUserId || 0);

        await postCanvasLiveStrokeEvent(safeCanvasId, {
          type: 'mask_info',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: sequence += 1,
          strokeId: `${redoOperationId}-delete-${index}`,
          layerId,
          targetLayerId: layerId,
          targetOwnerName: ownerName,
          targetOwnerClientId: ownerClientId,
          targetOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          tool: 'mask',
          brushPreset: 'mask',
          maskAction: 'layer_delete',
          reason: 'redo_restore:delete',
          allowEmpty: true,
          maskInfo: {
            type: 'canvas_layer_event',
            version: 2,
            layerId,
            maskId: String(layer.name || layerId).slice(0, 128),
            shape: 'rect',
            action: 'layer_delete',
            hiddenFromOthers: false,
            inverted: false,
            bounds: { x: 0, y: 0, width: 0, height: 0 },
            transform: { translateX: 0, translateY: 0, rotation: 0, scaleX: 1, scaleY: 1, originX: 0, originY: 0 },
            vertices: [],
            maskVertices: [],
            maskVertexCount: 0,
          },
          state: {
            maskAction: 'layer_delete',
            reason: 'redo_restore:delete',
            allowEmpty: true,
            redoOperationId,
            targetLayerId: layerId,
            targetOwnerName: ownerName,
            targetOwnerClientId: ownerClientId,
            targetOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          },
          timestamp: Date.now(),
        });
      }

      for (let index = 0; index < targetLocalLayers.length; index += 1) {
        const layer = targetLocalLayers[index];
        const layerId = String(layer && layer.id ? layer.id : '').trim();
        if (!layerId || layer.hiddenFromOthers === true) continue;
        const ownerName = String(layer.ownerName || layerOwnerName || 'User').trim() || 'User';
        const ownerClientId = String(layer.ownerClientId || liveClientIdRef.current || '').trim();
        const ownerUserId = Number(layer.ownerUserId || layer.owner_user_id || currentUserId || 0);

        await postCanvasLiveStrokeEvent(safeCanvasId, {
          type: 'mask_info',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: sequence += 1,
          strokeId: `${redoOperationId}-create-${index}`,
          layerId,
          targetLayerId: layerId,
          layerName: String(layer.name || 'Layer 1'),
          targetOwnerName: ownerName,
          targetOwnerClientId: ownerClientId,
          targetOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          layerOwnerName: ownerName,
          layerOwnerClientId: ownerClientId,
          layerOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          visible: layer.visible !== false,
          opacity: Number.isFinite(Number(layer.opacity)) ? Number(layer.opacity) : 1,
          tool: 'mask',
          brushPreset: 'mask',
          maskAction: 'layer_create',
          reason: 'redo_restore:create',
          allowEmpty: true,
          maskInfo: {
            type: 'canvas_layer_event',
            version: 2,
            layerId,
            maskId: String(layer.name || layerId).slice(0, 128),
            shape: 'rect',
            action: 'layer_create',
            hiddenFromOthers: layer.hiddenFromOthers === true,
            inverted: false,
            bounds: { x: 0, y: 0, width: 0, height: 0 },
            transform: { translateX: 0, translateY: 0, rotation: 0, scaleX: 1, scaleY: 1, originX: 0, originY: 0 },
            vertices: [],
            maskVertices: [],
            maskVertexCount: 0,
          },
          state: {
            maskAction: 'layer_create',
            reason: 'redo_restore:create',
            allowEmpty: true,
            redoOperationId,
            targetLayerId: layerId,
            targetOwnerName: ownerName,
            targetOwnerClientId: ownerClientId,
            targetOwnerUserId: Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0,
          },
          timestamp: Date.now(),
        });
      }

      if (targetLocalLayers.length) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 80);
        });
      }

      for (let index = 0; index < targetLocalLayers.length; index += 1) {
        const layer = targetLocalLayers[index];
        const layerId = String(layer && layer.id ? layer.id : '').trim();
        if (!layerId || typeof stageRef.current.exportLayerPatch !== 'function') continue;
        const patch = stageRef.current.exportLayerPatch(layerId);
        if (!patch) continue;

        appendReplayEvent({
          type: 'stroke_end',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: sequence + 1,
          strokeId: `${redoOperationId}-confirm-${index}`,
          layerId,
          layerName: String(layer.name || layerOwnerName || 'Layer 1'),
          patch,
          rasterPatchEvent: true,
          rasterPatchType: 'raster_patch',
          reason: 'redo_restore:confirm',
          state: {
            rasterPatchEvent: true,
            rasterPatchType: 'raster_patch',
            patch,
            reason: 'redo_restore:confirm',
            redoOperationId,
            layerId,
          },
          timestamp: Date.now(),
        });

        if (layer.hiddenFromOthers === true) continue;

        await postCanvasLiveStrokeEvent(safeCanvasId, {
          type: 'stroke_end',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: sequence += 1,
          strokeId: `${redoOperationId}-confirm-${index}`,
          layerId,
          layerName: String(layer.name || layerOwnerName || 'Layer 1'),
          patch,
          rasterPatchEvent: true,
          rasterPatchType: 'raster_patch',
          reason: 'redo_restore:confirm',
          state: {
            rasterPatchEvent: true,
            rasterPatchType: 'raster_patch',
            patch,
            reason: 'redo_restore:confirm',
            redoOperationId,
            layerId,
          },
          timestamp: Date.now(),
        });
      }
    }

    if (snapshot === clearSnapshotRef.current) {
      clearHistoryResetPendingRef.current = true;
    }
    setHasPixels(stageRef.current.hasPixels());
    updateHistoryCounts();
    markCanvasStateDirty();
  }, [appendReplayEvent, canvasId, currentUserId, layerOwnerName, layers, markCanvasStateDirty, purgeReplayLayerEvents, restoreProjectionHistory, restoreReplayLayerEvents, syncLayerStateFromSnapshot, updateHistoryCounts]);

  const chooseOwnerClearScope = useCallback(() => {
    if (!isCanvasOwner || typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.setAttribute('role', 'presentation');
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.zIndex = '2147483647';
      overlay.style.display = 'flex';
      overlay.style.alignItems = 'center';
      overlay.style.justifyContent = 'center';
      overlay.style.background = 'rgba(0, 0, 0, 0.42)';
      overlay.style.padding = '24px';

      const panel = document.createElement('div');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-label', 'Clear layers');
      panel.style.width = 'min(360px, 100%)';
      panel.style.borderRadius = '16px';
      panel.style.background = '#111827';
      panel.style.color = '#f9fafb';
      panel.style.boxShadow = '0 24px 80px rgba(0, 0, 0, 0.4)';
      panel.style.padding = '18px';
      panel.style.fontFamily = 'inherit';

      const title = document.createElement('div');
      title.textContent = 'Clear which layers?';
      title.style.fontSize = '16px';
      title.style.fontWeight = '700';
      title.style.marginBottom = '8px';

      const body = document.createElement('div');
      body.textContent = 'Choose whether to clear the whole deck or only your own layers.';
      body.style.fontSize = '13px';
      body.style.lineHeight = '1.45';
      body.style.color = '#d1d5db';
      body.style.marginBottom = '16px';

      const actions = document.createElement('div');
      actions.style.display = 'grid';
      actions.style.gridTemplateColumns = '1fr 1fr';
      actions.style.gap = '10px';

      let finished = false;
      const finish = (value) => {
        if (finished) return;
        finished = true;
        document.removeEventListener('keydown', handleKeyDown);
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        resolve(value);
      };

      const handleKeyDown = (event) => {
        if (event.key === 'Escape') finish(false);
      };

      const makeButton = (label, primary, value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.style.border = '1px solid rgba(255, 255, 255, 0.16)';
        button.style.borderRadius = '12px';
        button.style.padding = '10px 12px';
        button.style.cursor = 'pointer';
        button.style.font = 'inherit';
        button.style.fontSize = '13px';
        button.style.fontWeight = '700';
        button.style.background = primary ? '#ef4444' : 'rgba(255, 255, 255, 0.08)';
        button.style.color = '#ffffff';
        button.addEventListener('click', () => finish(value));
        return button;
      };

      const allLayersButton = makeButton('All layers', true, true);
      const myLayersButton = makeButton('Just my layers', false, false);
      actions.appendChild(allLayersButton);
      actions.appendChild(myLayersButton);
      panel.appendChild(title);
      panel.appendChild(body);
      panel.appendChild(actions);
      overlay.appendChild(panel);
      document.body.appendChild(overlay);
      document.addEventListener('keydown', handleKeyDown);
      myLayersButton.focus();
    });
  }, [isCanvasOwner]);

  const handleClear = useCallback(async () => {
    if (gameRestrictionsRef.current && gameRestrictionsRef.current.noClear) return;
    if (!stageRef.current) return;
    if (gameModeRef.current && typeof gameModeRef.current.onEdit === 'function') gameModeRef.current.onEdit('clear');

    const previousProjectionCount = getProjectionStrokeCount();

    const stateLayers = Array.isArray(layers) ? layers.filter(Boolean) : [];
    const gpuLayers = typeof stageRef.current.getLayers === 'function'
      ? stageRef.current.getLayers()
      : [];
    const stateById = new Map(
      stateLayers
        .map((layer) => [String(layer && layer.id ? layer.id : '').trim(), layer])
        .filter(([id]) => id)
    );
    const authoritativeById = new Map();

    for (const gpuLayer of Array.isArray(gpuLayers) ? gpuLayers : []) {
      const id = String(gpuLayer && gpuLayer.id ? gpuLayer.id : '').trim();
      if (!id) continue;
      authoritativeById.set(id, {
        ...gpuLayer,
        ...(stateById.get(id) || {}),
        id,
      });
    }

    for (const layer of stateLayers) {
      const id = String(layer && layer.id ? layer.id : '').trim();
      if (!id || authoritativeById.has(id)) continue;
      authoritativeById.set(id, { ...layer, id });
    }

    const authoritativeLayers = Array.from(authoritativeById.values());
    if (!authoritativeLayers.length) {
      const viewport = modelViewportRef.current;
      if (viewport && typeof viewport.clearProjectionStrokes === 'function') {
        viewport.clearProjectionStrokes();
      }
      return;
    }

    const clearAllLayers = isCanvasOwner ? await chooseOwnerClearScope() : false;
    const targetLayers = clearAllLayers
      ? authoritativeLayers.slice()
      : authoritativeLayers.filter((layer) => isLayerOwnedByCurrentUser(
        layer,
        liveClientIdRef.current,
        layerOwnerName,
        currentUserId,
      ));

    if (!targetLayers.length) return;

    const viewport = modelViewportRef.current;
    if (viewport && typeof viewport.clearProjectionStrokes === 'function') {
      viewport.clearProjectionStrokes();
    } else if (previousProjectionCount > 0 && viewport?.setProjectionStrokeCount) {
      // Legacy fallback for an older viewport implementation.
      viewport.setProjectionStrokeCount(0);
    }

    const deletedLayerIds = new Set(
      targetLayers
        .map((layer) => String(layer && layer.id ? layer.id : '').trim())
        .filter(Boolean)
    );

    for (const layerId of deletedLayerIds) {
      purgeReplayLayerEvents(layerId);
    }

    for (const layer of targetLayers) {
      deletedLayerIdsRef.current.add(getLayerDeletionKey(
        layer.id,
        layer.ownerUserId || layer.owner_user_id || 0,
        layer.ownerClientId || '',
        layer.ownerName || '',
      ));
    }

    const remainingLayers = authoritativeLayers.filter(
      (layer) => !deletedLayerIds.has(String(layer && layer.id ? layer.id : '').trim())
    );

    const localOwnerProfile = {
      ownerName: layerOwnerName,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId,
    };
    const replacementLayer = createReplacementLayer(localOwnerProfile, 0);
    const nextLayers = [...remainingLayers, replacementLayer];
    const nextActiveLayerId = replacementLayer.id;

    stageRef.current.setLayers(nextLayers, nextActiveLayerId);
    setLayers(nextLayers);
    setActiveLayerId(nextActiveLayerId);

    const safeCanvasId = Number(canvasId);
    if (Number.isInteger(safeCanvasId) && safeCanvasId > 0) {
      const clearOperationId = `clear-${currentUserId || 0}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const makeDeleteEvent = (layer, passIndex, layerIndex) => {
        const layerId = String(layer && layer.id ? layer.id : '').trim();
        const targetOwnerName = String(layer.ownerName || layerOwnerName || 'User').trim() || 'User';
        const targetOwnerClientId = String(layer.ownerClientId || '').trim();
        const targetOwnerUserId = Number(layer.ownerUserId || layer.owner_user_id || 0);
        const eventSequence = Date.now() + (passIndex * 1000) + layerIndex;

        return {
          type: 'mask_info',
          clientId: liveClientIdRef.current,
          userId: currentUserId || undefined,
          senderUserId: currentUserId || undefined,
          ownerClientId: liveClientIdRef.current,
          ownerName: layerOwnerName,
          ownerUserId: currentUserId || undefined,
          seq: eventSequence,
          strokeId: `${clearOperationId}-delete-${passIndex}-${layerIndex}`,
          clearOperationId,
          layerId,
          deletedLayerId: layerId,
          targetLayerId: layerId,
          targetOwnerName,
          targetOwnerClientId,
          targetOwnerUserId: Number.isInteger(targetOwnerUserId) && targetOwnerUserId > 0 ? targetOwnerUserId : 0,
          layerOwnerName: targetOwnerName,
          layerOwnerClientId: targetOwnerClientId,
          layerOwnerUserId: Number.isInteger(targetOwnerUserId) && targetOwnerUserId > 0 ? targetOwnerUserId : 0,
          tool: 'mask',
          brushPreset: 'mask',
          maskAction: 'layer_delete',
          reason: 'layer_event:delete',
          allowEmpty: true,
          maskInfo: {
            type: 'canvas_layer_event',
            version: 2,
            layerId,
            maskId: String(layer.name || layerId).slice(0, 128),
            shape: 'rect',
            action: 'layer_delete',
            hiddenFromOthers: layer.hiddenFromOthers === true,
            inverted: false,
            bounds: { x: 0, y: 0, width: 0, height: 0 },
            transform: { translateX: 0, translateY: 0, rotation: 0, scaleX: 1, scaleY: 1, originX: 0, originY: 0 },
            vertices: [],
            maskVertices: [],
            maskVertexCount: 0,
          },
          state: {
            maskAction: 'layer_delete',
            reason: 'layer_event:delete',
            allowEmpty: true,
            clearOperationId,
            deletedLayerId: layerId,
            targetLayerId: layerId,
            targetOwnerName,
            targetOwnerClientId,
            targetOwnerUserId: Number.isInteger(targetOwnerUserId) && targetOwnerUserId > 0 ? targetOwnerUserId : 0,
            layerOwnerName: targetOwnerName,
            layerOwnerClientId: targetOwnerClientId,
            layerOwnerUserId: Number.isInteger(targetOwnerUserId) && targetOwnerUserId > 0 ? targetOwnerUserId : 0,
          },
          timestamp: Date.now(),
        };
      };

      for (let passIndex = 0; passIndex < 3; passIndex += 1) {
        for (let layerIndex = 0; layerIndex < targetLayers.length; layerIndex += 1) {
          const layer = targetLayers[layerIndex];
          const layerId = String(layer && layer.id ? layer.id : '').trim();
          if (!layerId || layer.hiddenFromOthers === true) continue;
          await postCanvasLiveStrokeEvent(
            safeCanvasId,
            makeDeleteEvent(layer, passIndex, layerIndex),
          );
        }

        if (passIndex < 2) {
          await new Promise((resolve) => {
            window.setTimeout(resolve, passIndex === 0 ? 180 : 650);
          });
        }
      }
    }

    setHasPixels(stageRef.current.hasPixels());

    const clearSnapshot = stageRef.current.snapshot();
    if (clearSnapshot) {
      stampProjectionHistory(clearSnapshot);
      destroySnapshots(redoHistoryRef.current);
      redoHistoryRef.current = [];
      historyRef.current.push(clearSnapshot);
      clearSnapshotRef.current = clearSnapshot;
      clearHistoryResetPendingRef.current = true;

      while (historyRef.current.length > MAX_HISTORY + 1) {
        const removed = historyRef.current.shift();
        if (removed && removed !== clearSnapshot) stageRef.current.destroySnapshot(removed);
      }

      updateHistoryCounts();
    }
  }, [canvasId, chooseOwnerClearScope, currentUserId, destroySnapshots, getProjectionStrokeCount, isCanvasOwner, layerOwnerName, layers, purgeReplayLayerEvents, stampProjectionHistory, updateHistoryCounts]);

  const captureAnimationFrameSnapshot = useCallback((frameId, frameName, frameIndex = 0, previousFrame = null) => {
    const stage = stageRef.current;
    if (!stage || typeof stage.exportLayerPatch !== 'function') return null;
    const currentLayers = Array.isArray(layersRef.current) ? layersRef.current.filter(Boolean).map((layer) => ({ ...layer })) : [];
    const previousLayerBitmaps = previousFrame && previousFrame.layerBitmaps && typeof previousFrame.layerBitmaps === 'object'
      ? previousFrame.layerBitmaps
      : {};
    const previousPaintLayerStates = previousFrame && previousFrame.paintLayerStates && typeof previousFrame.paintLayerStates === 'object'
      ? previousFrame.paintLayerStates
      : {};
    const layerBitmaps = {};
    const paintLayerStates = {};
    for (const layer of currentLayers) {
      const layerId = String(layer && layer.id ? layer.id : '').trim();
      if (!layerId) continue;
      const patch = stage.exportLayerPatch(layerId);
      if (patch && typeof patch.dataUrl === 'string' && patch.dataUrl.startsWith('data:image/')) {
        layerBitmaps[layerId] = patch.dataUrl;
      } else if (typeof previousLayerBitmaps[layerId] === 'string' && previousLayerBitmaps[layerId].startsWith('data:image/')) {
        layerBitmaps[layerId] = previousLayerBitmaps[layerId];
      }
      if (typeof stage.exportPaintLayerState === 'function') {
        const paintState = stage.exportPaintLayerState(layerId);
        if (paintState && typeof paintState === 'object') {
          paintLayerStates[layerId] = paintState;
        } else if (previousPaintLayerStates[layerId] && typeof previousPaintLayerStates[layerId] === 'object') {
          paintLayerStates[layerId] = previousPaintLayerStates[layerId];
        }
      }
    }
    const safeFrameId = frameId || createAnimationFrameId();
    if (typeof stage.snapshot === 'function') {
      const previousSnapshot = animationStageSnapshotsRef.current.get(safeFrameId);
      const nextSnapshot = stage.snapshot();
      if (nextSnapshot) {
        if (previousSnapshot && previousSnapshot !== nextSnapshot && typeof stage.destroySnapshot === 'function') {
          stage.destroySnapshot(previousSnapshot);
        }
        animationStageSnapshotsRef.current.set(safeFrameId, nextSnapshot);
      }
    }

    return sanitizeAnimationFrame({
      id: safeFrameId,
      name: frameName || `Frame ${frameIndex + 1}`,
      index: frameIndex,
      activeLayerId: String(activeLayerId || ''),
      layers: currentLayers,
      layerBitmaps,
      paintLayerStates,
      savedAt: Date.now(),
    }, frameIndex);
  }, [activeLayerId]);

  const persistAnimationState = useCallback((frames, activeFrameId, enabled = animationEnabledRef.current) => {
    persistAnimationFramesIndividually(
      canvasId,
      frames,
      activeFrameId,
      enabled,
    ).catch(() => {});
  }, [canvasId]);

  const broadcastAnimationFrames = useCallback((action, frames, activeFrameId) => {
    if (animationBroadcastTimerRef.current) window.clearTimeout(animationBroadcastTimerRef.current);
    animationBroadcastTimerRef.current = window.setTimeout(() => {
      animationBroadcastTimerRef.current = 0;
      sendCanvasAnimationEvent(action, {
        enabled: animationEnabledRef.current,
        frames,
        activeFrameId,
      });
    }, 180);
  }, [sendCanvasAnimationEvent]);

  const saveActiveAnimationFrame = useCallback((action = 'frame_update', broadcast = true) => {
    if (!animationEnabledRef.current || animationRestoringFrameRef.current) return null;
    const frames = sanitizeAnimationFrames(animationFramesRef.current);
    const activeId = String(animationActiveFrameIdRef.current || frames[0]?.id || '');
    if (!frames.length || !activeId) return null;
    const activeIndex = Math.max(0, frames.findIndex((frame) => frame.id === activeId));
    const current = frames[activeIndex];
    const snapshot = captureAnimationFrameSnapshot(activeId, current?.name || `Frame ${activeIndex + 1}`, activeIndex, current);
    if (!snapshot) return null;
    const nextFrames = frames.map((frame, index) => frame.id === activeId ? sanitizeAnimationFrame(snapshot, index) : sanitizeAnimationFrame(frame, index));
    animationFramesRef.current = nextFrames;
    setAnimationFrames(nextFrames);
    persistAnimationState(nextFrames, activeId, true);
    if (broadcast) broadcastAnimationFrames(action, nextFrames, activeId);
    return snapshot;
  }, [broadcastAnimationFrames, captureAnimationFrameSnapshot, persistAnimationState]);

  const scheduleAnimationFrameSave = useCallback((action = 'frame_update', broadcast = true) => {
    if (!animationEnabledRef.current || animationRestoringFrameRef.current) return;
    if (animationCaptureTimerRef.current) window.clearTimeout(animationCaptureTimerRef.current);
    animationCaptureTimerRef.current = window.setTimeout(() => {
      animationCaptureTimerRef.current = 0;
      saveActiveAnimationFrame(action, broadcast);
    }, 260);
  }, [saveActiveAnimationFrame]);

  useEffect(() => {
    animationScheduleSaveRef.current = scheduleAnimationFrameSave;
    return () => {
      if (animationScheduleSaveRef.current === scheduleAnimationFrameSave) animationScheduleSaveRef.current = null;
    };
  }, [scheduleAnimationFrameSave]);

  const restoreAnimationFrame = useCallback(async (frame) => {
    const stage = stageRef.current;
    if (!stage || !frame) return false;
    animationRestoringFrameRef.current = true;
    try {
      const nextLayers = Array.isArray(frame.layers) ? frame.layers.map((layer) => ({ ...layer })) : [];
      if (!nextLayers.length) return false;
      const nextActiveLayerId = nextLayers.some((layer) => String(layer.id) === String(frame.activeLayerId))
        ? String(frame.activeLayerId)
        : String(nextLayers[nextLayers.length - 1]?.id || '');

      layersRef.current = nextLayers;
      flushSync(() => {
        setLayers(nextLayers);
        if (nextActiveLayerId) setActiveLayerId(nextActiveLayerId);
      });

      const currentStage = stageRef.current;
      if (!currentStage) return false;
      currentStage.setLayers(nextLayers, nextActiveLayerId);

      const storedSnapshot = animationStageSnapshotsRef.current.get(String(frame.id || ''));
      if (storedSnapshot && typeof currentStage.restoreSnapshot === 'function') {
        currentStage.restoreSnapshot(storedSnapshot);
      } else {
        for (const layer of nextLayers) {
          const layerId = String(layer.id || '').trim();
          const bitmap = frame.layerBitmaps && frame.layerBitmaps[layerId];
          if (layerId && typeof currentStage.clearLayer === 'function') currentStage.clearLayer(layerId);
          if (layerId && typeof bitmap === 'string' && bitmap.startsWith('data:image/')) {
            await currentStage.loadLayerDataUrl(layerId, bitmap);
          }
          const paintState = frame.paintLayerStates && frame.paintLayerStates[layerId];
          if (layerId && paintState && typeof currentStage.loadPaintLayerState === 'function') {
            await currentStage.loadPaintLayerState(layerId, paintState);
          }
        }

        if (typeof currentStage.snapshot === 'function') {
          const restoredSnapshot = currentStage.snapshot();
          if (restoredSnapshot) animationStageSnapshotsRef.current.set(String(frame.id || ''), restoredSnapshot);
        }
      }

      const restoredFrameId = String(frame.id || '');
      const restoredFrameIndex = animationFramesRef.current.findIndex(
        (candidate) => String(candidate && candidate.id ? candidate.id : '') === restoredFrameId
      );
      animationMountedFrameIdRef.current = restoredFrameId;
      animationMountedFrameIndexRef.current = restoredFrameIndex;
      setHasPixels(currentStage.hasPixels());
      resetHistory();
      return true;
    } finally {
      animationRestoringFrameRef.current = false;
    }
  }, [resetHistory]);

  const handleSelectAnimationFrame = useCallback(async (frameId) => {
    const nextId = String(frameId || '');
    if (!nextId || nextId === animationActiveFrameIdRef.current || animationRestoringFrameRef.current) return;
    if (animationCaptureTimerRef.current) {
      window.clearTimeout(animationCaptureTimerRef.current);
      animationCaptureTimerRef.current = 0;
    }
    saveActiveAnimationFrame('frame_update', false);
    const frames = sanitizeAnimationFrames(animationFramesRef.current);
    const target = frames.find((frame) => frame.id === nextId);
    if (!target) return;
    animationActiveFrameIdRef.current = nextId;
    animationActiveFrameIndexRef.current = Math.max(0, frames.findIndex((frame) => frame.id === nextId));
    setAnimationActiveFrameId(nextId);
    await restoreAnimationFrame(target);
    persistAnimationState(animationFramesRef.current, nextId, true);
  }, [persistAnimationState, restoreAnimationFrame, saveActiveAnimationFrame]);

  const handleAddAnimationFrame = useCallback(async () => {
    saveActiveAnimationFrame('frame_update', false);
    const frames = sanitizeAnimationFrames(animationFramesRef.current);
    const frameIndex = frames.length;
    const currentLayers = Array.isArray(layersRef.current) ? layersRef.current.filter(Boolean).map((layer) => ({ ...layer })) : [];
    const blankFrame = sanitizeAnimationFrame({
      id: createAnimationFrameId(),
      name: `Frame ${frameIndex + 1}`,
      index: frameIndex,
      activeLayerId: String(activeLayerId || currentLayers[currentLayers.length - 1]?.id || ''),
      layers: currentLayers,
      layerBitmaps: {},
      paintLayerStates: {},
      savedAt: Date.now(),
    }, frameIndex);
    const nextFrames = [...frames, blankFrame];
    animationFramesRef.current = nextFrames;
    animationActiveFrameIdRef.current = blankFrame.id;
    animationActiveFrameIndexRef.current = frameIndex;
    setAnimationFrames(nextFrames);
    setAnimationActiveFrameId(blankFrame.id);
    await restoreAnimationFrame(blankFrame);
    persistAnimationState(nextFrames, blankFrame.id, true);
    sendCanvasAnimationEvent('frame_add', { enabled: true, frames: nextFrames, activeFrameId: blankFrame.id });
  }, [activeLayerId, persistAnimationState, restoreAnimationFrame, saveActiveAnimationFrame, sendCanvasAnimationEvent]);

  const handleDeleteAnimationFrame = useCallback(async (frameId) => {
    const frames = sanitizeAnimationFrames(animationFramesRef.current);
    if (frames.length <= 1) return;
    const removeId = String(frameId || animationActiveFrameIdRef.current || '');
    const removeIndex = frames.findIndex((frame) => frame.id === removeId);
    if (removeIndex < 0) return;
    const nextFrames = frames.filter((frame) => frame.id !== removeId).map((frame, index) => sanitizeAnimationFrame(frame, index));
    deleteCanvasState(getCanvasAnimationFrameStateKey(canvasId, removeId)).catch(() => {});
    const removedSnapshot = animationStageSnapshotsRef.current.get(removeId);
    if (removedSnapshot && stageRef.current && typeof stageRef.current.destroySnapshot === 'function') {
      stageRef.current.destroySnapshot(removedSnapshot);
    }
    animationStageSnapshotsRef.current.delete(removeId);
    const nextActive = nextFrames[Math.min(removeIndex, nextFrames.length - 1)] || nextFrames[0];
    animationFramesRef.current = nextFrames;
    animationActiveFrameIdRef.current = nextActive.id;
    animationActiveFrameIndexRef.current = Math.max(0, nextFrames.findIndex((frame) => frame.id === nextActive.id));
    setAnimationFrames(nextFrames);
    setAnimationActiveFrameId(nextActive.id);
    await restoreAnimationFrame(nextActive);
    persistAnimationState(nextFrames, nextActive.id, true);
    sendCanvasAnimationEvent('frame_delete', { enabled: true, frames: nextFrames, activeFrameId: nextActive.id });
  }, [canvasId, persistAnimationState, restoreAnimationFrame, sendCanvasAnimationEvent]);

  const setSharedAnimationMode = useCallback(async (enabled) => {
    if (!isCanvasOwner) return;
    const nextEnabled = Boolean(enabled);
    const wasEnabled = Boolean(animationEnabledRef.current);
    const safeCanvasId = Number(canvasId);
    if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0) return;

    try {
      const response = await fetch(`/api/canvas/${encodeURIComponent(String(safeCanvasId))}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ animation_enabled: nextEnabled }),
      });
      if (!response.ok) return;
    } catch {
      return;
    }

    animationEnabledRef.current = nextEnabled;
    setAnimationEnabled(nextEnabled);

    if (nextEnabled && !wasEnabled) {
      const firstFrame = captureAnimationFrameSnapshot(createAnimationFrameId(), 'Frame 1', 0);
      if (firstFrame) {
        const nextFrames = [firstFrame];
        animationFramesRef.current = nextFrames;
        animationActiveFrameIdRef.current = firstFrame.id;
        animationActiveFrameIndexRef.current = 0;
        animationMountedFrameIdRef.current = firstFrame.id;
        animationMountedFrameIndexRef.current = 0;
        setAnimationFrames(nextFrames);
        setAnimationActiveFrameId(firstFrame.id);
        persistAnimationState(nextFrames, firstFrame.id, true);
        sendCanvasAnimationEvent('enable', {
          enabled: true,
          frames: nextFrames,
          activeFrameId: firstFrame.id,
        });
      } else {
        animationFramesRef.current = [];
        animationActiveFrameIdRef.current = '';
        animationActiveFrameIndexRef.current = 0;
        animationMountedFrameIdRef.current = '';
        animationMountedFrameIndexRef.current = -1;
        setAnimationFrames([]);
        setAnimationActiveFrameId('');
        persistAnimationState([], '', true);
        sendCanvasAnimationModeEvent(true, 'enable');
      }
      return;
    }

    const currentFrames = sanitizeAnimationFrames(animationFramesRef.current);
    const currentActiveFrameId = String(animationActiveFrameIdRef.current || currentFrames[0]?.id || '');
    persistAnimationState(currentFrames, currentActiveFrameId, nextEnabled);
    sendCanvasAnimationEvent(nextEnabled ? 'enable' : 'disable', {
      enabled: nextEnabled,
      frames: currentFrames,
      activeFrameId: currentActiveFrameId,
    });
  }, [canvasId, captureAnimationFrameSnapshot, isCanvasOwner, persistAnimationState, sendCanvasAnimationEvent, sendCanvasAnimationModeEvent]);

  const handleEnableAnimation = useCallback(() => {
    setSharedAnimationMode(true);
  }, [setSharedAnimationMode]);

  const handleDisableAnimation = useCallback(() => {
    setSharedAnimationMode(false);
  }, [setSharedAnimationMode]);

  const animationPanel = animationEnabled ? (
    <CanvasAnimationPanel
      enabled={animationEnabled}
      isOwner={isCanvasOwner}
      frames={animationFrames}
      activeFrameId={animationActiveFrameId}
      onEnableAnimation={handleEnableAnimation}
      onDisableAnimation={handleDisableAnimation}
      onAddFrame={handleAddAnimationFrame}
      onDeleteFrame={handleDeleteAnimationFrame}
      onSelectFrame={handleSelectAnimationFrame}
    />
  ) : null;

  const handleSave = useCallback((requestedFormat = 'png') => {
    if (!stageRef.current) return;
    const isJpeg = requestedFormat === 'jpeg' || requestedFormat === 'jpg';
    const mimeType = isJpeg ? 'image/jpeg' : 'image/png';
    const extension = isJpeg ? 'jpg' : 'png';
    const dataUrl = stageRef.current.exportDataUrl(mimeType, isJpeg ? 0.94 : undefined);
    downloadDataUrl(
      dataUrl,
      `${canvasName.replace(/[^a-z0-9_-]+/gi, '_') || 'canvas'}.${extension}`,
    );
  }, [canvasName]);

  const handleSaveCanvasProject = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || typeof stage.exportLayerPatch !== 'function') return;
    const currentLayers = Array.isArray(layersRef.current) ? layersRef.current.filter(Boolean) : [];
    const layerBitmaps = {};
    const paintLayerStates = {};
    for (const layer of currentLayers) {
      const layerId = String(layer && layer.id ? layer.id : '').trim();
      if (!layerId) continue;
      const patch = typeof stage.exportBaseLayerPatch === 'function'
        ? stage.exportBaseLayerPatch(layerId)
        : stage.exportLayerPatch(layerId);
      if (patch && typeof patch.dataUrl === 'string' && patch.dataUrl.startsWith('data:image/')) {
        layerBitmaps[layerId] = patch.dataUrl;
      }
      if (typeof stage.exportPaintLayerState === 'function') {
        const paintState = stage.exportPaintLayerState(layerId);
        if (paintState && typeof paintState === 'object') paintLayerStates[layerId] = paintState;
      }
    }
    const replayEvents = serialiseReplayEvents(
      filterReplayEventsForLayers(replayEventsRef.current, currentLayers)
    );
    const payload = {
      type: 'voxemera-canvas',
      version: 4,
      savedAt: Date.now(),
      canvasId: Number(canvasId) || 0,
      name: canvasName,
      width: documentSize.width,
      height: documentSize.height,
      activeLayerId: String(activeLayerId || ''),
      layers: currentLayers.map((layer) => ({ ...layer })),
      layerBitmaps,
      paintLayerStates,
      paperSurface: typeof stage.getPaperSurface === 'function' ? stage.getPaperSurface() : null,
      compositeImage: stage.exportDataUrl('image/png'),
      playback: {
        type: 'eventLog',
        eventCount: replayEvents.length,
      },
      replayEvents,
    };
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/vnd.voxemera.canvas+json' });
    downloadBlob(
      blob,
      `${canvasName.replace(/[^a-z0-9_-]+/gi, '_') || 'canvas'}.voxcanvas`,
    );
  }, [activeLayerId, canvasId, canvasName, documentSize.height, documentSize.width]);

  const openFilePicker = useCallback((mode) => {
    const input = fileInputRef.current;
    if (!input) return;
    fileInputModeRef.current = mode === 'project' ? 'project' : 'image';
    input.accept = mode === 'project'
      ? '.voxcanvas,application/json,application/vnd.voxemera.canvas+json'
      : 'image/png,image/jpeg,image/webp';
    input.click();
  }, []);

  const handleImportCanvasProject = useCallback(() => {
    openFilePicker('project');
  }, [openFilePicker]);

  const handleImportCanvasImage = useCallback(() => {
    openFilePicker('image');
  }, [openFilePicker]);

  const importImageFileAsLayer = useCallback(async (file) => {
    // Drawing games forbid outside pictures (pasted, dropped or picked) unless their rules allow them.
    if (gameRestrictionsRef.current && !gameRestrictionsRef.current.allowImport) return false;
    if (!file || !String(file.type || '').toLowerCase().startsWith('image/')) return false;
    const stage = stageRef.current;
    if (!stage) return false;

    if (modelViewOpen) {
      const [image, imageDataUrl] = await Promise.all([
        loadImageFromFile(file),
        readFileAsDataUrl(file),
      ]);
      const sourceName = String(file.name || '')
        .replace(/\.[^.]+$/, '')
        .trim();
      const currentLayers = Array.isArray(layersRef.current)
        ? layersRef.current.filter(Boolean)
        : [];
      const ownedCount = currentLayers.filter((layer) => (
        isLayerOwnedByCurrentUser(layer, liveClientIdRef.current, layerOwnerName, currentUserId)
      )).length;
      const id = createOwnedLayerId({
        ownerName: layerOwnerName,
        ownerClientId: liveClientIdRef.current,
        ownerUserId: currentUserId || 0,
      }, 'decal');
      const viewport = modelViewportRef.current;
      const defaultPlacement = viewport && typeof viewport.getDefaultDecalPlacement === 'function'
        ? viewport.getDefaultDecalPlacement()
        : null;
      const nextLayer = withLayerVersion({
        id,
        name: sourceName || `Image ${ownedCount + 1}`,
        ownerName: layerOwnerName,
        ownerClientId: liveClientIdRef.current,
        ownerUserId: currentUserId || undefined,
        visible: true,
        opacity: 1,
        contentType: 'decal',
        decalData: {
          imageDataUrl,
          imageWidth: Math.max(1, Number(image.naturalWidth || image.width) || 1),
          imageHeight: Math.max(1, Number(image.naturalHeight || image.height) || 1),
          position: Array.isArray(defaultPlacement?.position) ? defaultPlacement.position : null,
          normal: Array.isArray(defaultPlacement?.normal) ? defaultPlacement.normal : null,
          baseWorldSize: Math.max(0.01, Number(defaultPlacement?.baseWorldSize) || 1),
          scale: 1,
          rotation: 0,
          flipX: false,
          flipY: false,
        },
      }, 1, Date.now());
      const nextLayers = [...currentLayers, nextLayer];
      layersRef.current = nextLayers;
      flushSync(() => {
        setLayers(nextLayers);
        setActiveLayerId(id);
      });
      stage.setLayers(nextLayers, id);
      stage.setActiveLayer(id);
      sendLayerState(nextLayers);
      markCanvasStateDirty();
      resetHistory();
      return true;
    }

    const image = await loadImageFromFile(file);
    const sourceName = String(file.name || '')
      .replace(/\.[^.]+$/, '')
      .trim();
    const currentLayers = Array.isArray(layersRef.current)
      ? layersRef.current.filter(Boolean)
      : [];
    const ownedCount = currentLayers.filter((layer) => (
      isLayerOwnedByCurrentUser(layer, liveClientIdRef.current, layerOwnerName, currentUserId)
    )).length;
    const id = createOwnedLayerId({
      ownerName: layerOwnerName,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId || 0,
    }, 'image');
    const now = Date.now();
    const nextLayer = withLayerVersion({
      id,
      name: sourceName || `Image ${ownedCount + 1}`,
      ownerName: layerOwnerName,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId || undefined,
      visible: true,
      opacity: 1,
    }, 1, now);
    const nextLayers = [...currentLayers, nextLayer];

    // Keep React, refs, the layer panel and the GPU engine on the new layer
    // before any pixels are written. This prevents loadImage() from clearing
    // and replacing the previously active layer.
    layersRef.current = nextLayers;
    flushSync(() => {
      setLayers(nextLayers);
      setActiveLayerId(id);
    });
    stage.setLayers(nextLayers, id);
    stage.setActiveLayer(id);
    stage.loadImage(image);

    sendLayerState(nextLayers);

    const patch = typeof stage.exportLayerPatch === 'function'
      ? stage.exportLayerPatch(id)
      : null;
    if (patch) {
      handleLocalLiveEvent({
        type: 'stroke_end',
        layerId: id,
        strokeId: `gpu-import-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        patch,
        rasterPatchEvent: true,
        rasterPatchType: 'raster_patch',
        reason: 'image_import_layer',
        state: {
          rasterPatchEvent: true,
          rasterPatchType: 'raster_patch',
          patch,
          reason: 'image_import_layer',
        },
      });
    }

    setHasPixels(true);
    resetHistory();
    markCanvasStateDirty();
    return true;
  }, [currentUserId, handleLocalLiveEvent, layerOwnerName, markCanvasStateDirty, modelViewOpen, resetHistory, sendLayerState]);

  const gameAllowsImport = !gameRestrictions || gameRestrictions.allowImport;
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    // A game editor that takes no outside pictures must not catch pastes and drops meant for the page.
    if (!gameAllowsImport) return undefined;

    const getImageFile = (fileList) => Array.from(fileList || []).find((candidate) => {
      if (!candidate) return false;
      const type = String(candidate.type || '').toLowerCase();
      const name = String(candidate.name || '').toLowerCase();
      return type.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp|avif)$/i.test(name);
    }) || null;

    const isEditableTarget = (target) => {
      const tagName = String(target && target.tagName ? target.tagName : '').toLowerCase();
      return Boolean(target && (
        target.isContentEditable
        || tagName === 'input'
        || tagName === 'textarea'
        || tagName === 'select'
      ));
    };

    const importDroppedOrPastedFile = (file) => {
      if (!file) return;
      importImageFileAsLayer(file).catch((error) => {
        console.error('[Canvas image import] Unable to import image:', error);
      });
    };

    const handlePaste = (event) => {
      if (isEditableTarget(event.target)) return;
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      const imageItem = Array.from(clipboardData.items || []).find((candidate) => {
        if (!candidate || candidate.kind !== 'file') return false;
        return String(candidate.type || '').toLowerCase().startsWith('image/');
      });
      const file = imageItem && typeof imageItem.getAsFile === 'function'
        ? imageItem.getAsFile()
        : getImageFile(clipboardData.files);
      if (!file) return;

      event.preventDefault();
      event.stopPropagation();
      importDroppedOrPastedFile(file);
    };

    const isInsideBrushImporter = (event) => Boolean(event.target && typeof event.target.closest === 'function' && event.target.closest('.vbi-root'));

    const dragContainsFiles = (event) => {
      if (isInsideBrushImporter(event)) return false;
      const transfer = event.dataTransfer;
      if (!transfer) return false;
      if (getImageFile(transfer.files)) return true;
      const types = Array.from(transfer.types || []).map((type) => String(type).toLowerCase());
      if (types.includes('files')) return true;
      return Array.from(transfer.items || []).some((item) => (
        item && item.kind === 'file'
      ));
    };

    const handleDragEnterOrOver = (event) => {
      if (!dragContainsFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop = (event) => {
      if (!dragContainsFiles(event)) return;
      // Always cancel browser navigation for dropped files, even when the OS
      // supplies an empty MIME type. We validate the extension afterwards.
      event.preventDefault();
      event.stopPropagation();
      const droppedFiles = Array.from((event.dataTransfer && event.dataTransfer.files) || []);
      const brushFiles = droppedFiles.filter((candidate) => candidate && BRUSH_PACK_FILE_PATTERN.test(String(candidate.name || '')));
      if (brushFiles.length) {
        setDroppedBrushFiles(brushFiles);
        return;
      }
      const file = getImageFile(event.dataTransfer && event.dataTransfer.files);
      if (file) importDroppedOrPastedFile(file);
    };

    // Capture phase is intentional: nested canvas controls must not get a
    // chance to let Chrome open the dropped image in a new tab.
    document.addEventListener('paste', handlePaste, true);
    document.addEventListener('dragenter', handleDragEnterOrOver, true);
    document.addEventListener('dragover', handleDragEnterOrOver, true);
    document.addEventListener('drop', handleDrop, true);
    return () => {
      document.removeEventListener('paste', handlePaste, true);
      document.removeEventListener('dragenter', handleDragEnterOrOver, true);
      document.removeEventListener('dragover', handleDragEnterOrOver, true);
      document.removeEventListener('drop', handleDrop, true);
    };
  }, [gameAllowsImport, importImageFileAsLayer]);

  const handleFileChange = useCallback(async (event) => {
    if (gameModeRef.current) return;
    const file = event.target.files && event.target.files[0];
    const requestedMode = fileInputModeRef.current;
    event.target.value = '';
    if (!file || !stageRef.current) return;
    const fileName = String(file.name || '').toLowerCase();
    const isProjectFile = requestedMode === 'project'
      || fileName.endsWith('.voxcanvas')
      || String(file.type || '').includes('json');

    try {
      if (isProjectFile) {
        const rawProject = await readTextFromFile(file);
        const project = JSON.parse(rawProject);
        const projectLayers = Array.isArray(project.layers)
          ? project.layers.filter((layer) => layer && String(layer.id || '').trim())
          : [];
        if (!projectLayers.length) throw new Error('The Voxemera file has no layers.');

        const projectActiveLayerId = String(project.activeLayerId || '').trim();
        const nextActiveLayerId = projectLayers.some((layer) => String(layer.id) === projectActiveLayerId)
          ? projectActiveLayerId
          : String(projectLayers[projectLayers.length - 1].id);
        const layerBitmaps = project.layerBitmaps && typeof project.layerBitmaps === 'object'
          ? project.layerBitmaps
          : {};

        stageRef.current.setLayers(projectLayers, nextActiveLayerId);
        for (const layer of projectLayers) {
          stageRef.current.clearLayer(String(layer.id));
        }
        for (const layer of projectLayers) {
          const layerId = String(layer.id || '').trim();
          const dataUrl = layerBitmaps[layerId];
          if (!layerId || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) continue;
          await stageRef.current.loadLayerDataUrl(layerId, dataUrl);
        }

        if (project.paperSurface && typeof stageRef.current.setPaperSurface === 'function') {
          stageRef.current.setPaperSurface(project.paperSurface);
        }
        const paintLayerStates = project.paintLayerStates && typeof project.paintLayerStates === 'object'
          ? project.paintLayerStates
          : {};
        if (typeof stageRef.current.loadPaintLayerState === 'function') {
          for (const layer of projectLayers) {
            const layerId = String(layer.id || '').trim();
            const paintState = paintLayerStates[layerId];
            if (!layerId || !paintState || typeof paintState !== 'object') continue;
            await stageRef.current.loadPaintLayerState(layerId, paintState);
          }
        }

        const projectReplayEvents = Array.isArray(project.replayEvents)
          ? project.replayEvents
          : Array.isArray(project.strokeLog)
            ? project.strokeLog
            : Array.isArray(project.events)
              ? project.events
              : [];
        replayEventsRef.current = filterReplayEventsForLayers(projectReplayEvents, projectLayers)
          .map((replayEvent, index) => ({
            ...replayEvent,
            replaySequence: Number(replayEvent.replaySequence) || index + 1,
            loggedAt: Number(replayEvent.loggedAt || replayEvent.timestamp || replayEvent.seq) || Date.now(),
          }))
          .sort((left, right) => Number(left.replaySequence || 0) - Number(right.replaySequence || 0));
        replaySequenceRef.current = replayEventsRef.current.reduce(
          (maximum, replayEvent) => Math.max(maximum, Number(replayEvent.replaySequence) || 0),
          0
        );
        deletedReplayLayerEventsRef.current.clear();
        layersRef.current = projectLayers;
        setLayers(projectLayers);
        setActiveLayerId(nextActiveLayerId);
        setReplayRevision((value) => value + 1);
        setHasPixels(stageRef.current.hasPixels());
        resetHistory();
        markCanvasStateDirty();
        return;
      }

      await importImageFileAsLayer(file);
    } catch {
    }
  }, [importImageFileAsLayer, markCanvasStateDirty, resetHistory]);

  const handleAddImportedBrushPresets = useCallback((presets, preferredActiveId = '') => {
    const list = Array.isArray(presets) ? presets.filter(Boolean) : [];
    if (!list.length) return;
    setImportedBrushPresets((current) => {
      const nextById = new Map((Array.isArray(current) ? current : []).filter(Boolean).map((brush) => [brush.id, brush]));
      list.forEach((brush) => nextById.set(brush.id, brush));
      return Array.from(nextById.values());
    });
    const nextActiveId = String(preferredActiveId || list[list.length - 1]?.id || '');
    if (nextActiveId) setActiveImportedBrushId(nextActiveId);
  }, []);

  // A selected brush whose tips are still decoding stays active (its dabs
  // start once the tips arrive) rather than falling back to the round pen.
  const activeImportedBrush = (((activeTool === 'pen' || activeTool === 'line' || activeTool === 'rectangle' || activeTool === 'ellipse') && activeBrushPreset === 'pen')
    || activeTool === 'eraser'
    || (activeTool === 'paint' && String(paintSettings?.tool || 'tube') === 'brush'))
    ? importedBrushPresets.find((brush) => brush && brush.id === activeImportedBrushId && brush.paintable !== false) || null
    : null;
  const isShapeToolActive = activeTool === 'line' || activeTool === 'rectangle' || activeTool === 'ellipse';
  const isBrushDrivenTool = activeTool === 'pen' || isShapeToolActive;
  const isAirbrush = isBrushDrivenTool && activeBrushPreset === 'airbrush' && !activeImportedBrush;
  const isCalligraphy = isBrushDrivenTool && activeBrushPreset === 'calligraphy' && !activeImportedBrush;
  const activeSettingsKey = activeTool === 'paint'
    ? 'paint'
    : activeTool === 'eraser'
    ? activeImportedBrush
      ? `eraser:imported:${activeImportedBrush.id}`
      : 'eraser'
    : activeImportedBrush
      ? `imported:${activeImportedBrush.id}`
      : isAirbrush
        ? 'airbrush'
        : isCalligraphy
          ? 'calligraphy'
          : 'pen';
  const activeSettingsValuesRef = useRef(getStoredSettingsForKey(initialToolSettingsRef.current, 'pen', initialLastColourRef.current));

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('voxemera.canvas.bucketExpansion', String(bucketExpansion));
    }
  }, [bucketExpansion]);

  useEffect(() => {
    writeStoredToolSettings(toolSettingsByKey);
  }, [toolSettingsByKey]);

  useEffect(() => {
    writeStoredPaintSettings(paintSettings);
  }, [paintSettings]);

  useEffect(() => {
    writeStoredLastTool({
      activeTool,
      activeBrushPreset,
      activeImportedBrushId,
    });
  }, [activeBrushPreset, activeImportedBrushId, activeTool]);

  useEffect(() => {
    const settings = getStoredSettingsForKey(toolSettingsByKey, activeSettingsKey, readStoredLastColour());
    activeSettingsValuesRef.current = settings;
    setStrokeColor(settings.strokeColor);
    setBrushSize(settings.brushSize);
    setBrushSoftness(settings.brushSoftness);
    setJitterAmount(settings.jitterAmount);
    setToolOpacity(settings.toolOpacity);
    setCalligraphySettings(settings.calligraphySettings && typeof settings.calligraphySettings === 'object'
      ? { ...DEFAULT_CALLIGRAPHY_SETTINGS, ...settings.calligraphySettings }
      : { ...DEFAULT_CALLIGRAPHY_SETTINGS });
    setAirbrushDistance(settings.airbrushDistance == null ? AIRBRUSH_TOOL_DEFAULTS.airbrushDistance : settings.airbrushDistance);
    setAirbrushPaintFlow(settings.airbrushPaintFlow == null ? AIRBRUSH_TOOL_DEFAULTS.airbrushPaintFlow : settings.airbrushPaintFlow);
    setAirbrushPressurePaintFlowEnabled(settings.airbrushPressurePaintFlowEnabled == null ? AIRBRUSH_TOOL_DEFAULTS.airbrushPressurePaintFlowEnabled : settings.airbrushPressurePaintFlowEnabled);
    setAirbrushAirPressure(settings.airbrushAirPressure == null ? AIRBRUSH_TOOL_DEFAULTS.airbrushAirPressure : settings.airbrushAirPressure);
    setAirbrushNeedleLimit(settings.airbrushNeedleLimit == null ? AIRBRUSH_TOOL_DEFAULTS.airbrushNeedleLimit : settings.airbrushNeedleLimit);
  }, [activeSettingsKey, toolSettingsByKey]);

  const updateActiveToolSetting = useCallback((field, valueOrUpdater, setter) => {
    const currentSettings = activeSettingsValuesRef.current || getDefaultSettingsForKey(activeSettingsKey);
    const currentValue = currentSettings[field];
    const nextValue = typeof valueOrUpdater === 'function' ? valueOrUpdater(currentValue) : valueOrUpdater;
    const nextSettings = { ...currentSettings, [field]: nextValue };
    activeSettingsValuesRef.current = nextSettings;
    setter(nextValue);
    setToolSettingsByKey((current) => ({
      ...current,
      [activeSettingsKey]: {
        ...(current[activeSettingsKey] && typeof current[activeSettingsKey] === 'object' ? current[activeSettingsKey] : {}),
        [field]: nextValue,
      },
    }));
  }, [activeSettingsKey]);

  const setPersistentStrokeColor = useCallback((value) => {
    const nextColour = typeof value === 'function' ? value(strokeColor) : value;
    writeStoredLastColour(nextColour);
    updateActiveToolSetting('strokeColor', nextColour, setStrokeColor);
  }, [strokeColor, updateActiveToolSetting]);
  const setPersistentBrushSize = useCallback((value) => updateActiveToolSetting('brushSize', value, setBrushSize), [updateActiveToolSetting]);
  const setPersistentBrushSoftness = useCallback((value) => updateActiveToolSetting('brushSoftness', value, setBrushSoftness), [updateActiveToolSetting]);
  const setPersistentJitterAmount = useCallback((value) => updateActiveToolSetting('jitterAmount', value, setJitterAmount), [updateActiveToolSetting]);
  const setPersistentToolOpacity = useCallback((value) => updateActiveToolSetting('toolOpacity', value, setToolOpacity), [updateActiveToolSetting]);
  const setPersistentCalligraphySettings = useCallback((value) => updateActiveToolSetting('calligraphySettings', value, setCalligraphySettings), [updateActiveToolSetting]);
  const setPersistentAirbrushDistance = useCallback((value) => updateActiveToolSetting('airbrushDistance', value, setAirbrushDistance), [updateActiveToolSetting]);
  const setPersistentAirbrushPaintFlow = useCallback((value) => updateActiveToolSetting('airbrushPaintFlow', value, setAirbrushPaintFlow), [updateActiveToolSetting]);
  const setPersistentAirbrushPressurePaintFlowEnabled = useCallback((value) => updateActiveToolSetting('airbrushPressurePaintFlowEnabled', value, setAirbrushPressurePaintFlowEnabled), [updateActiveToolSetting]);
  const setPersistentAirbrushAirPressure = useCallback((value) => updateActiveToolSetting('airbrushAirPressure', value, setAirbrushAirPressure), [updateActiveToolSetting]);
  const setPersistentAirbrushNeedleLimit = useCallback((value) => updateActiveToolSetting('airbrushNeedleLimit', value, setAirbrushNeedleLimit), [updateActiveToolSetting]);
  // ---- game mode: the game's rules layered over the editor
  useEffect(() => {
    if (gameRestrictions && !isToolAllowed(gameRestrictions, activeTool)) setActiveToolState(fallbackTool(gameRestrictions));
  }, [activeTool, gameRestrictions]);

  useEffect(() => {
    if (!gameRestrictions) return;
    const colour = snapColour(gameRestrictions, strokeColor);
    if (colour !== strokeColor) setStrokeColor(colour);
    const size = snapSize(gameRestrictions, brushSize);
    if (size !== brushSize) setBrushSize(size);
  }, [brushSize, gameRestrictions, strokeColor]);

  useEffect(() => {
    // Imported brush tips are images, so games that forbid imports also forbid them.
    if (gameRestrictions && !gameRestrictions.allowImport && activeImportedBrushId) setActiveImportedBrushId('');
  }, [activeImportedBrushId, gameRestrictions]);

  const gameModeActive = Boolean(gameMode);
  useEffect(() => {
    if (gameModeActive) purgeStaleGameDrawingStates();
  }, [gameModeActive]);

  const gameRotation = gameMode && Number.isFinite(Number(gameMode.rotation)) ? Number(gameMode.rotation) : null;
  useEffect(() => {
    if (gameRotation !== null) setViewRotation(gameRotation);
  }, [gameRotation]);

  useEffect(() => {
    const game = gameModeRef.current;
    if (game && typeof game.onContentChange === 'function') game.onContentChange();
  }, [historyCount, redoCount, layers]);

  useEffect(() => {
    const game = gameModeRef.current;
    if (!game || !game.apiRef) return undefined;
    const api = {
      exportPicture: () => (stageRef.current ? stageRef.current.exportDataUrl('image/png') : ''),
      hasPixels: () => Boolean(stageRef.current && stageRef.current.hasPixels()),
      revertLastStroke: () => handleUndo({ force: true }),
      saveLocally: () => persistCanvasStateNow(),
    };
    game.apiRef.current = api;
    return () => {
      if (game.apiRef.current === api) game.apiRef.current = null;
    };
  }, [handleUndo, persistCanvasStateNow]);

  const isGameInputBlocked = useCallback(() => {
    const game = gameModeRef.current;
    return Boolean(game && typeof game.isInputBlocked === 'function' && game.isInputBlocked(activeToolRef.current));
  }, []);

  const activeToolLabel = activeTool === 'paint' ? 'GPU Paint' : activeTool === 'eraser' ? 'GPU Eraser' : activeImportedBrush ? 'GPU Imported Brush' : isAirbrush ? 'GPU Airbrush' : isCalligraphy ? 'GPU Calligraphy' : 'GPU Pen';
  const activeBrushLabel = activeTool === 'paint'
    ? `${String(paintSettings.tool || 'tube').replace(/^./, (value) => value.toUpperCase())} Paint`
    : activeTool === 'eraser'
    ? activeImportedBrush
      ? `${activeImportedBrush.name || 'Imported Brush'} Eraser`
      : 'Round Eraser'
    : activeImportedBrush
    ? (activeImportedBrush.name || 'Imported Brush')
    : isAirbrush
      ? '0.3mm GPU Airbrush'
      : isCalligraphy
        ? 'Ellipse Calligraphy'
        : 'Round Pen Brush';
  const displayZoomPercent = Math.round(zoom * 100);

  const handleFitView = useCallback(() => {
    const viewport = canvasViewportRef.current;
    if (!viewport) return;

    const viewportStyle = typeof window !== 'undefined'
      ? window.getComputedStyle(viewport)
      : null;
    const horizontalPadding = viewportStyle
      ? (Number.parseFloat(viewportStyle.paddingLeft) || 0) + (Number.parseFloat(viewportStyle.paddingRight) || 0)
      : 0;
    const verticalPadding = viewportStyle
      ? (Number.parseFloat(viewportStyle.paddingTop) || 0) + (Number.parseFloat(viewportStyle.paddingBottom) || 0)
      : 0;
    const availableWidth = Math.max(1, viewport.clientWidth - horizontalPadding);
    const availableHeight = Math.max(1, viewport.clientHeight - verticalPadding);
    const nextZoom = Math.max(
      MIN_ZOOM,
      Math.min(
        MAX_ZOOM,
        availableWidth / Math.max(1, documentSize.width),
        availableHeight / Math.max(1, documentSize.height),
      ),
    );
    const nextPan = { x: 0, y: 0 };

    zoomRef.current = nextZoom;
    viewPanRef.current = nextPan;
    setZoom(nextZoom);
    setViewPan(nextPan);
  }, [documentSize.height, documentSize.width]);

  useEffect(() => {
    if (!canvasStageReady) return undefined;

    const canvasKey = String(canvasId || '');
    if (initialFillCanvasIdRef.current === canvasKey) return undefined;

    let cancelled = false;
    let firstFrame = 0;
    let secondFrame = 0;
    let resizeObserver = null;

    const applyInitialFill = () => {
      if (cancelled || initialFillCanvasIdRef.current === canvasKey) return;

      const viewport = canvasViewportRef.current;
      if (!viewport || viewport.clientWidth <= 0 || viewport.clientHeight <= 0) return;

      handleFitView();
      initialFillCanvasIdRef.current = canvasKey;

      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    };

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(applyInitialFill);
    });

    if (typeof window.ResizeObserver === 'function' && canvasViewportRef.current) {
      resizeObserver = new window.ResizeObserver(applyInitialFill);
      resizeObserver.observe(canvasViewportRef.current);
    }

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [canvasId, canvasStageReady, handleFitView]);

  const handlePanWheel = useCallback((event) => {
    if (activeTool !== 'grab') return;

    const frame = canvasFrameRef.current;
    if (!frame) return;

    event.preventDefault();
    event.stopPropagation();

    const currentZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(zoomRef.current) || DEFAULT_ZOOM));
    const deltaModeMultiplier = event.deltaMode === 1
      ? 16
      : event.deltaMode === 2 && typeof window !== 'undefined'
        ? window.innerHeight
        : 1;
    const wheelDelta = (Number(event.deltaY) || 0) * deltaModeMultiplier;
    if (!wheelDelta) return;

    const nextZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, currentZoom * Math.exp(-wheelDelta * 0.0015)));
    if (Math.abs(nextZoom - currentZoom) < 0.0001) return;

    const frameRect = frame.getBoundingClientRect();
    if (frameRect.width <= 0 || frameRect.height <= 0) return;

    const clientX = Number(event.clientX) || 0;
    const clientY = Number(event.clientY) || 0;
    const canvasX = (clientX - frameRect.left) / currentZoom;
    const canvasY = (clientY - frameRect.top) / currentZoom;

    zoomRef.current = nextZoom;
    flushSync(() => {
      setZoom(nextZoom);
    });

    const nextFrame = canvasFrameRef.current;
    if (!nextFrame) return;

    const nextRect = nextFrame.getBoundingClientRect();
    const panDeltaX = clientX - (nextRect.left + canvasX * nextZoom);
    const panDeltaY = clientY - (nextRect.top + canvasY * nextZoom);
    const currentPan = viewPanRef.current || { x: 0, y: 0 };
    const nextPan = {
      x: (Number(currentPan.x) || 0) + panDeltaX,
      y: (Number(currentPan.y) || 0) + panDeltaY,
    };

    viewPanRef.current = nextPan;
    flushSync(() => {
      setViewPan(nextPan);
    });
  }, [activeTool]);

  useEffect(() => {
    const viewport = canvasViewportRef.current;
    if (!viewport) return undefined;

    viewport.addEventListener('wheel', handlePanWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handlePanWheel);
  }, [handlePanWheel]);


  useEffect(() => {
    const viewport = canvasViewportRef.current;
    if (!viewport) return undefined;

    const getGestureMetrics = (touches) => {
      if (!touches || touches.length !== 2) return null;
      const first = touches[0];
      const second = touches[1];
      const dx = Number(second.clientX || 0) - Number(first.clientX || 0);
      const dy = Number(second.clientY || 0) - Number(first.clientY || 0);
      return {
        distance: Math.max(1, Math.hypot(dx, dy)),
        angle: Math.atan2(dy, dx),
        centerX: (Number(first.clientX || 0) + Number(second.clientX || 0)) * 0.5,
        centerY: (Number(first.clientY || 0) + Number(second.clientY || 0)) * 0.5,
      };
    };

    const normaliseAngleDelta = (value) => {
      let result = value;
      while (result > Math.PI) result -= Math.PI * 2;
      while (result < -Math.PI) result += Math.PI * 2;
      return result;
    };

    const cancelActiveCanvasStroke = () => {
      const stage = stageRef.current;
      if (stage && typeof stage.cancelActivePointerStroke === 'function') {
        stage.cancelActivePointerStroke();
      }
    };

    const clearPendingTouchStart = () => {
      const pending = mobilePendingTouchStartRef.current;
      if (pending && pending.timer) clearTimeout(pending.timer);
      mobilePendingTouchStartRef.current = null;
    };

    const cloneTouchPointerEvent = (type, source) => new PointerEvent(type, {
      bubbles: true,
      cancelable: true,
      composed: true,
      pointerId: source.pointerId,
      pointerType: 'touch',
      isPrimary: source.isPrimary,
      clientX: source.clientX,
      clientY: source.clientY,
      screenX: source.screenX,
      screenY: source.screenY,
      pageX: source.pageX,
      pageY: source.pageY,
      width: source.width,
      height: source.height,
      pressure: source.pressure,
      tangentialPressure: source.tangentialPressure,
      tiltX: source.tiltX,
      tiltY: source.tiltY,
      twist: source.twist,
      button: type === 'pointerup' ? 0 : source.button,
      buttons: type === 'pointerup' ? 0 : (source.buttons || 1),
    });

    const releasePendingTouchStart = (pointerUpEvent = null) => {
      const pending = mobilePendingTouchStartRef.current;
      if (!pending) return false;
      clearPendingTouchStart();
      if (!pending.target || mobileGestureInputBlockedRef.current) return true;
      pending.target.dispatchEvent(cloneTouchPointerEvent('pointerdown', pending.source));
      if (pointerUpEvent) {
        pending.target.dispatchEvent(cloneTouchPointerEvent('pointerup', pointerUpEvent));
      }
      return true;
    };

    const handlePointerDownCapture = (event) => {
      if (!event || event.pointerType !== 'touch' || event.isTrusted === false) return;
      const pointerIds = mobileTouchPointerIdsRef.current;
      pointerIds.add(event.pointerId);

      if (pointerIds.size >= 2) {
        clearPendingTouchStart();
        mobileGestureInputBlockedRef.current = true;
        cancelActiveCanvasStroke();
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const source = {
        pointerId: event.pointerId,
        isPrimary: event.isPrimary,
        clientX: event.clientX,
        clientY: event.clientY,
        screenX: event.screenX,
        screenY: event.screenY,
        pageX: event.pageX,
        pageY: event.pageY,
        width: event.width,
        height: event.height,
        pressure: event.pressure,
        tangentialPressure: event.tangentialPressure,
        tiltX: event.tiltX,
        tiltY: event.tiltY,
        twist: event.twist,
        button: event.button,
        buttons: event.buttons,
      };
      const target = event.target;
      const timer = setTimeout(() => {
        releasePendingTouchStart();
      }, 65);
      mobilePendingTouchStartRef.current = {
        pointerId: event.pointerId,
        source,
        target,
        timer,
      };
      event.preventDefault();
      event.stopPropagation();
    };

    const handlePointerFinishCapture = (event) => {
      if (!event || event.pointerType !== 'touch' || event.isTrusted === false) return;
      const pointerIds = mobileTouchPointerIdsRef.current;
      pointerIds.delete(event.pointerId);

      const pending = mobilePendingTouchStartRef.current;
      if (pending && pending.pointerId === event.pointerId) {
        releasePendingTouchStart(event);
        event.preventDefault();
        event.stopPropagation();
      }

      if (pointerIds.size === 0) {
        mobileGestureInputBlockedRef.current = false;
        mobilePinchRef.current = null;
      }
    };

    const handleTouchStart = (event) => {
      const metrics = getGestureMetrics(event.touches);
      if (!metrics) return;

      mobileGestureInputBlockedRef.current = true;
      cancelActiveCanvasStroke();

      mobilePinchRef.current = {
        startDistance: metrics.distance,
        startAngle: metrics.angle,
        startCenterX: metrics.centerX,
        startCenterY: metrics.centerY,
        startZoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(zoomRef.current) || DEFAULT_ZOOM)),
        startRotation: Number(viewRotationRef.current) || 0,
        startPanX: Number(viewPanRef.current && viewPanRef.current.x) || 0,
        startPanY: Number(viewPanRef.current && viewPanRef.current.y) || 0,
      };

      event.preventDefault();
      event.stopPropagation();
    };

    const handleTouchMove = (event) => {
      const gesture = mobilePinchRef.current;
      const metrics = getGestureMetrics(event.touches);
      if (!gesture || !metrics) return;

      event.preventDefault();
      event.stopPropagation();

      const scaleFactor = metrics.distance / Math.max(1, gesture.startDistance);
      const nextZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, gesture.startZoom * scaleFactor));
      const angleDelta = normaliseAngleDelta(metrics.angle - gesture.startAngle);
      const nextRotation = gesture.startRotation + (angleDelta * 180 / Math.PI);
      const nextPan = {
        x: gesture.startPanX + (metrics.centerX - gesture.startCenterX),
        y: gesture.startPanY + (metrics.centerY - gesture.startCenterY),
      };

      zoomRef.current = nextZoom;
      viewRotationRef.current = nextRotation;
      viewPanRef.current = nextPan;

      flushSync(() => {
        setZoom(nextZoom);
        setViewRotation(nextRotation);
        setViewPan(nextPan);
      });
    };

    const finishTouchGesture = (event) => {
      if (!mobilePinchRef.current && !mobileGestureInputBlockedRef.current) return;
      if (event.touches && event.touches.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      mobilePinchRef.current = null;
      mobileGestureInputBlockedRef.current = false;
      mobileTouchPointerIdsRef.current.clear();
      event.preventDefault();
      event.stopPropagation();
    };

    viewport.addEventListener('pointerdown', handlePointerDownCapture, true);
    viewport.addEventListener('pointerup', handlePointerFinishCapture, true);
    viewport.addEventListener('pointercancel', handlePointerFinishCapture, true);
    viewport.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });
    viewport.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
    viewport.addEventListener('touchend', finishTouchGesture, { capture: true, passive: false });
    viewport.addEventListener('touchcancel', finishTouchGesture, { capture: true, passive: false });

    return () => {
      viewport.removeEventListener('pointerdown', handlePointerDownCapture, true);
      viewport.removeEventListener('pointerup', handlePointerFinishCapture, true);
      viewport.removeEventListener('pointercancel', handlePointerFinishCapture, true);
      viewport.removeEventListener('touchstart', handleTouchStart, true);
      viewport.removeEventListener('touchmove', handleTouchMove, true);
      viewport.removeEventListener('touchend', finishTouchGesture, true);
      viewport.removeEventListener('touchcancel', finishTouchGesture, true);
      clearPendingTouchStart();
      mobileTouchPointerIdsRef.current.clear();
      mobileGestureInputBlockedRef.current = false;
      mobilePinchRef.current = null;
    };
  }, []);

  useEffect(() => {
    setTextObjectsByLayer((current) => {
      let changed = false;
      const next = { ...current };
      const liveIds = new Set();
      (Array.isArray(layers) ? layers : []).forEach((layer) => {
        const id = String(layer?.id || '').trim();
        if (!id) return;
        liveIds.add(id);
        if (layer?.textObject) {
          const previous = next[id];
          if (previous !== layer.textObject) {
            next[id] = layer.textObject;
            changed = true;
          }
        }
      });
      Object.keys(next).forEach((id) => {
        if (!liveIds.has(id)) {
          delete next[id];
          changed = true;
        }
      });
      textObjectsByLayerRef.current = next;
      return changed ? next : current;
    });
  }, [layers]);

  const updateLayers = useCallback((updater) => {
    setLayers((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      const safe = Array.isArray(next) && next.length ? next : current;
      layersRef.current = safe;
      sendLayerState(safe);
      return safe;
    });
  }, [sendLayerState]);

  const ownsLayer = useCallback((layerOrId) => {
    const layer = typeof layerOrId === 'object'
      ? layerOrId
      : layers.find((candidate) => String(candidate.id) === String(layerOrId));
    return isLayerOwnedByCurrentUser(layer, liveClientIdRef.current, layerOwnerName, currentUserId);
  }, [currentUserId, layerOwnerName, layers]);

  const handleSelectLayer = useCallback((layerId) => {
    const safeLayerId = String(layerId || '').trim();
    if (!safeLayerId || !ownsLayer(safeLayerId)) return false;

    const layerExists = layersRef.current.some(
      (layer) => String(layer && layer.id ? layer.id : '').trim() === safeLayerId
    );
    if (!layerExists) return false;

    // Keep the layer panel, React state and GPU renderer on the same text layer.
    setActiveLayerId(safeLayerId);
    if (stageRef.current && typeof stageRef.current.setActiveLayer === 'function') {
      stageRef.current.setActiveLayer(safeLayerId);
    }
    return true;
  }, [ownsLayer]);

  const handleAddLayer = useCallback(() => {
    const id = createOwnedLayerId({
      ownerName: layerOwnerName,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId || 0,
    }, 'new');
    updateLayers((current) => {
      const ownedCount = current.filter((layer) => isLayerOwnedByCurrentUser(layer, liveClientIdRef.current, layerOwnerName, currentUserId)).length;
      return [...current, { id, name: `Layer ${ownedCount + 1}`, ownerName: layerOwnerName, ownerClientId: liveClientIdRef.current, ownerUserId: currentUserId || undefined, visible: true, opacity: 1 }];
    });
    setActiveLayerId(id);
  }, [currentUserId, layerOwnerName, updateLayers]);

  const getTextLayerName = useCallback((textObject, fallback = 'Text') => {
    const text = String(textObject?.text || '')
      .replace(/\s+/g, ' ')
      .trim();
    return text || fallback;
  }, []);

  const handleCreateTextLayer = useCallback((textObject, modelDecalData = null) => {
    const id = createOwnedLayerId({
      ownerName: layerOwnerName,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId || 0,
    }, 'text');
    updateLayers((current) => [...current, {
      id,
      name: getTextLayerName(textObject, 'Placeholder Text'),
      ownerName: layerOwnerName,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId || undefined,
      visible: true,
      opacity: 1,
      textObject: textObject || null,
      ...(modelDecalData ? { contentType: 'decal', decalData: modelDecalData } : {}),
    }]);
    const nextTextObjects = { ...textObjectsByLayerRef.current, [id]: textObject || null };
    textObjectsByLayerRef.current = nextTextObjects;
    setTextObjectsByLayer(nextTextObjects);
    setActiveLayerId(id);
    return id;
  }, [currentUserId, getTextLayerName, layerOwnerName, updateLayers]);

  const handleUpdateTextLayer = useCallback((layerId, textObject) => {
    if (!layerId) return;
    const safeLayerId = String(layerId);
    const nextTextObjects = { ...textObjectsByLayerRef.current, [safeLayerId]: textObject || null };
    textObjectsByLayerRef.current = nextTextObjects;
    setTextObjectsByLayer(nextTextObjects);

    const currentLayers = Array.isArray(layersRef.current) ? layersRef.current : [];
    const nextLayers = currentLayers.map((layer) => {
      if (String(layer.id) !== safeLayerId) return layer;
      if (!isLayerOwnedByCurrentUser(layer, liveClientIdRef.current, layerOwnerName, currentUserId)) return layer;
      return {
        ...layer,
        name: getTextLayerName(textObject, 'Text'),
        textObject: textObject || null,
      };
    });

    layersRef.current = nextLayers;
    setLayers(nextLayers);
    sendLayerState(nextLayers);
  }, [currentUserId, getTextLayerName, layerOwnerName, sendLayerState]);

  const handleUpdateDecalLayer = useCallback((layerId, nextDecalData) => {
    const safeLayerId = String(layerId || '').trim();
    if (!safeLayerId || !ownsLayer(safeLayerId)) return;
    updateLayers((current) => current.map((layer) => {
      if (String(layer?.id || '') !== safeLayerId) return layer;
      const currentDecal = layer?.decalData && typeof layer.decalData === 'object' ? layer.decalData : {};
      return {
        ...layer,
        contentType: 'decal',
        decalData: {
          ...currentDecal,
          ...(nextDecalData && typeof nextDecalData === 'object' ? nextDecalData : {}),
        },
      };
    }));
    markCanvasStateDirty();
  }, [markCanvasStateDirty, ownsLayer, updateLayers]);

  const handleDeleteLayer = useCallback((layerId) => {
    if (!ownsLayer(layerId)) return;
    const safeLayerId = String(layerId || '');
    setTextObjectsByLayer((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, safeLayerId)) return current;
      const next = { ...current };
      delete next[safeLayerId];
      return next;
    });
    const ownedLayers = layers.filter((layer) => isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    if (ownedLayers.length <= 1) return;
    purgeReplayLayerEvents(layerId);
    updateLayers((current) => {
      const next = current.filter((layer) => layer.id !== layerId);
      if (layerId === activeLayerId) {
        const replacement = next.find((layer) => isLayerOwnedByCurrentUser(
          layer,
          liveClientIdRef.current,
          layerOwnerName,
          currentUserId,
        ));
        if (replacement) setActiveLayerId(replacement.id);
      }
      return next;
    });
  }, [activeLayerId, currentUserId, layerOwnerName, layers, ownsLayer, purgeReplayLayerEvents, updateLayers]);

  const handleMoveLayer = useCallback((layerId, direction) => {
    if (!ownsLayer(layerId)) return;
    updateLayers((current) => {
      const ownedIndexes = current
        .map((layer, index) => isLayerOwnedByCurrentUser(layer, liveClientIdRef.current, layerOwnerName, currentUserId) ? index : -1)
        .filter((index) => index >= 0);
      const ownedPosition = ownedIndexes.findIndex((index) => current[index].id === layerId);
      const targetOwnedPosition = ownedPosition + direction;
      if (ownedPosition < 0 || targetOwnedPosition < 0 || targetOwnedPosition >= ownedIndexes.length) return current;
      const sourceIndex = ownedIndexes[ownedPosition];
      const targetIndex = ownedIndexes[targetOwnedPosition];
      const next = current.slice();
      [next[sourceIndex], next[targetIndex]] = [next[targetIndex], next[sourceIndex]];
      return next;
    });
  }, [currentUserId, layerOwnerName, ownsLayer, updateLayers]);

  const commitLayerPrivacyChanges = useCallback((layerIds, hiddenFromOthers) => {
    const targetIds = new Set(
      (Array.isArray(layerIds) ? layerIds : [layerIds])
        .map((layerId) => String(layerId || '').trim())
        .filter(Boolean)
    );
    if (!targetIds.size) return;

    const now = Date.now();
    const sourceLayers = Array.isArray(layersRef.current) && layersRef.current.length
      ? layersRef.current
      : layers;
    const changedLayers = [];
    const nextLayers = sourceLayers.map((layer) => {
      if (!layer || !targetIds.has(String(layer.id || ''))) return layer;
      if (!isLayerOwnedByCurrentUser(
        layer,
        liveClientIdRef.current,
        layerOwnerName,
        currentUserId,
      )) return layer;

      const nextHiddenFromOthers = hiddenFromOthers == null
        ? layer.hiddenFromOthers !== true
        : hiddenFromOthers === true;
      if (layer.hiddenFromOthers === nextHiddenFromOthers) return layer;

      const nextLayer = withLayerVersion(
        { ...layer, hiddenFromOthers: nextHiddenFromOthers },
        Math.max(getLayerRevision(layer) + 1, now),
        now,
      );
      changedLayers.push(nextLayer);
      return nextLayer;
    });

    if (!changedLayers.length) return;
    layersRef.current = nextLayers;
    updateLayers(nextLayers);

    changedLayers.forEach((layer) => {
      Promise.resolve(sendLayerPrivacyEvent(layer))
        .catch(() => false)
        .then(() => {
          if (layer.hiddenFromOthers !== true) {
            sendAuthoritativeLayer(layer);
          }
        });
    });
  }, [currentUserId, layerOwnerName, layers, sendAuthoritativeLayer, sendLayerPrivacyEvent, updateLayers]);

  const handleToggleLayerHiddenFromOthers = useCallback((layerId) => {
    commitLayerPrivacyChanges(layerId, null);
  }, [commitLayerPrivacyChanges]);

  const handleToggleMyLayersHiddenFromOthers = useCallback(() => {
    const sourceLayers = Array.isArray(layersRef.current) && layersRef.current.length
      ? layersRef.current
      : layers;
    const ownedLayers = sourceLayers.filter((layer) => isLayerOwnedByCurrentUser(
      layer,
      liveClientIdRef.current,
      layerOwnerName,
      currentUserId,
    ));
    if (!ownedLayers.length) return;
    const shouldHide = ownedLayers.some((layer) => layer.hiddenFromOthers !== true);
    commitLayerPrivacyChanges(ownedLayers.map((layer) => layer.id), shouldHide);
  }, [commitLayerPrivacyChanges, currentUserId, layerOwnerName, layers]);

  // Party games open over the canvas and leave the canvas document untouched.
  const [partyGamesOpen, setPartyGamesOpen] = useState(false);
  const handleOpenPartyGames = useCallback(() => {
    // Inside the Voxemera shell the drawing games live in the shared Games destination; this canvas's
    // members can be offered as the audience there. The stand-alone Canvas page opens them over the room.
    if (typeof window !== 'undefined' && !String(window.location.pathname || '').startsWith('/canvas')) {
      const detail = { category: 'Drawing', canvasId: Number(canvasId) || 0 };
      window.__voxPendingGamesContext = detail;
      window.dispatchEvent(new CustomEvent('vox-open-games', { detail }));
      return;
    }
    setPartyGamesOpen(true);
  }, [canvasId]);
  const handleClosePartyGames = useCallback(() => setPartyGamesOpen(false), []);

  const handleOpenCanvasGamePanel = useCallback(() => {
    setCanvasGamePanelOpen(true);
  }, []);

  const handleCloseCanvasGamePanel = useCallback(() => {
    setCanvasGamePanelOpen(false);
  }, []);

  useEffect(() => {
    setDocumentSize((current) => {
      if (
        current.width === resolvedCanvasDocumentSize.width
        && current.height === resolvedCanvasDocumentSize.height
      ) {
        return current;
      }
      return resolvedCanvasDocumentSize;
    });
  }, [resolvedCanvasDocumentSize.height, resolvedCanvasDocumentSize.width]);

  const handleSetDocumentSize = useCallback(async (nextWidth, nextHeight) => {
    if (!isCanvasOwner || savingDocumentSize) return;

    const id = Number(canvasId);
    if (!Number.isInteger(id) || id <= 0) return;

    const nextSize = {
      width: clampDocumentDimension(nextWidth, documentSize.width),
      height: clampDocumentDimension(nextHeight, documentSize.height),
    };

    setSavingDocumentSize(true);

    try {
      const response = await fetch('/api/canvas/' + encodeURIComponent(String(id)), {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          canvas_width: nextSize.width,
          canvas_height: nextSize.height,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error((payload && payload.error) || 'Failed to update canvas size.');
      }

      const confirmedCanvas = payload && payload.canvas ? payload.canvas : null;
      const confirmedSize = {
        width: clampDocumentDimension(confirmedCanvas && confirmedCanvas.canvas_width, nextSize.width),
        height: clampDocumentDimension(confirmedCanvas && confirmedCanvas.canvas_height, nextSize.height),
      };

      setDocumentSize(confirmedSize);
      postCanvasLiveStrokeEvent(id, {
        type: 'canvas_size',
        strokeId: 'canvas-size-' + id + '-' + Date.now(),
        clientId: liveClientIdRef.current,
        userId: currentUserId,
        canvas_width: confirmedSize.width,
        canvas_height: confirmedSize.height,
        timestamp: Date.now(),
      });
    } catch (error) {
      if (typeof window !== 'undefined') {
        window.alert(String((error && error.message) || 'Failed to update canvas size.'));
      }
    } finally {
      setSavingDocumentSize(false);
    }
  }, [canvasId, currentUserId, documentSize.height, documentSize.width, isCanvasOwner, savingDocumentSize]);

  const sketchRelayState = sketchRelay.state;
  // Anyone in the room sees the game as soon as somebody has joined, so they can join too.
  const sketchRelayActive = Boolean(sketchRelayState && (sketchRelayState.players.length > 0 || sketchRelayState.status !== 'lobby'));
  const canvasGamePanel = canvasGamePanelOpen || sketchRelayActive ? (
    <CanvasGamePanel
      game={sketchRelay}
      currentUserId={currentUserId}
      onClose={handleCloseCanvasGamePanel}
      canvasId={canvasId}
    />
  ) : null;

  const layersPanel = useMemo(() => (
    <CanvasLayersPanel
      layers={layers}
      activeLayerId={activeLayerId}
      currentUserId={currentUserId}
      currentUserName={layerOwnerName}
      modelViewOpen={modelViewOpen}
      onSelectLayer={handleSelectLayer}
      onAddLayer={handleAddLayer}
      onDeleteLayer={handleDeleteLayer}
      onToggleLayerHiddenFromOthers={handleToggleLayerHiddenFromOthers}
      onToggleMyLayersHiddenFromOthers={handleToggleMyLayersHiddenFromOthers}
      onToggleLayerVisibility={(layerId) => {
        if (!ownsLayer(layerId)) return;
        updateLayers((current) => current.map((layer) => layer.id === layerId ? { ...layer, visible: layer.visible === false } : layer));
      }}
      onRenameLayer={(layerId, name) => {
        if (!ownsLayer(layerId)) return;
        updateLayers((current) => current.map((layer) => layer.id === layerId ? { ...layer, name } : layer));
      }}
      onLayerOpacityChange={(layerId, opacityValue) => {
        if (!ownsLayer(layerId)) return;
        updateLayers((current) => current.map((layer) => layer.id === layerId ? { ...layer, opacity: opacityValue } : layer));
      }}
      onLayerEdgeWearChange={(layerId, edgeWearPercent) => {
        if (!modelViewOpen || !ownsLayer(layerId)) return;
        const safeEdgeWearPercent = Math.max(-100, Math.min(100, Number(edgeWearPercent) || 0));
        updateLayers((current) => current.map((layer) => (
          layer.id === layerId ? { ...layer, edgeWearPercent: safeEdgeWearPercent } : layer
        )));
        markCanvasStateDirty();
      }}
      onLayerNormalSettingsChange={(layerId, patch) => {
        if (!modelViewOpen || !ownsLayer(layerId)) return;
        const safePatch = patch && typeof patch === 'object' ? patch : {};
        updateLayers((current) => current.map((layer) => {
          if (String(layer?.id || '') !== String(layerId || '')) return layer;
          return {
            ...layer,
            ...(Object.prototype.hasOwnProperty.call(safePatch, 'normalSourceLayerId')
              ? { normalSourceLayerId: String(safePatch.normalSourceLayerId || '') }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(safePatch, 'normalSelfPercent')
              ? { normalSelfPercent: Math.max(0, Math.min(100, Number(safePatch.normalSelfPercent) || 0)) }
              : {}),
          };
        }));
        markCanvasStateDirty();
      }}
      onMoveLayerUp={(layerId) => handleMoveLayer(layerId, 1)}
      onMoveLayerDown={(layerId) => handleMoveLayer(layerId, -1)}
      canEditLayer={ownsLayer}
      canDeleteLayer={(layer) => ownsLayer(layer) && layers.filter((candidate) => ownsLayer(candidate)).length > 1}
      canRenameLayer={ownsLayer}
    />
  ), [activeLayerId, currentUserId, handleAddLayer, handleDeleteLayer, handleMoveLayer, handleSelectLayer, handleToggleLayerHiddenFromOthers, handleToggleMyLayersHiddenFromOthers, layerOwnerName, layers, markCanvasStateDirty, modelViewOpen, ownsLayer, updateLayers]);

  const replayEventsForCurrentLayers = useMemo(
    () => filterReplayEventsForLayers(replayEventsRef.current, layers),
    [layers, replayRevision]
  );

  const handleToggleReplay = useCallback(() => {
    setReplayOpen((value) => !value);
  }, []);

  const handlePaintSettingsChange = useCallback((value) => {
    setPaintSettingsState((current) => normalisePaintSettings({ ...current, ...(value || {}) }));
  }, []);

  const handlePaintDebugViewModeChange = useCallback((value) => {
    const raw = String(value || '').trim().toLowerCase();
    const view = raw === 'color' || raw === 'colour'
      ? 'color'
      : raw === 'height' || raw === 'heightmap'
        ? 'heightmap'
        : 'final';
    setPaintSettingsState((current) => normalisePaintSettings({ ...current, view }));
    const stage = stageRef.current;
    if (stage && typeof stage.setPaintDebugViewMode === 'function') {
      stage.setPaintDebugViewMode(activeTool === 'paint' ? view : 'final');
    }
  }, [activeTool]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof stage.setPaintDebugViewMode !== 'function') return;
    stage.setPaintDebugViewMode(activeTool === 'paint' ? paintSettings.view : 'final');
  }, [activeTool, paintSettings.view]);

  const handleDryPaint = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || !ownsLayer(activeLayerId) || typeof stage.dryPaint !== 'function') return;
    if (!stage.dryPaint(activeLayerId)) return;
    const patch = typeof stage.exportLayerPatch === 'function'
      ? stage.exportLayerPatch(activeLayerId)
      : null;
    handleLocalLiveEvent({
      type: 'paint_action',
      action: 'dry',
      layerId: activeLayerId,
      strokeId: `paint-dry-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      patch,
      rasterPatchEvent: Boolean(patch),
      rasterPatchType: patch ? 'raster_patch' : undefined,
      state: {
        action: 'dry',
        paintSettings,
        patch,
        rasterPatchEvent: Boolean(patch),
        rasterPatchType: patch ? 'raster_patch' : undefined,
      },
    });
    captureSnapshot();
  }, [activeLayerId, captureSnapshot, handleLocalLiveEvent, ownsLayer, paintSettings]);

  const handleClearPaint = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || !ownsLayer(activeLayerId) || typeof stage.clearPaint !== 'function') return;
    if (!stage.clearPaint(activeLayerId)) return;
    const patch = typeof stage.exportLayerPatch === 'function'
      ? stage.exportLayerPatch(activeLayerId)
      : null;
    handleLocalLiveEvent({
      type: 'paint_action',
      action: 'clear',
      layerId: activeLayerId,
      strokeId: `paint-clear-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      patch,
      rasterPatchEvent: Boolean(patch),
      rasterPatchType: patch ? 'raster_patch' : undefined,
      state: {
        action: 'clear',
        paintSettings,
        patch,
        rasterPatchEvent: Boolean(patch),
        rasterPatchType: patch ? 'raster_patch' : undefined,
      },
    });
    captureSnapshot();
  }, [activeLayerId, captureSnapshot, handleLocalLiveEvent, ownsLayer, paintSettings]);

  const handleTransformCommand = useCallback((command) => {
    const stage = stageRef.current;
    if (!stage || !ownsLayer(activeLayerId) || typeof stage.applyTransformCommand !== 'function') return;
    if (!stage.applyTransformCommand(command)) return;
    const patch = typeof stage.exportLayerPatch === 'function' ? stage.exportLayerPatch(activeLayerId) : null;
    handleLocalLiveEvent({
      type: 'stroke_end',
      layerId: activeLayerId,
      strokeId: `transform-command-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      point: null,
      patch,
      rasterPatchEvent: Boolean(patch),
      rasterPatchType: patch ? 'raster_patch' : undefined,
      state: { action: 'transform_command', command, patch },
    });
    captureSnapshot();
  }, [activeLayerId, captureSnapshot, handleLocalLiveEvent, ownsLayer]);

  const sidebarExtensionPanel = useMemo(() => (
    <CanvasRightSidebarExtensions
      activeTool={activeTool}
      liquifyMode={liquifyMode}
      setLiquifyMode={(value) => setLiquifyMode(normaliseLiquifyMode(value))}
      liquifySettings={liquifySettings}
      setLiquifySettings={setLiquifySettings}
      activeBrushPreset={activeBrushPreset}
      brushSize={brushSize}
      setBrushSize={setPersistentBrushSize}
      strokeColor={strokeColor}
      setStrokeColor={setPersistentStrokeColor}
      paintSettings={paintSettings}
      onPaintSettingsChange={handlePaintSettingsChange}
      paintDebugViewMode={paintSettings.view}
      setPaintDebugViewMode={handlePaintDebugViewModeChange}
      onDryPaint={handleDryPaint}
      onClearPaint={handleClearPaint}
      brushSoftness={brushSoftness}
      setBrushSoftness={setPersistentBrushSoftness}
      activeToolOpacity={toolOpacity}
      onToolOpacityChange={setPersistentToolOpacity}
      pencilOrientation={pencilOrientation}
      setPencilOrientation={setPencilOrientation}
      pencilGrade={pencilGrade}
      setPencilGrade={setPencilGrade}
      setActiveTool={setActiveTool}
      setActiveBrushPreset={setActiveBrushPreset}
      importedBrushPresets={importedBrushPresets}
      activeImportedBrushId={activeImportedBrushId}
      setActiveImportedBrushId={setActiveImportedBrushId}
      onAddImportedBrushPresets={handleAddImportedBrushPresets}
      bucketExpansion={bucketExpansion}
      setBucketExpansion={setBucketExpansion}
      jitterAmount={jitterAmount}
      setJitterAmount={setPersistentJitterAmount}
      calligraphySettings={calligraphySettings}
      setCalligraphySettings={setPersistentCalligraphySettings}
      airbrushDistance={airbrushDistance}
      setAirbrushDistance={setPersistentAirbrushDistance}
      airbrushPaintFlow={airbrushPaintFlow}
      setAirbrushPaintFlow={setPersistentAirbrushPaintFlow}
      airbrushPressurePaintFlowEnabled={airbrushPressurePaintFlowEnabled}
      setAirbrushPressurePaintFlowEnabled={setPersistentAirbrushPressurePaintFlowEnabled}
      airbrushAirPressure={airbrushAirPressure}
      setAirbrushAirPressure={setPersistentAirbrushAirPressure}
      gamePanel={canvasGamePanel}
      textSettings={textSettings}
      setTextSettings={setTextSettings}
      transformMode={transformMode}
      setTransformMode={setTransformMode}
      perspectiveAxis={perspectiveAxis}
      setPerspectiveAxis={setPerspectiveAxis}
      onTransformCommand={handleTransformCommand}
      modelViewOpen={modelViewOpen}
      paintMaterialSettings={paintMaterialSettings}
      onPaintMaterialSettingsChange={handlePaintMaterialSettingsChange}
      pbrBrushActive={pbrBrushActive}
      lock3dPaintToStartingSurface={lock3dPaintToStartingSurface}
      onLock3dPaintToStartingSurfaceChange={handleLock3dPaintToStartingSurfaceChange}
    />
  ), [activeBrushPreset, activeImportedBrushId, activeTool, airbrushAirPressure, airbrushDistance, airbrushPaintFlow, airbrushPressurePaintFlowEnabled, brushSize, brushSoftness, bucketExpansion, calligraphySettings, canvasGamePanel, handleAddImportedBrushPresets, handleClearPaint, handleDryPaint, handlePaintDebugViewModeChange, handlePaintSettingsChange, handleTransformCommand, importedBrushPresets, jitterAmount, liquifyMode, liquifySettings, paintSettings, pencilGrade, pencilOrientation, textSettings, perspectiveAxis, setPersistentAirbrushAirPressure, setPersistentAirbrushDistance, setPersistentAirbrushPaintFlow, setPersistentAirbrushPressurePaintFlowEnabled, setPersistentBrushSize, setPersistentBrushSoftness, setPersistentCalligraphySettings, setPersistentJitterAmount, setPersistentStrokeColor, setPersistentToolOpacity, strokeColor, toolOpacity, transformMode, modelViewOpen, paintMaterialSettings, handlePaintMaterialSettingsChange, pbrBrushActive, lock3dPaintToStartingSurface, handleLock3dPaintToStartingSurfaceChange]);

  useEffect(() => {
    if (!localStateHydratedRef.current) return;
    scheduleCanvasStateSave();
  }, [activeLayerId, areaAssignments, layers, localStateRevision, scheduleCanvasStateSave]);

  // A paper change (File > Paper & Surface or the Paper tab) is a document
  // change: save it even when nothing has been painted yet.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handlePaperSurfaceChange = () => {
      if (localStateHydratedRef.current) markCanvasStateDirty();
    };
    window.addEventListener('vx-paint-surface', handlePaperSurfaceChange);
    return () => window.removeEventListener('vx-paint-surface', handlePaperSurfaceChange);
  }, [markCanvasStateDirty]);

  useEffect(() => () => {
    if (typeof window !== 'undefined' && localStateSaveTimerRef.current) {
      window.clearTimeout(localStateSaveTimerRef.current);
      localStateSaveTimerRef.current = 0;
    }
    persistCanvasStateNow().catch(() => {});
  }, [persistCanvasStateNow]);


  const broadcastWorkspaceMode = useCallback((nextMode) => {
    const safeCanvasId = Number(canvasId);
    if (!Number.isInteger(safeCanvasId) || safeCanvasId <= 0) return false;
    const resolvedMode = nextMode === '3d' ? '3d' : '2d';
    const timestamp = Date.now();
    postCanvasLiveStrokeEvent(safeCanvasId, {
      type: 'workspace_mode',
      workspaceMode: resolvedMode,
      mode: resolvedMode,
      clientId: liveClientIdRef.current,
      ownerClientId: liveClientIdRef.current,
      ownerUserId: currentUserId || undefined,
      senderUserId: currentUserId || undefined,
      ownerName: layerOwnerName,
      seq: timestamp,
      timestamp,
      state: { workspaceMode: resolvedMode, mode: resolvedMode },
    });
    return true;
  }, [canvasId, currentUserId, layerOwnerName]);

  const setWorkspaceMode = useCallback((nextMode) => {
    const resolvedMode = nextMode === '3d' ? '3d' : '2d';
    writeStoredWorkspaceMode(canvasId, resolvedMode);
    setModelViewOpen(resolvedMode === '3d');
    broadcastWorkspaceMode(resolvedMode);
  }, [broadcastWorkspaceMode, canvasId]);

  const toggleWorkspaceMode = useCallback(() => {
    setModelViewOpen((current) => {
      const nextMode = current ? '2d' : '3d';
      writeStoredWorkspaceMode(canvasId, nextMode);
      broadcastWorkspaceMode(nextMode);
      return nextMode === '3d';
    });
  }, [broadcastWorkspaceMode, canvasId]);

  useEffect(() => {
    setModelViewOpen(!gameModeRef.current && readStoredWorkspaceMode(canvasId) === '3d');
  }, [canvasId]);

  const handleChooseModel = useCallback(() => {
    if (modelFileInputRef.current) modelFileInputRef.current.click();
  }, []);

  const handleExportModel = useCallback(async (requestedFormat = 'glb') => {
    const viewport = modelViewportRef.current;
    if (!viewport || typeof viewport.exportModel !== 'function') return false;
    try {
      await viewport.exportModel(requestedFormat);
      return true;
    } catch (error) {
      console.error('[3D model export]', error);
      return false;
    }
  }, []);

  const handleModelFileChange = useCallback((event) => {
    const file = event && event.target && event.target.files ? event.target.files[0] : null;
    if (!file) return;
    setModelFile(file);
    setWorkspaceMode('3d');
    event.target.value = '';
  }, [setWorkspaceMode]);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".voxcanvas,application/json,application/vnd.voxemera.canvas+json,image/png,image/jpeg,image/webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <ImportedBrushImportModal
        open={Boolean(droppedBrushFiles)}
        importedBrushPresets={importedBrushPresets}
        activeImportedBrushId={activeImportedBrushId}
        initialFiles={droppedBrushFiles}
        onInitialFilesConsumed={() => setDroppedBrushFiles((files) => (files && files.length ? [] : files))}
        onClose={() => setDroppedBrushFiles(null)}
        onAddImportedBrushPresets={handleAddImportedBrushPresets}
        onSetActiveImportedBrushId={setActiveImportedBrushId}
      />

      <input
        ref={modelFileInputRef}
        type="file"
        accept=".glb,.gltf,.obj,.stl,.zip,model/gltf-binary,model/gltf+json,application/zip"
        onChange={handleModelFileChange}
        style={{ display: 'none' }}
      />

      <CanvasDesktopLayout
        gameMode={gameMode ? {
          allowImport: Boolean(gameRestrictions && gameRestrictions.allowImport),
          noUndo: Boolean(gameRestrictions && gameRestrictions.noUndo),
          noClear: Boolean(gameRestrictions && gameRestrictions.noClear),
          isToolAllowed: (tool) => isToolAllowed(gameRestrictions, tool),
          palette: gameRestrictions && gameRestrictions.palette,
        } : null}
        name={gameMode ? String(gameMode.title || 'Drawing') : canvasName}
        activeToolLabel={activeToolLabel}
        activeBrushLabel={activeBrushLabel}
        strokeColor={strokeColor}
        setStrokeColor={setPersistentStrokeColor}
        activeToolOpacity={toolOpacity}
        onToolOpacityChange={setPersistentToolOpacity}
        brushSize={brushSize}
        setBrushSize={setPersistentBrushSize}
        brushSoftness={brushSoftness}
        setBrushSoftness={setPersistentBrushSoftness}
        jitterAmount={jitterAmount}
        setJitterAmount={setPersistentJitterAmount}
        pencilOrientation={pencilOrientation}
        setPencilOrientation={setPencilOrientation}
        pencilGrade={pencilGrade}
        setPencilGrade={setPencilGrade}
        calligraphyAutoEnabled={calligraphyAutoEnabled}
        setCalligraphyAutoEnabled={setCalligraphyAutoEnabled}
        calligraphySettings={calligraphySettings}
        setCalligraphySettings={setPersistentCalligraphySettings}
        airbrushDistance={airbrushDistance}
        setAirbrushDistance={setPersistentAirbrushDistance}
        airbrushPaintFlow={airbrushPaintFlow}
        setAirbrushPaintFlow={setPersistentAirbrushPaintFlow}
        airbrushPressurePaintFlowEnabled={airbrushPressurePaintFlowEnabled}
        setAirbrushPressurePaintFlowEnabled={setPersistentAirbrushPressurePaintFlowEnabled}
        airbrushAirPressure={airbrushAirPressure}
        setAirbrushAirPressure={setPersistentAirbrushAirPressure}
        airbrushNeedleLimit={airbrushNeedleLimit}
        setAirbrushNeedleLimit={setPersistentAirbrushNeedleLimit}
        liquifySettings={liquifySettings}
        setLiquifySettings={setLiquifySettings}
        displayZoomPercent={displayZoomPercent}
        viewRotation={0}
        documentSize={documentSize}
        isOwner={gameMode ? false : isCanvasOwner}
        savingDocumentSize={savingDocumentSize}
        onSetDocumentSize={handleSetDocumentSize}
        historyCount={historyCount}
        redoCount={redoCount}
        onBack={gameMode ? null : onBack}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onSaveCanvas={handleSave}
        onSaveCanvasProject={handleSaveCanvasProject}
        onLoadCanvasProject={handleImportCanvasProject}
        onImportCanvasImage={handleImportCanvasImage}
        onExportModel={handleExportModel}
        modelViewOpen={modelViewOpen}
        onToggleModelView={toggleWorkspaceMode}
        onSaveCanvasHistory={() => handleSave('png')}
        onClearCanvas={handleClear}
        onZoomOut={() => setZoom((value) => Math.max(MIN_ZOOM, value / ZOOM_BUTTON_FACTOR))}
        onZoomReset={handleFitView}
        onZoomIn={() => setZoom((value) => Math.min(MAX_ZOOM, value * ZOOM_BUTTON_FACTOR))}
        activeTool={activeTool}
        activeBrushPreset={activeBrushPreset}
        setActiveTool={setActiveTool}
        setActiveBrushPreset={setActiveBrushPreset}
        importedBrushPresets={gameRestrictions && !gameRestrictions.allowImport ? [] : importedBrushPresets}
        activeImportedBrushId={activeImportedBrushId}
        setActiveImportedBrushId={setActiveImportedBrushId}
        onAddImportedBrushPresets={gameRestrictions && !gameRestrictions.allowImport ? null : handleAddImportedBrushPresets}
        onOpenFilters={null}
        replayPanelOpen={replayOpen}
        onToggleReplayPanel={handleToggleReplay}
        layersPanel={layersPanel}
        sidebarExtensionPanel={sidebarExtensionPanel}
        gamePanel={gameMode ? null : canvasGamePanel}
        activeLayer={layers.find((layer) => layer.id === activeLayerId) || layers[0]}
        selectionState={null}
        onSliderPreviewChange={null}
        onOpenCanvasAreas={() => setCanvasAreasOpen(true)}
        canOpenCanvasAreas={!gameMode && isCanvasOwner}
        onOpenCanvasGame={handleOpenCanvasGamePanel}
        canOpenCanvasGame={!gameMode}
        onOpenPartyGames={gameMode ? null : handleOpenPartyGames}
        onEnableAnimation={handleEnableAnimation}
        onDisableAnimation={handleDisableAnimation}
        canEnableAnimation={!gameMode && isCanvasOwner}
        animationEnabled={animationEnabled}
        animationPanel={animationPanel}
        canvasId={canvasId}
        canvasTabs={gameMode ? [] : canvasTabs}
        canvasTabBusy={canvasTabBusy}
        onOpenCanvasTab={onOpenCanvasTab}
        onCreateCanvasTab={onCreateCanvasTab}
        onRenameCanvasTab={onRenameCanvasTab}
        onCopyCanvasTab={onCopyCanvasTab}
        onDeleteCanvasTab={onDeleteCanvasTab}
        currentUserId={currentUserId}
        onReorderCanvasTabs={onReorderCanvasTabs}
        modelToolbarControls={modelViewOpen ? (
          <>
            <style>{`#canvas-3d-toolbar-portal-target > div:not(:first-child) { display: none !important; }`}</style>
            <div id="canvas-3d-toolbar-portal-target" style={{ display: 'flex', alignItems: 'center', gap: 4 }} />
          </>
        ) : null}
      >
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {modelViewOpen ? (
            <CanvasModelViewport
              modelFile={modelFile}
              onChooseModel={handleChooseModel}
              onClose={() => setWorkspaceMode('2d')}
              paintStageRef={stageRef}
              documentWidth={documentSize.width}
              documentHeight={documentSize.height}
              modelViewportRef={modelViewportRef}
              onProjectionStrokeCommitted={handleProjectionStrokeCommitted}
              onPhysicalPaintStrokeCommitted={handlePhysicalPaintStrokeCommitted}
              paintMaterialSettings={paintMaterialSettings}
              lock3dPaintToStartingSurface={lock3dPaintToStartingSurface}
              activeTool={activeTool}
              layers={layers}
              activeLayerId={activeLayerId}
              onUpdateDecalLayer={handleUpdateDecalLayer}
              onSelectLayer={handleSelectLayer}
              textSettings={textSettings}
              onCreateTextLayer={handleCreateTextLayer}
              onUpdateTextLayer={handleUpdateTextLayer}
            />
          ) : null}
          <div
            ref={canvasViewportRef}
            aria-hidden={modelViewOpen ? 'true' : undefined}
            style={{
              position: modelViewOpen ? 'absolute' : 'relative',
              inset: modelViewOpen ? 0 : undefined,
              visibility: modelViewOpen ? 'hidden' : 'visible',
              pointerEvents: modelViewOpen ? 'none' : 'auto',
              flex: '1 1 auto',
              minWidth: 0,
              minHeight: 0,
              overflow: 'auto',
              padding: gameMode ? 12 : 40,
              boxSizing: 'border-box',
              background: '#858585',
            }}
          >
          <div
            ref={canvasFrameRef}
            style={{
              width: documentSize.width * zoom,
              height: documentSize.height * zoom,
              margin: '0 auto',
              transform: `translate3d(${viewPan.x}px, ${viewPan.y}px, 0) rotate(${modelViewOpen ? 0 : viewRotation}deg)${gameRestrictions && gameRestrictions.mirror ? ' scaleX(-1)' : ''}`,
              transformOrigin: '50% 50%',
              touchAction: 'none',
              background: '#ffffff',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.35), 0 22px 60px rgba(0,0,0,0.34)',
              position: 'relative',
            }}
          >
            {gameMode && Array.isArray(gameMode.underlays) ? gameMode.underlays.filter((entry) => entry && entry.src).map((entry) => (
              <img
                key={entry.key || entry.src.slice(-32)}
                src={entry.src}
                alt=""
                aria-hidden="true"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: `${Number(entry.left) || 0}%`,
                  top: `${Number(entry.top) || 0}%`,
                  width: `${Number(entry.width) || 100}%`,
                  height: `${Number(entry.height) || 100}%`,
                  opacity: entry.opacity == null ? 1 : entry.opacity,
                  pointerEvents: 'none',
                  userSelect: 'none',
                  zIndex: 0,
                }}
              />
            )) : null}
            {canvasStageReady ? (
              <CanvasStageSurface
                ref={stageRef}
                transparentBackground={Boolean(gameMode)}
              width={documentSize.width}
              height={documentSize.height}
              strokeColor={strokeColor}
              brushSize={brushSize}
              brushSoftness={brushSoftness}
              opacity={toolOpacity}
              activeTool={activeTool}
              transformMode={transformMode}
              perspectiveAxis={perspectiveAxis}
              textSettings={textSettings}
              setTextSettings={setTextSettings}
              onCreateTextLayer={handleCreateTextLayer}
              onUpdateTextLayer={handleUpdateTextLayer}
              onDeleteTextLayer={handleDeleteLayer}
              onSelectTextLayer={handleSelectLayer}
              textObjectsByLayer={textObjectsByLayer}
              bucketExpansion={bucketExpansion}
              activeBrushPreset={activeBrushPreset}
              activeImportedBrush={activeImportedBrush}
              airbrushDistance={airbrushDistance}
              airbrushPaintFlow={airbrushPaintFlow}
              airbrushPressurePaintFlowEnabled={airbrushPressurePaintFlowEnabled}
              airbrushAirPressure={airbrushAirPressure}
              airbrushNeedleLimit={airbrushNeedleLimit}
              jitterAmount={jitterAmount}
              pencilOrientation={pencilOrientation}
              pencilGrade={pencilGrade}
              calligraphySettings={calligraphySettings}
              liquifyMode={liquifyMode}
              liquifySettings={liquifySettings}
              paintSettings={paintSettings}
              paintDebugViewMode={activeTool === 'paint' ? paintSettings.view : 'final'}
              onReady={restoreCanvasState}
              onStrokeComplete={captureSnapshot}
              onPixelsChanged={setHasPixels}
              onLocalLiveEvent={handleLocalLiveEvent}
              layers={layers}
              activeLayerId={activeLayerId}
              canEditActiveLayer={ownsLayer(activeLayerId)}
              canEditLayer={ownsLayer}
              canvasAreaSelection={activeCanvasAreaSelection}
              canDrawAtPoint={canCurrentUserDrawAtCanvasPoint}
              isPointerInputBlocked={() => mobileGestureInputBlockedRef.current || isGameInputBlocked()}
              viewPan={viewPan}
              viewRotation={modelViewOpen ? 0 : viewRotation}
              onViewPanChange={setViewPan}
              />
            ) : null}
            {gameMode && gameMode.blindLabel ? (
              <div
                data-testid="game-blind-cover"
                aria-hidden="true"
                style={{ position: 'absolute', inset: 0, zIndex: 14, background: '#ffffff', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5c5f66', fontSize: 18, fontWeight: 900 }}
              >
                <span style={{ transform: gameRestrictions && gameRestrictions.mirror ? 'scaleX(-1)' : undefined }}>{gameMode.blindLabel}</span>
              </div>
            ) : null}
            {gameMode && gameMode.overlay ? (
              <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 15, pointerEvents: 'none', overflow: 'hidden' }}>{gameMode.overlay}</div>
            ) : null}
            {areaAssignments.enabled ? (
              <svg
                viewBox={`0 0 ${documentSize.width} ${documentSize.height}`}
                preserveAspectRatio="none"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 12,
                  overflow: 'visible',
                }}
              >
                {areaAssignments.layout === 'horizontal' ? (
                  <line
                    x1="0"
                    y1={documentSize.height * (areaAssignments.splitY / 100)}
                    x2={documentSize.width}
                    y2={documentSize.height * (areaAssignments.splitY / 100)}
                    stroke="rgba(30, 30, 30, 0.9)"
                    strokeWidth={Math.max(1, 2 / Math.max(zoom, 0.01))}
                    strokeDasharray={`${8 / Math.max(zoom, 0.01)} ${6 / Math.max(zoom, 0.01)}`}
                  />
                ) : null}
                {areaAssignments.layout === 'vertical' ? (
                  <line
                    x1={documentSize.width * (areaAssignments.splitX / 100)}
                    y1="0"
                    x2={documentSize.width * (areaAssignments.splitX / 100)}
                    y2={documentSize.height}
                    stroke="rgba(30, 30, 30, 0.9)"
                    strokeWidth={Math.max(1, 2 / Math.max(zoom, 0.01))}
                    strokeDasharray={`${8 / Math.max(zoom, 0.01)} ${6 / Math.max(zoom, 0.01)}`}
                  />
                ) : null}
                {areaAssignments.layout === 'diagonal_down' ? (
                  <line x1="0" y1="0" x2={documentSize.width} y2={documentSize.height} stroke="rgba(30, 30, 30, 0.9)" strokeWidth={Math.max(1, 2 / Math.max(zoom, 0.01))} strokeDasharray={`${8 / Math.max(zoom, 0.01)} ${6 / Math.max(zoom, 0.01)}`} />
                ) : null}
                {areaAssignments.layout === 'diagonal_up' ? (
                  <line x1="0" y1={documentSize.height} x2={documentSize.width} y2="0" stroke="rgba(30, 30, 30, 0.9)" strokeWidth={Math.max(1, 2 / Math.max(zoom, 0.01))} strokeDasharray={`${8 / Math.max(zoom, 0.01)} ${6 / Math.max(zoom, 0.01)}`} />
                ) : null}
                {areaAssignments.layout === 'quarters' ? (
                  <>
                    <line x1={documentSize.width * (areaAssignments.splitX / 100)} y1="0" x2={documentSize.width * (areaAssignments.splitX / 100)} y2={documentSize.height} stroke="rgba(30, 30, 30, 0.9)" strokeWidth={Math.max(1, 2 / Math.max(zoom, 0.01))} strokeDasharray={`${8 / Math.max(zoom, 0.01)} ${6 / Math.max(zoom, 0.01)}`} />
                    <line x1="0" y1={documentSize.height * (areaAssignments.splitY / 100)} x2={documentSize.width} y2={documentSize.height * (areaAssignments.splitY / 100)} stroke="rgba(30, 30, 30, 0.9)" strokeWidth={Math.max(1, 2 / Math.max(zoom, 0.01))} strokeDasharray={`${8 / Math.max(zoom, 0.01)} ${6 / Math.max(zoom, 0.01)}`} />
                  </>
                ) : null}
              </svg>
            ) : null}
          </div>
        </div>
        </div>
      </CanvasDesktopLayout>

      <CanvasAreaAssignmentModal
        open={canvasAreasOpen}
        areaAssignments={areaAssignments}
        availableUsers={canvasAreaUsers}
        onClose={() => setCanvasAreasOpen(false)}
        onApply={(nextAssignments) => {
          const safeAssignments = sanitizeCanvasAreaAssignments(nextAssignments);
          setAreaAssignments(safeAssignments);
          sendCanvasAreaAssignmentsEvent(safeAssignments);
          setCanvasAreasOpen(false);
          setLocalStateRevision((value) => value + 1);
        }}
      />

      <CanvasReplayModal
        open={replayOpen}
        width={documentSize.width}
        height={documentSize.height}
        layers={layers}
        events={replayEventsForCurrentLayers}
        onClose={() => setReplayOpen(false)}
      />

      {!hasPixels ? null : null}

      {partyGamesOpen ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#1e1f22' }} data-testid="party-overlay">
          <PartyArcadeView user={{ id: currentUserId }} canvasId={Number(canvasId) || 0} onClose={handleClosePartyGames} />
        </div>
      ) : null}
    </>
  );
}
